import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute, ParamMap } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable, Subscription, forkJoin, of, firstValueFrom } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { DomainContextService } from '../../services/domain-context';
import { DomainTopicsService, ParentTopic } from '../../services/domain-topics';
import { CompareDomainContextService } from '../../services/compare-domain-context';
import { CompareContextService } from '../../services/compare-context';
import { FeatureFlagService } from '../../services/feature-flag';
import {
  InventoryPlanClustersService,
  PlanCluster,
  InventoryTrackingFilter,
  ActionPlanInternalLinkRow,
  ActionPlanSuggestionStatus,
  ActionPlanUnclusteredPageRow,
  UnclusteredPageRecommendation,
  ActionPlanV2Row,
  ActionPlanV2Response,
  ActionPlanV2SignalType,
  PlanClusterClassValue
} from '../../services/inventory-plan-clusters.service';
import { NotificationService } from '../../services/notification.service';
import {
  TopicPickerOption,
  buildTopicOptionsFromParents,
  isInflectionEquivalentSlug,
  topicKindTagClass as topicKindTagClassForOption
} from '../../utils/topic-picker-options';
import { DataForSEOService, type SupportingPagesV2RowResult } from '../../services/dataforseo';
import { UrlSerpRefreshProgress, UrlSerpSyncService } from '../../services/url-serp-sync';
import { QuickCompareLaunchService } from '../../services/quick-compare-launch';
import { InventoryQuickCompareDrawerComponent } from '../inventory-quick-compare-drawer/inventory-quick-compare-drawer';
import { UrlDetailComponent } from '../url-detail/url-detail';
import { PlanV2WorkItemBodyComponent } from '../plan-v2-work-item-body/plan-v2-work-item-body';
import { ImpactPackagesComponent } from '../impact-packages/impact-packages';
import { InventoryCompetingPagesComponent } from '../inventory-competing-pages/inventory-competing-pages';
import { ImpactBriefService, type ImpactBriefPayload } from '../../services/impact-brief.service';
import {
  pushFreshnessPdfLines,
  parseWorkItemDetail,
  pushSupportingGapPdfLines,
  pushInternalLinkPdfLines,
  pushTechnicalItemsPdfLines,
  workItemPdfChipLabel,
  workItemExampleEntries,
  summarizeWorkItemsRecap,
  summarizeWorkItemsOverview,
  WORK_ITEM_PDF_LINE_HEIGHT,
  WORK_ITEM_PDF_PARAGRAPH_GAP,
  type PlanV2WorkItemDetailV1,
  type WorkItemPdfLine,
  type WorkItemPdfSegment,
  type WorkItemPdfSegmentStyle,
  type WorkItemOverviewRow,
  type WorkItemRecapLine
} from '../../utils/plan-v2-work-item-display';
import { PlanV2IssueQueueService } from '../../services/plan-v2-issue-queue.service';
import { CrawlRunPeriodService, type CrawlRunPeriodView } from '../../services/crawl-run-period.service';
import type {
  PlanV2IssueDispositionRecord,
  PlanV2WorkItemsUrlGroup
} from '../../utils/plan-v2-issues';
import {
  SITE_WIDE_GROUP_NOTE,
  type PlanV2WorkItemGroup
} from '../../utils/plan-v2-work-item-groups';
import {
  planV2UrlReopenConfirmMessage,
  planV2UrlReopenResultMessage,
  PLAN_V2_URL_REOPEN_NOTHING_TO_DO
} from '../../utils/plan-v2-url-reopen';
import { contentGapSignalTooltip } from '../../utils/content-gap-signal-label';
import { normalizeUrlForCompare } from '../../utils/topic-supporting-pages';
import {
  buildFreshnessWorkItemContext,
  type FreshnessWorkItemContext
} from '../../utils/plan-v2-work-item-compose';
import { saveWorkItemsPdf, workItemPdfTextWithLink } from '../../utils/work-item-pdf-links';
import {
  FULL_COMPARE_COMPETITOR_QP,
  FULL_COMPARE_YOUR_QP
} from '../page-comparison/page-comparison-prefill';
import {
  canSyncQuickCompareUrlsToQueryParams,
  quickCompareQueryParamValueTooLarge
} from '../../utils/quick-compare-query-sync';
import {
  classifyPageHealth,
  effectiveHealthBandLabel,
  effectiveHealthDotClass,
  healthBandDotClass,
  pageHealthBandClass,
  inventoryHealthFilterLabel,
  pageHealthBandLabel,
  resolveEffectiveHealthBand,
  secondaryHealthBandLabel,
  secondaryHealthDotClass,
  type InventoryHealthFilterBand,
  type PageHealthBand
} from './inventory-page-health';
import {
  buildPageSignalsIndex,
  type InventoryPageSignal,
  type InventoryPageSignalKind
} from './inventory-page-signals';
import { buildInventoryClientView } from './inventory-client-view';
import {
  formatInventoryRealizationNumber,
  formatInventoryRealizationRatio,
  inventoryRealizationGapTitle,
  inventoryRealizationRatioTitle
} from './inventory-realization-sort';
import { formatPageDemandSourceConfidence, formatPageDemandSplitReasons } from '../../utils/page-demand-display';
import { unclusteredRecommendationLabel } from '@taptimize/shared/dist/review/page-traffic-evidence';

/** Shape of a single page row from GET /api/v1/urls/content-inventory */
interface InventoryPage {
  urlId: string;
  url: string;
  analysisId: string;
  analyzedAt: string;
  onpageScore: number;
  wordCount: number;
  statusCode: number;
  titleLength: number;
  descriptionLength: number;
  readabilityIndex: number;
  h1Count: number;
  h2Count: number;
  h3Count: number;
  internalLinksCount: number;
  externalLinksCount: number;
  criticalIssuesCount: number;
  warningsCount: number;
  topicSignalsCount: number;
  topTopicSignals?: Array<{
    topicSlug: string;
    label: string;
    weight: number;
    salience: number;
    relativeDensity: number;
    relevancePct: number;
  }>;
  topicWeight: number | null;
  topicClass: 'core' | 'mention' | null;
  topicRelevanceScore: number | null;
  topicRelevancePct: number | null;
  rankedKeywordsCount: number;
  estimatedOrganicTraffic: number | null;
  inventoryInclusionState?: 'default' | 'force_include' | 'force_exclude';
  clusters?: string[];
  pageDemand?: PageDemandSnapshot | null;
  pageRealization?: PageRealizationSnapshot | null;
  pageHealth?: PageHealthSnapshot | null;
  pageSecondaryHealth?: SecondaryPageHealthSnapshot | null;
  effectiveHealthBand?: InventoryHealthFilterBand | null;
  /** Labs keyword difficulty for SERP target query (0–100); null until URL SERP refresh. */
  serpKeywordDifficulty?: number | null;
}

interface PageDemandSideSnapshot {
  label: string;
  source: string;
  confidence: 'high' | 'medium' | 'low' | 'none';
  detail?: string;
}

interface PageDemandRealizationSnapshot {
  label: string;
  source: string;
  confidence: 'high' | 'medium' | 'low' | 'none';
  position: number | null;
  searchVolume: number | null;
  detail?: string;
}

interface PageDemandSnapshot {
  claim: PageDemandSideSnapshot | null;
  claimIntentTokens?: string[];
  realization: PageDemandRealizationSnapshot | null;
  ambientTopic: PageDemandSideSnapshot | null;
  splitReasons: string[];
  computedAt: string | null;
}

interface PageRealizationCeilingDriverSnapshot {
  keyword: string;
  searchVolume: number;
  matchingTokens: string[];
  upsideAtTargetRank: number;
}

interface PageRealizationSnapshot {
  realizedVisibility: number;
  realizedSource: 'etv' | 'traffic_proxy';
  demandCeiling: number | null;
  ceiling: number;
  realizationRatio: number | null;
  gap: number | null;
  gapGate: 'scored' | 'low_confidence' | 'pending' | 'unscored';
  ceilingDriver: PageRealizationCeilingDriverSnapshot | null;
  alignedKeywordCount: number;
}

interface PageHealthSnapshot {
  band: 'critical' | 'weak' | 'ok' | 'best' | 'unscored';
  reasons: string[];
  model: 'realization_gap' | 'eligibility' | 'unscored';
}

interface SecondaryPageHealthSnapshot {
  band: 'critical' | 'weak' | 'ok' | 'best' | 'unscored' | null;
  model: 'none' | 'freshness_hold' | 'earned_visibility';
  reasons: string[];
  recentlyUpdated: boolean;
  unscoredSegment:
    | 'no_claim'
    | 'low_confidence_claim'
    | 'no_market_data'
    | 'no_aligned_ceiling'
    | 'immaterial_ceiling'
    | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface ActionPlanTargetGroup {
  targetUrlId: string;
  targetUrl: string;
  targetTraffic: number;
  maxScore: number;
  rows: ActionPlanInternalLinkRow[];
}

interface ActionPlanClusterSummary {
  id: string;
  label: string;
  corePages: Array<{ url: string; estimatedOrganicTraffic: number }>;
  supportingPages: Array<{ url: string; estimatedOrganicTraffic: number }>;
}

/** Reserved `pathPrefix` query value; must match backend `PATH_PREFIX_ONE_SEGMENT`. */
const PATH_PREFIX_ONE_SEGMENT = '__one_segment__';

/** Inventory Quick Compare deep-link query keys (see docs/features/PAGE_COMPARISON.md). */
const QUICK_COMPARE_PRIMARY_QP = 'quickComparePrimary';
const QUICK_COMPARE_COMPARE_QP = 'quickCompareCompare';
const INVENTORY_COLUMN_PREFS_STORAGE_KEY = 'inventory.visibleColumns.v1';
const INVENTORY_PAGES_FILTERS_EXPANDED_STORAGE_KEY = 'inventory.pagesFiltersExpanded.v1';

/**
 * Sub-views. `planV2` + `workItems` are the living brief (this period's queue
 * and this period's commitments); `previous` is last period's, compare only
 * (docs/LIVING_BRIEF_PLAN.md LB2). `competing` is every pair of pages that compete for the same
 * searches, with the Cannibalization card's verdict (read-only).
 */
type InventorySection =
  | 'pages'
  | 'plan'
  | 'actionPlan'
  | 'planV2'
  | 'workItems'
  | 'previous'
  | 'competing';

/** Sections a `?section=` deep link may open. */
const PLAN_V2_DEEP_LINK_SECTIONS: readonly InventorySection[] = ['planV2', 'workItems', 'previous'];

interface PlanTopicCandidateRow {
  urlId: string;
  analysisId: string;
  url: string;
  analyzedAt: string;
  onpageScore: number;
  wordCount: number;
  statusCode: number;
  criticalIssuesCount: number;
  warningsCount: number;
  topicSignalsCount: number;
  topicWeight: number;
  topicRelevanceScore: number;
  topicRelevancePct: number;
  topicClass: 'core' | 'mention';
  rankedKeywordsCount: number;
  estimatedOrganicTraffic: number | null;
}

type PlanClusterClassOption = PlanClusterClassValue;

const HEALTH_FILTER_BANDS: InventoryHealthFilterBand[] = [
  'best',
  'ok',
  'weak',
  'unscored',
  'critical',
  'recently_updated'
];

type SortField =
  | 'analyzedAt'
  | 'onpageScore'
  | 'wordCount'
  | 'statusCode'
  | 'health'
  | 'secondaryHealth'
  | 'effectiveHealth'
  | 'realizationGap'
  | 'realizationRatio'
  | 'serpKeywordDifficulty'
  | 'estimatedOrganicTraffic'
  | 'topicWeight'
  | 'topicRelevanceScore'
  | 'url';

type InventoryColumnKey =
  | 'score'
  | 'issues'
  | 'health'
  | 'realizationGap'
  | 'realizationRatio'
  | 'serpKeywordDifficulty'
  | 'demandClaim'
  | 'signals'
  | 'topTopics'
  | 'clusters'
  | 'topicClass'
  | 'topicRelevance'
  | 'topicWeight'
  | 'keywords'
  | 'estimatedOrganicTraffic'
  | 'analyzedAt';

interface InventoryColumnOption {
  key: InventoryColumnKey;
  label: string;
  requiresTopicScope?: boolean;
}

/** Backend: `or` = in any selected cluster; `not` = not in any selected cluster. */
type ClusterFilterRuleMode = 'or' | 'not';
type ClusterFilterRuleJoin = 'and' | 'or';

interface ClusterFilterRuleRow {
  uid: string;
  mode: ClusterFilterRuleMode;
  /** How this rule combines with the previous rule (ignored for the first row). */
  join: ClusterFilterRuleJoin;
  topicIds: string[];
  searchQuery: string;
}

/** Marked competitor + Domain Topics authority for selected slug (same metric as topic picker). */
type CompetitorTopicPillRow =
  | { domain: string; kind: 'ready'; authorityScore: number }
  | { domain: string; kind: 'unavailable'; message: string };

@Component({
  selector: 'app-content-inventory',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    InventoryQuickCompareDrawerComponent,
    UrlDetailComponent,
    PlanV2WorkItemBodyComponent,
    ImpactPackagesComponent,
    InventoryCompetingPagesComponent
  ],
  templateUrl: './content-inventory.html',
  styleUrl: './content-inventory.css'
})
export class ContentInventoryComponent implements OnInit, OnDestroy {
  /** Exposed for template (feature flags, env). */
  protected readonly environment = environment;

  private readonly API_URL = environment.apiUrl + '/v1';
  private readonly topTopicsPillsLimit = 1;
  /** Solo Pages: advanced filters (topic, cluster rules, dev actions) collapsed by default. */
  pagesFiltersExpanded = false;
  pageSignalsByUrlId = new Map<string, InventoryPageSignal[]>();

  loading = true;
  error: string | null = null;
  trafficRefreshRunning = false;
  demandLabelRefreshRunning = false;
  urlSerpRefreshRunning = false;
  /** When true, bypass TopicSerp cache and re-fetch via Live Advanced (~$0.002 per keyword). */
  urlSerpForceRefresh = false;
  urlSerpRefreshProgress: UrlSerpRefreshProgress | null = null;
  private urlSerpProgressClearTimer: ReturnType<typeof setTimeout> | null = null;
  rankedKwDevRunning = false;
  pages: InventoryPage[] = [];
  pagination: Pagination = { page: 1, limit: 50, total: 0, totalPages: 0 };

  compareToDomain: string | null = null;
  compareLoading = false;
  compareError: string | null = null;
  comparePages: InventoryPage[] = [];
  comparePagination: Pagination = { page: 1, limit: 50, total: 0, totalPages: 0 };

  // Search & Sort
  searchQuery = '';
  selectedDomain = '';
  selectedTopicSlug = '';
  topicSearchQuery = '';
  showTopicSuggestions = false;
  includeChildTopics = false;
  parentTopics: ParentTopic[] = [];
  availableTopics: TopicPickerOption[] = [];
  /** Plan tab picker: always includes child subtopics (independent of Pages “Include subtopics”). */
  planAvailableTopics: TopicPickerOption[] = [];
  loadingTopics = false;
  topicError: string | null = null;
  sortBy: SortField = 'analyzedAt';
  sortOrder: 'asc' | 'desc' = 'desc';
  trackingFilter: InventoryTrackingFilter = 'tracked';
  readonly healthFilterBandOptions: InventoryHealthFilterBand[] = [...HEALTH_FILTER_BANDS];
  selectedHealthFilterBands: InventoryHealthFilterBand[] = [...HEALTH_FILTER_BANDS];

  /** First-segment path filters (OR). '/' = homepage only. Solo + compare primary column. */
  availablePathPrefixes: string[] = [];
  selectedPathPrefixes: string[] = [];
  loadingPathPrefixes = false;
  pathPrefixError: string | null = null;

  /** Compare column: separate path options and selection */
  availableComparePathPrefixes: string[] = [];
  selectedComparePathPrefixes: string[] = [];
  loadingComparePathPrefixes = false;
  comparePathPrefixError: string | null = null;

  /** Topic row: marked competitors with same authorityScore as Domain Topics compact API (when data exists). */
  competitorTopicPills: CompetitorTopicPillRow[] = [];
  competitorTopicPillsLoading = false;
  competitorTopicPillsNoMarked = false;
  competitorTopicPillsSettingsError: string | null = null;
  compareTopicPillSaveError: string | null = null;
  isSavingCompareFromTopicPill = false;

  /** Step 2: at most one selected URL per side in topic compare mode (full inventory URL strings). */
  quickComparePrimaryUrl: string | null = null;
  quickCompareCompareUrl: string | null = null;
  /** Step 4: slide-over Quick Compare (no route change). */
  quickCompareDrawerOpen = false;

  /** Sub-view within Content Inventory. */
  inventorySection: InventorySection = 'pages';

  /** Plan v2 tab — synthesized priority queue (dev-only). */
  planV2Rows: ActionPlanV2Row[] = [];
  planV2Summary: ActionPlanV2Response['data']['summary'] | null = null;
  planV2Meta: ActionPlanV2Response['data']['meta'] | null = null;
  planV2Loading = false;
  planV2Error: string | null = null;
  planV2Page = 1;
  planV2PageSize = 50;
  /**
   * The crawl pair this queue is about (LB1) — the same period the dashboard
   * names. Null until it loads, and null forever for a domain with no captured
   * pair: no pair means no dates, not invented ones.
   */
  planV2Period: CrawlRunPeriodView | null = null;
  /**
   * This period's impact packages (IMPACT_BRIEF_PLAN.md IB3) — the same composer
   * payload the Dashboard renders, fetched from the same route and rendered by
   * the same component. Issues re-reads it with the queue rather than caching it,
   * because a capture recomposes every package and a stale strip over a fresh
   * queue is precisely the disagreement the plan's trust bar forbids.
   */
  impactBrief: ImpactBriefPayload | null = null;
  impactBriefLoading = false;
  /** Packages opened past the five-page cap. See dashboard.ts#expandPackage. */
  expandedPackageIds: string[] = [];
  expandingPackageId: string | null = null;
  /** Issues row open in the review drawer (replaces inline table expand). */
  planV2DrawerRow: ActionPlanV2Row | null = null;
  planV2ActionsOnly = false;
  planV2ShowIgnored = false;
  planV2IssuesLoading = false;
  planV2Regenerating = false;
  planV2ClearingPrevious = false;
  planV2IgnoringUrlId: string | null = null;
  workItemsExporting = false;
  workItemReturningKey: string | null = null;
  workItemReopeningUrlId: string | null = null;
  workItemCarryingKey: string | null = null;

  /** Action Plan tab (V1: internal linking opportunities). */
  actionPlanRows: ActionPlanInternalLinkRow[] = [];
  actionPlanUnclusteredRows: ActionPlanUnclusteredPageRow[] = [];
  actionPlanClusterSummaries: ActionPlanClusterSummary[] = [];
  actionPlanLoading = false;
  actionPlanError: string | null = null;
  actionPlanUpdatingKey: string | null = null;
  actionPlanExporting = false;
  actionPlanIncludeDone = false;
  actionPlanIncludeDismissed = false;
  actionPlanCollapsedTargets = new Set<string>();
  actionPlanDeletedTargets = new Set<string>();
  actionPlanWhyExpandedRows = new Set<string>();
  actionPlanEdits: Record<string, { anchor: string; sentence: string }> = {};
  actionPlanUnclusteredEdits: Record<string, { recommendationText: string }> = {};
  actionPlanClusterDescriptions: Record<string, string> = {};
  private actionPlanDeletedTargetsDomain = '';

  /** Plan tab: monitored topic clusters persisted in backend. */
  planClusters: PlanCluster[] = [];
  planTopicSearchQuery = '';
  showPlanTopicSuggestions = false;
  planAddTopicError: string | null = null;
  planLoading = false;
  planError: string | null = null;
  planSuccess: string | null = null;
  planBootstrapping = false;
  findingUrlsTopicId: string | null = null;
  planUrlSearchState: Record<string, { loading: boolean; rows: PlanTopicCandidateRow[]; error: string | null }> = {};
  planFindOtherTopicInput: Record<string, string> = {};
  planFindOtherTopicSuggestionsOpen: Record<string, boolean> = {};
  editingPlanTopicId: string | null = null;
  planTopicLabelDraft: Record<string, string> = {};
  savingPlanTopicLabelId: string | null = null;
  movingPlanClusterId: string | null = null;
  planMoveTargetClusterIdBySource: Record<string, string> = {};
  planMoveSelectedByCluster: Record<string, Array<{ urlId: string; url: string }>> = {};
  planBulkClassByCluster: Record<string, PlanClusterClassOption> = {};
  planClassSortDirection: 'default' | 'reverse' = 'default';
  openPlanClassMenuKey: string | null = null;
  private lastBulkSelectAnchorUrl: string | null = null;
  private planMoveLastAnchorByCluster: Record<string, string | null> = {};
  updatingPlanClassKey: string | null = null;

  /** Pages tab: multi-select URLs (active domain) to attach to a plan cluster. */
  bulkSelectedUrls: Array<{ urlId?: string; url: string }> = [];
  bulkTargetClusterId: string | null = null;
  bulkRemoveClusterId: string | null = null;
  isBulkSaving = false;
  isBulkRemoving = false;
  isUpdatingInclusion = false;
  /** Pages view only: multi-rule cluster membership filters (draft UI; use Apply to send). */
  clusterFilterRules: ClusterFilterRuleRow[] = [];
  /** Last-applied cluster rules (matches what `loadInventory` requested). */
  appliedClusterFilterRules: ClusterFilterRuleRow[] = [];
  clusterFilterOpenRuleUid: string | null = null;
  columnPickerOpen = false;
  visibleColumns: Record<InventoryColumnKey, boolean> = this.defaultVisibleColumns();
  readonly columnOptions: InventoryColumnOption[] = [
    { key: 'score', label: 'Score' },
    { key: 'issues', label: 'Issues' },
    { key: 'health', label: 'Health' },
    { key: 'realizationGap', label: 'Gap' },
    { key: 'realizationRatio', label: 'Ratio' },
    { key: 'serpKeywordDifficulty', label: 'KD' },
    { key: 'demandClaim', label: 'Demand claim' },
    { key: 'signals', label: 'Signals' },
    { key: 'topTopics', label: 'Top topics' },
    { key: 'clusters', label: 'Cluster' },
    { key: 'topicClass', label: 'Class', requiresTopicScope: true },
    { key: 'topicRelevance', label: 'Relevance', requiresTopicScope: true },
    { key: 'topicWeight', label: 'Weight', requiresTopicScope: true },
    { key: 'keywords', label: 'Keywords' },
    { key: 'estimatedOrganicTraffic', label: 'Est. Traffic' },
    { key: 'analyzedAt', label: 'Analyzed' }
  ];

  private searchTimeout: any = null;
  private competitorTopicPillsRequestGen = 0;
  private inventoryRequestGen = 0;
  private inventorySourcePages: InventoryPage[] | null = null;
  private inventorySourceQueryKey = '';
  private topicsRequestGen = 0;
  private activePathPrefixesRequestGen = 0;
  private comparePathPrefixesRequestGen = 0;
  /** Debounce hover before fetching competitor authority for a topic row (see docs/features/CONTENT_INVENTORY_TOPIC_COMPARE_UI.md). */
  private topicSuggestionHoverTimer: ReturnType<typeof setTimeout> | null = null;
  /** Cached GET compare/settings marked list for the active domain (null = not loaded). */
  private markedCompetitorsForPreview: string[] | null = null;
  /**
   * Per-topic-slug competitor preview for the open typeahead (lazy on hover).
   * Keys: lowercase slug. Replacing the object reference helps default change detection.
   */
  topicSuggestionCompetitorCache: Record<
    string,
    { status: 'loading' | 'ready' | 'error'; rows?: CompetitorTopicPillRow[]; error?: string }
  > = {};

  private readonly subscriptions = new Subscription();
  private isContextLoaded = false;
  private readonly maxTopicSuggestions = 10;
  private readonly topTopicsPopoverWidthPx = 300;
  private topTopicsPopoverHideTimer: ReturnType<typeof setTimeout> | null = null;
  private planSuccessTimeout: ReturnType<typeof setTimeout> | null = null;
  /** Avoid feedback when programmatically updating Quick Compare query params. */
  private updatingQuickCompareRoute = false;
  private readonly clusterPopoverWidthPx = 300;
  private clusterPopoverHideTimer: ReturnType<typeof setTimeout> | null = null;
  clusterPopover: { open: boolean; left: number; top: number; clusters: string[] } = {
    open: false,
    left: 0,
    top: 0,
    clusters: []
  };

  topTopicsPopover: { open: boolean; left: number; top: number; topics: Array<{ label: string; relevancePct: number }> } = {
    open: false,
    left: 0,
    top: 0,
    topics: []
  };

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private domainContext: DomainContextService,
    private domainTopicsService: DomainTopicsService,
    private compareDomainContext: CompareDomainContextService,
    private compareContext: CompareContextService,
    public featureFlags: FeatureFlagService,
    private inventoryPlanClusters: InventoryPlanClustersService,
    private notifications: NotificationService,
    private quickCompareLaunchService: QuickCompareLaunchService,
    private urlSerpSync: UrlSerpSyncService,
    readonly planV2IssueQueue: PlanV2IssueQueueService,
    private readonly dataForSeo: DataForSEOService,
    private readonly crawlRunPeriod: CrawlRunPeriodService,
    private readonly impactBriefService: ImpactBriefService
  ) {
    this.resetClusterFilterRules();
  }

  isDevView(): boolean {
    return !this.featureFlags.isProduction() && !this.featureFlags.isUserView();
  }

  isActionPlanV2Enabled(): boolean {
    return this.featureFlags.isEnabled('actionPlanV2');
  }

  isClustersEnabled(): boolean {
    return this.featureFlags.isEnabled('clusters');
  }

  openClustersOrganizer(): void {
    this.router.navigate(['/clusters']);
  }

  runTrafficRefreshForActiveDomain(): void {
    const domain = (this.selectedDomain || '').trim();
    if (!domain || this.trafficRefreshRunning) return;

    this.trafficRefreshRunning = true;

    this.subscriptions.add(
      this.http.post<{ success: boolean; message?: string; data?: any }>(
        `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/traffic-enrichment`,
        { force: true }
      ).subscribe({
        next: (res) => {
          this.trafficRefreshRunning = false;
          this.notifications.success(res?.message || 'Estimated traffic refresh complete.');
          this.loadInventory();
        },
        error: (err) => {
          this.trafficRefreshRunning = false;
          this.notifications.error(err?.error?.message || err?.message || 'Failed to refresh estimated traffic.');
        }
      })
    );
  }

  runDemandLabelRefreshForActiveDomain(): void {
    const domain = (this.selectedDomain || '').trim();
    if (!domain || this.demandLabelRefreshRunning) return;

    this.demandLabelRefreshRunning = true;

    this.subscriptions.add(
      this.http.post<{ success: boolean; message?: string; data?: { labeledUrls?: number; matchedUrls?: number } }>(
        `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/page-demand-labeling`,
        {}
      ).subscribe({
        next: (res) => {
          this.demandLabelRefreshRunning = false;
          const labeled = res?.data?.labeledUrls;
          const matched = res?.data?.matchedUrls;
          const suffix = Number.isFinite(labeled) && Number.isFinite(matched)
            ? ` (${labeled}/${matched} URLs)`
            : '';
          this.notifications.success((res?.message || 'Page demand labels updated.') + suffix);
          this.loadInventory();
        },
        error: (err) => {
          this.demandLabelRefreshRunning = false;
          this.notifications.error(err?.error?.message || err?.message || 'Failed to refresh page demand labels.');
        }
      })
    );
  }

  runUrlSerpRefreshForActiveDomain(): void {
    const domain = (this.selectedDomain || '').trim();
    if (!domain || this.urlSerpRefreshRunning) return;

    if (this.urlSerpProgressClearTimer) {
      clearTimeout(this.urlSerpProgressClearTimer);
      this.urlSerpProgressClearTimer = null;
    }

    this.urlSerpRefreshRunning = true;
    this.urlSerpRefreshProgress = {
      percent: 0,
      keywordsDone: 0,
      totalKeywords: 0,
      statusLine: this.urlSerpForceRefresh
        ? 'Starting URL SERP refresh (force API, bypass cache)…'
        : 'Starting URL SERP refresh (cache-first)…',
      etaLabel: null,
      chunksCompleted: 0,
      runningCostUsd: 0
    };

    void this.runUrlSerpRefreshChunks(domain);
  }

  private scheduleUrlSerpProgressClear(): void {
    if (this.urlSerpProgressClearTimer) {
      clearTimeout(this.urlSerpProgressClearTimer);
    }
    this.urlSerpProgressClearTimer = setTimeout(() => {
      this.urlSerpProgressClearTimer = null;
      this.urlSerpRefreshProgress = null;
    }, 8000);
  }

  private async runUrlSerpRefreshChunks(domain: string): Promise<void> {
    try {
      const totals = await this.urlSerpSync.refreshDomainBatched(domain, {
        forceRefresh: this.urlSerpForceRefresh,
        onProgress: (p) => {
          this.urlSerpRefreshProgress = p;
        }
      });

      if (totals.totalKeywords === 0) {
        this.notifications.info('No SERP target queries for tracked URLs on this domain. Run page demand labeling first.');
      } else {
        const costLabel =
          totals.estimatedCostUsd >= 0.001 ? ` (~$${totals.estimatedCostUsd.toFixed(3)} API)` : '';
        const failSuffix =
          totals.failedKeywords > 0
            ? ` ${totals.failedKeywords} keyword${totals.failedKeywords === 1 ? '' : 's'} failed.`
            : '';
        this.notifications.success(
          `URL SERP: ${totals.snapshotsWritten} snapshots in ${totals.elapsedSec}s · ${totals.apiFetches} API · ${totals.cacheHits} cached${costLabel}.${failSuffix}`
        );
      }

      if (this.urlSerpRefreshProgress) {
        this.urlSerpRefreshProgress = {
          ...this.urlSerpRefreshProgress,
          percent: 100,
          etaLabel: `Finished in ${totals.elapsedSec}s`,
          statusLine: `Done · ${totals.snapshotsWritten} snapshots · ${totals.apiFetches} API calls`
        };
      }
    } catch (err: any) {
      this.notifications.error(
        err?.error?.message || err?.message || 'Failed to refresh URL SERP snapshots.'
      );
    } finally {
      this.urlSerpRefreshRunning = false;
      this.scheduleUrlSerpProgressClear();
    }
  }

  runRankedKeywordsRefreshForActiveDomain(): void {
    const domain = (this.selectedDomain || '').trim();
    if (!domain || this.rankedKwDevRunning) return;

    this.rankedKwDevRunning = true;

    this.subscriptions.add(
      this.http.post<{ success: boolean; message?: string; data?: any }>(
        `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/ranked-keywords-enrichment`,
        { force: false, maxPages: 500, offset: 0 }
      ).subscribe({
        next: (res) => {
          this.rankedKwDevRunning = false;
          const d = res?.data;
          const total = d?.matchedTrackedUrls ?? d?.processed ?? 0;
          if (d?.skippedFresh > 0 && !d?.updated) {
            this.notifications.info(
              `Ranked keywords already fresh — ${d.skippedFresh} of ${total} URLs within 30-day TTL. Use force=true to re-fetch.`
            );
          } else {
            const cost = Number.isFinite(d?.estimatedCostSum) ? `$${(d.estimatedCostSum as number).toFixed(3)}` : '—';
            this.notifications.success(
              `Ranked keywords updated: ${d?.updated ?? 0} of ${total} pages have keywords, cost ~${cost}.`
            );
            this.loadInventory();
          }
        },
        error: (err) => {
          this.rankedKwDevRunning = false;
          this.notifications.error(err?.error?.message || err?.message || 'Failed to refresh ranked keywords.');
        }
      })
    );
  }

  setInventorySection(section: InventorySection): void {
    if (section !== 'planV2') {
      this.closePlanV2Drawer();
    }
    this.inventorySection = section;
    if (section === 'plan') {
      this.quickCompareDrawerOpen = false;
      this.refreshPlanClusters();
      return;
    }
    if (section === 'actionPlan') {
      this.quickCompareDrawerOpen = false;
      this.loadActionPlan();
      return;
    }
    if (section === 'competing') {
      // The list loads itself (InventoryCompetingPagesComponent), from the domain it is given.
      this.quickCompareDrawerOpen = false;
      return;
    }
    if (section === 'planV2' || section === 'workItems' || section === 'previous') {
      this.quickCompareDrawerOpen = false;
      this.loadPlanV2IssueDispositions(() => {
        if (section === 'planV2') this.loadPlanV2();
      });
      return;
    }
    this.rebuildPageSignalsIndex();
  }

  toggleActionPlanIncludeDone(): void {
    this.actionPlanIncludeDone = !this.actionPlanIncludeDone;
    this.loadActionPlan();
  }

  toggleActionPlanIncludeDismissed(): void {
    this.actionPlanIncludeDismissed = !this.actionPlanIncludeDismissed;
    this.loadActionPlan();
  }

  loadPlanV2IssueDispositions(done?: () => void): void {
    const domain = (this.selectedDomain || '').trim();
    if (!domain || !this.isActionPlanV2Enabled()) {
      done?.();
      return;
    }
    this.planV2IssuesLoading = true;
    this.planV2IssueQueue
      .load(domain)
      .pipe(
        switchMap(() =>
          this.inventoryPlanClusters.getInternalLinkActionPlan(domain, {
            includeCompleted: true,
            includeDismissed: true,
            limit: 2000
          })
        ),
        switchMap((res) =>
          this.planV2IssueQueue.repairMisplacedInternalLinkWorkItems(domain, res?.data?.rows ?? [])
        ),
        switchMap(() => {
          const focalUrls = this.planV2IssueQueue.focalUrlsNeedingSectionGapRepair();
          if (!focalUrls.length) {
            return of(new Map<string, SupportingPagesV2RowResult[]>());
          }
          return forkJoin(
            focalUrls.map((focalUrl) =>
              this.dataForSeo.getSupportingPagesOpportunitySnapshot(focalUrl).pipe(
                map((snapshot) => ({
                  focalUrl,
                  rows: snapshot?.data?.rows ?? []
                })),
                catchError(() => of({ focalUrl, rows: [] as SupportingPagesV2RowResult[] }))
              )
            )
          ).pipe(
            map((entries) => {
              const v2ByFocalUrl = new Map<string, SupportingPagesV2RowResult[]>();
              for (const entry of entries) {
                v2ByFocalUrl.set(normalizeUrlForCompare(entry.focalUrl), entry.rows);
              }
              return v2ByFocalUrl;
            })
          );
        }),
        switchMap((v2ByFocalUrl) =>
          this.planV2IssueQueue.repairSupportingSectionWorkItems(domain, v2ByFocalUrl)
        ),
        switchMap(() => {
          const freshnessUrls = this.planV2IssueQueue.freshnessWorkItemUrls();
          if (!freshnessUrls.length) {
            return of(new Map<string, FreshnessWorkItemContext>());
          }
          return forkJoin(
            freshnessUrls.map(({ urlId, url }) =>
              this.dataForSeo.getUrlAnalysis(url).pipe(
                map((res) => ({
                  urlId,
                  context: buildFreshnessWorkItemContext({
                    lastContentModified: res?.data?.analysis?.lastContentModified ?? null,
                    serpResults: res?.data?.pageSerp?.results ?? null
                  })
                })),
                catchError(() => of(null))
              )
            )
          ).pipe(
            map((entries) => {
              const contextByUrlId = new Map<string, FreshnessWorkItemContext>();
              for (const entry of entries) {
                if (entry) contextByUrlId.set(entry.urlId, entry.context);
              }
              return contextByUrlId;
            })
          );
        }),
        switchMap((contextByUrlId) =>
          this.planV2IssueQueue.repairFreshnessWorkItems(domain, contextByUrlId)
        ),
        catchError(() => of(0))
      )
      .subscribe({
        next: () => {
          this.planV2IssuesLoading = false;
          done?.();
        },
        error: () => {
          this.planV2IssuesLoading = false;
          done?.();
        }
      });
  }

  loadPlanV2(
    page = this.planV2Page,
    options: { preserveDrawerUrlId?: string | null } = {}
  ): void {
    const domain = (this.selectedDomain || '').trim();
    const preserveDrawerUrlId = (options.preserveDrawerUrlId || '').trim();
    if (!domain) {
      this.planV2Rows = [];
      this.planV2Summary = null;
      this.planV2Meta = null;
      this.planV2Error = null;
      this.planV2Period = null;
      this.impactBrief = null;
      this.impactBriefLoading = false;
      return;
    }
    this.loadPlanV2Period(domain);
    this.loadImpactBrief(domain);
    this.planV2Page = page;
    if (!preserveDrawerUrlId) {
      this.planV2DrawerRow = null;
    }
    this.planV2Loading = true;
    this.planV2Error = null;
    this.inventoryPlanClusters
      .getActionPlanV2(domain, { page: this.planV2Page, pageSize: this.planV2PageSize })
      .subscribe({
        next: (res) => {
          this.planV2Rows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
          this.planV2Summary = res?.data?.summary ?? null;
          this.planV2Meta = res?.data?.meta ?? null;
          if (preserveDrawerUrlId) {
            this.planV2DrawerRow =
              this.planV2Rows.find((row) => row.urlId === preserveDrawerUrlId) ?? null;
          }
          this.planV2Loading = false;
        },
        error: (err) => {
          this.planV2Rows = [];
          this.planV2Summary = null;
          this.planV2Meta = null;
          if (preserveDrawerUrlId) {
            this.planV2DrawerRow = null;
          }
          this.planV2Loading = false;
          this.planV2Error = err?.error?.message || 'Could not load Plan v2.';
        }
      });
  }

  /** Two states that look alike in one line, so they get separate sentences. */
  planV2PeriodHint(): string {
    return this.planV2Period?.hasPair
      ? 'The crawl pair this queue is about — the same period the dashboard shows.'
      : 'Only one crawl has been captured for this domain, so there is no previous crawl to compare against yet.';
  }

  /**
   * Re-read the period alongside the queue. A capture turns the period over, so
   * the label is refetched rather than served from the session cache — it is one
   * indexed summary read, and a stale header on a fresh queue is exactly the
   * "last period's leftovers" confusion this pass exists to remove.
   */
  private loadPlanV2Period(domain: string): void {
    this.subscriptions.add(
      this.crawlRunPeriod.viewFor(domain, { refresh: true }).subscribe((view) => {
        // Ignore a late response for a domain the operator has since left.
        if ((this.selectedDomain || '').trim().toLowerCase() !== domain.trim().toLowerCase()) return;
        this.planV2Period = view;
      })
    );
  }

  /**
   * The packages strip, loaded with the queue it sits above.
   *
   * The domain is passed explicitly rather than left for the server to resolve.
   * Issues is always *about* a chosen domain, and letting the server pick here
   * while the Dashboard pins its own `scopeDomain` is the one way documented on
   * `getBrief` that a multi-domain workspace could show two different package
   * sets — the trust bar breaking silently, on the surface built to prove it.
   *
   * A null payload — a failed read, or the Action Plan v2 flag being off, which
   * the service also lands here as null — renders no strip at all rather than an
   * error. The packages are a skim over a queue that loads on its own; losing
   * them must not cost the operator the brief underneath.
   */
  private loadImpactBrief(domain: string): void {
    this.impactBriefLoading = true;
    this.subscriptions.add(
      this.impactBriefService
        .getBrief(domain, { expandedPackageIds: this.expandedPackageIds })
        .subscribe({
        next: (payload) => {
          // Ignore a late response for a domain the operator has since left, so
          // the strip can never describe one site above another site's queue.
          if ((this.selectedDomain || '').trim().toLowerCase() !== domain.trim().toLowerCase()) return;
          this.impactBrief = payload;
          this.impactBriefLoading = false;
        },
        error: () => {
          if ((this.selectedDomain || '').trim().toLowerCase() !== domain.trim().toLowerCase()) return;
          this.impactBrief = null;
          this.impactBriefLoading = false;
        }
      })
    );
  }

  /**
   * "+13 more pages in this package" — the same behaviour the Dashboard has.
   *
   * Does not set `impactBriefLoading`: the strip stays on screen and only the
   * clicked package's footer says it is working.
   */
  expandImpactPackage(packageId: string): void {
    const domain = (this.selectedDomain || '').trim();
    if (!packageId || !domain || this.expandingPackageId) return;
    if (!this.expandedPackageIds.includes(packageId)) {
      this.expandedPackageIds = [...this.expandedPackageIds, packageId];
    }
    this.expandingPackageId = packageId;

    this.subscriptions.add(
      this.impactBriefService
        .getBrief(domain, { expandedPackageIds: this.expandedPackageIds })
        .subscribe({
          next: (payload) => {
            if ((this.selectedDomain || '').trim().toLowerCase() !== domain.toLowerCase()) return;
            if (payload) this.impactBrief = payload;
            this.expandingPackageId = null;
          },
          error: () => {
            this.expandingPackageId = null;
          }
        })
    );
  }

  /** Whether the strip has a payload to render. Null means show nothing at all. */
  hasImpactBrief(): boolean {
    return this.impactBrief !== null;
  }

  planV2IgnoredUrlIds(): Set<string> {
    return this.planV2IssueQueue.ignoredUrlIds();
  }

  isPlanV2UrlIgnored(row: ActionPlanV2Row): boolean {
    return this.planV2IssueQueue.isUrlIgnored(row.urlId);
  }

  planV2ActiveRows(): ActionPlanV2Row[] {
    const ignored = this.planV2IgnoredUrlIds();
    return this.planV2Rows.filter((row) => !ignored.has(row.urlId));
  }

  planV2IgnoredRows(): ActionPlanV2Row[] {
    const ignored = this.planV2IgnoredUrlIds();
    return this.planV2Rows.filter((row) => ignored.has(row.urlId));
  }

  /**
   * Pages whose only decision was a dismissed card — the third restore case.
   *
   * "Re-open page" lists work items and "Show ignored" lists put-aside URLs;
   * a page whose Technical card was waved off is in neither, and until now the
   * only way to get its cards back was Regenerate queue, which resets the whole
   * domain's period.
   */
  planV2DismissedOnlyGroups(): PlanV2WorkItemsUrlGroup[] {
    return this.planV2IssueQueue.dismissedOnlyUrlGroups();
  }

  /** Same reopen the work-item groups use — one page's decisions, back to the queue. */
  planV2RestoreDismissedUrl(group: PlanV2WorkItemsUrlGroup, event?: Event): void {
    this.planV2ReopenUrl(group, event);
  }

  /**
   * Pages hidden from the queue, however they were hidden.
   *
   * One count for one toggle: put-aside URLs and pages carrying dismissed
   * cards are two different states with two different Restore buttons, but an
   * operator looking for something that disappeared should not have to know
   * which kind it was before they can go looking.
   */
  /**
   * Reveal the hidden pages AND take the operator to them.
   *
   * Ticking the flag alone leaves them 1,600px below, which is the same
   * problem the notice exists to solve. The notice is in the header; the rows
   * are past the packages strip, so the scroll is part of the answer.
   */
  planV2RevealHidden(): void {
    this.planV2ShowIgnored = true;
    // Polled rather than fired on the next tick: the rows are rendered by a
    // change-detection pass that has not run yet when this returns, and a
    // scroll to an element that does not exist leaves the operator exactly
    // where the notice found them, having pressed a button that did nothing
    // visible.
    //
    // Six seconds of budget, because the first attempt gave one and measured
    // 1,000ms on the pilot's table — the retry expired in the same frame the
    // rows arrived, which looks identical to the button being broken. Bounded
    // all the same so a render that never comes cannot spin.
    let tries = 0;
    const land = () => {
      const target = document.querySelector('.plan-v2-ignored-divider');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (tries++ < 60) setTimeout(land, 100);
    };
    setTimeout(land, 100);
  }

  planV2HiddenPageCount(): number {
    return this.planV2IgnoredRows().length + this.planV2DismissedOnlyGroups().length;
  }

  trackPlanV2DismissedGroup(_i: number, group: PlanV2WorkItemsUrlGroup): string {
    return group.urlId;
  }

  /** "Technical, Internal links" — what was waved off, so the row is readable. */
  planV2DismissedCardTitles(group: PlanV2WorkItemsUrlGroup): string {
    const titles: string[] = [];
    for (const item of group.items) {
      const title = (item.cardTitle || '').trim();
      if (title && !titles.includes(title)) titles.push(title);
    }
    return titles.slice(0, 4).join(', ') + (titles.length > 4 ? `, +${titles.length - 4}` : '');
  }

  planV2DismissedCardCount(): number {
    return this.planV2IssueQueue.dismissedCount();
  }

  /** Dismissed cards, ignored URLs, and work items saved in **this** period. */
  planV2TriagePersistedCount(): number {
    return this.planV2IssueQueue.currentPeriodCount();
  }

  planV2HasPreviousPeriod(): boolean {
    return this.planV2IssueQueue.hasPreviousPeriod;
  }

  planV2PreviousWorkItems(): PlanV2IssueDispositionRecord[] {
    return this.planV2IssueQueue.previousWorkItems();
  }

  planV2PreviousWorkItemsByUrl(): PlanV2WorkItemsUrlGroup[] {
    return this.planV2IssueQueue.previousWorkItemsByUrl();
  }

  planV2PreviousWorkItemGroups(): PlanV2WorkItemGroup[] {
    return this.planV2IssueQueue.previousWorkItemGroups();
  }

  planV2PreviousDismissedItems(): PlanV2IssueDispositionRecord[] {
    return this.planV2IssueQueue.previousDismissedItems();
  }

  planV2PreviousItemCount(): number {
    return this.planV2IssueQueue.previousItemCount();
  }

  /** Open URLs in the synthesis pool (domain-wide), minus URLs the user ignored. */
  planV2AttentionCount(): number {
    const open = this.planV2Summary?.openQueueItems;
    if (open == null) {
      return this.planV2ActiveRows().length;
    }
    return Math.max(0, open - this.planV2IgnoredUrlIds().size);
  }

  planV2IgnoreUrl(row: ActionPlanV2Row, event?: Event): void {
    event?.stopPropagation();
    const domain = (this.selectedDomain || '').trim();
    if (!domain || this.planV2IgnoringUrlId) return;
    this.planV2IgnoringUrlId = row.urlId;
    if (this.planV2DrawerRow?.urlId === row.urlId) {
      this.planV2DrawerRow = null;
    }
    this.planV2IssueQueue.ignoreUrl(domain, row.urlId, row.url, row.priorityScore).subscribe({
      next: () => {
        this.planV2IgnoringUrlId = null;
        this.notifications.success('URL hidden from Issues queue.');
      },
      error: () => {
        this.planV2IgnoringUrlId = null;
        this.notifications.error('Could not ignore URL.');
      }
    });
  }

  planV2RestoreIgnoredUrl(row: ActionPlanV2Row, event?: Event): void {
    event?.stopPropagation();
    const domain = (this.selectedDomain || '').trim();
    if (!domain) return;
    this.planV2IssueQueue.restoreIgnoredUrl(domain, row.urlId).subscribe({
      next: () => this.notifications.success('URL returned to Issues queue.'),
      error: () => this.notifications.error('Could not restore URL.')
    });
  }

  /** Resets **this period's** triage only. Previous is history, not queue state. */
  planV2RegenerateQueue(): void {
    const domain = (this.selectedDomain || '').trim();
    if (!domain || this.planV2Regenerating) return;
    const persisted = this.planV2TriagePersistedCount();
    if (persisted === 0) {
      this.notifications.success('Nothing to reset — this period’s triage is already empty.');
      return;
    }
    const workItems = this.planV2WorkItems().length;
    const dismissed = this.planV2DismissedCardCount();
    const kept = this.planV2PreviousItemCount();
    const msg =
      `Reset this period’s triage for this domain?\n\n` +
      `This clears ${persisted} item${persisted === 1 ? '' : 's'} saved this period ` +
      `(${dismissed} dismissed/ignored` +
      (workItems ? `, ${workItems} work item${workItems === 1 ? '' : 's'}` : '') +
      `). The living brief starts fresh.` +
      (kept
        ? `\n\nPrevious keeps its ${kept} item${kept === 1 ? '' : 's'} — last period’s history is not touched.`
        : '');
    if (!window.confirm(msg)) return;
    this.planV2Regenerating = true;
    this.planV2IssueQueue.regenerateQueue(domain, 'current').subscribe({
      next: (res) => {
        this.planV2Regenerating = false;
        const n = res?.data?.clearedCount ?? persisted;
        this.notifications.success(
          `This period’s queue reset — ${n} triage item${n === 1 ? '' : 's'} cleared.`
        );
      },
      error: () => {
        this.planV2Regenerating = false;
        this.notifications.error('Could not regenerate queue.');
      }
    });
  }

  /** Explicit operator action from the Previous tab — never automatic. */
  planV2ClearPreviousPeriod(): void {
    const domain = (this.selectedDomain || '').trim();
    if (!domain || this.planV2ClearingPrevious) return;
    const count = this.planV2PreviousItemCount();
    if (count === 0) return;
    const msg =
      `Delete last period’s history for this domain?\n\n` +
      `This removes ${count} item${count === 1 ? '' : 's'} you accepted or dismissed before this period began. ` +
      `This period’s brief and work items are not touched.`;
    if (!window.confirm(msg)) return;
    this.planV2ClearingPrevious = true;
    this.planV2IssueQueue.regenerateQueue(domain, 'previous').subscribe({
      next: (res) => {
        this.planV2ClearingPrevious = false;
        const n = res?.data?.clearedCount ?? count;
        this.notifications.success(`History cleared — ${n} previous item${n === 1 ? '' : 's'} removed.`);
      },
      error: () => {
        this.planV2ClearingPrevious = false;
        this.notifications.error('Could not clear previous history.');
      }
    });
  }

  planV2WorkItems(): PlanV2IssueDispositionRecord[] {
    return this.planV2IssueQueue.workItems();
  }

  planV2WorkItemsByUrl(): PlanV2WorkItemsUrlGroup[] {
    return this.planV2IssueQueue.workItemsByUrl();
  }

  /** Site-wide first, then one group per page. */
  planV2WorkItemGroups(): PlanV2WorkItemGroup[] {
    return this.planV2IssueQueue.workItemGroups();
  }

  planV2SiteWideGroupNote(): string {
    return SITE_WIDE_GROUP_NOTE;
  }

  planV2SiteWideWorkItemCount(): number {
    return this.planV2WorkItemGroups().find((group) => group.kind === 'site_wide')?.items.length ?? 0;
  }

  /**
   * Put one page back into the belt.
   *
   * The count is said out loud before anything moves, because "re-open" over an
   * unknown number of decisions is a button nobody presses twice.
   */
  planV2ReopenUrl(group: PlanV2WorkItemsUrlGroup, event?: Event): void {
    event?.stopPropagation();
    const domain = (this.selectedDomain || '').trim();
    if (!domain || this.workItemReopeningUrlId) return;

    const plan = this.planV2IssueQueue.urlReopenPlan(group.urlId, group.url);
    if (!plan.total) {
      this.notifications.success(PLAN_V2_URL_REOPEN_NOTHING_TO_DO);
      return;
    }
    if (!window.confirm(planV2UrlReopenConfirmMessage(plan))) return;

    this.workItemReopeningUrlId = group.urlId;
    this.planV2IssueQueue.reopenUrl(domain, plan).subscribe({
      next: (count) => {
        this.workItemReopeningUrlId = null;
        this.notifications.success(planV2UrlReopenResultMessage(count));
      },
      error: () => {
        this.workItemReopeningUrlId = null;
        this.notifications.error('Could not re-open this page.');
      }
    });
  }

  planV2WorkItemsRecap(): WorkItemRecapLine[] {
    return summarizeWorkItemsRecap(this.planV2WorkItems());
  }

  planV2CarriedForward(item: PlanV2IssueDispositionRecord): boolean {
    return Boolean(item.carriedForwardAt);
  }

  carryWorkItemForward(item: PlanV2IssueDispositionRecord, event?: Event): void {
    event?.stopPropagation();
    const domain = (this.selectedDomain || '').trim();
    if (!domain || this.workItemCarryingKey) return;
    this.workItemCarryingKey = item.issueKey;
    this.planV2IssueQueue.carryForward(domain, item.issueKey).subscribe({
      next: () => {
        this.workItemCarryingKey = null;
        this.notifications.success('Added to this period’s work items.');
      },
      error: (err: any) => {
        this.workItemCarryingKey = null;
        this.notifications.error(err?.error?.message || 'Could not carry this item forward.');
      }
    });
  }

  openWorkItemsTab(): void {
    this.setInventorySection('workItems');
  }

  openPreviousTab(): void {
    this.setInventorySection('previous');
  }

  returnWorkItemToIssues(item: PlanV2IssueDispositionRecord, event?: Event): void {
    event?.stopPropagation();
    const domain = (this.selectedDomain || '').trim();
    if (!domain || this.workItemReturningKey) return;
    this.workItemReturningKey = item.issueKey;
    this.planV2IssueQueue.setDisposition(domain, item.issueKey, 'issues').subscribe({
      next: () => {
        this.workItemReturningKey = null;
        this.notifications.success('Returned to Issues.');
      },
      error: () => {
        this.workItemReturningKey = null;
        this.notifications.error('Could not return item to Issues.');
      }
    });
  }

  async exportWorkItemsPdf(): Promise<void> {
    const items = this.planV2WorkItems();
    if (!items.length || this.workItemsExporting) return;
    this.workItemsExporting = true;
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;
      const contentWidth = pageWidth - margin * 2;
      let y = margin;
      const domain = (this.selectedDomain || '').trim();
      const exportDate = new Date().toISOString().slice(0, 10);
      // Site-wide first, same as on screen. Exporting the per-URL groups alone
      // would drop template fixes out of the writer's list entirely.
      const groups = this.planV2WorkItemGroups();
      const pageCount = groups.filter((group) => group.kind === 'url').length;
      const theme = {
        pageBg: [18, 21, 27] as const,
        panelBg: [25, 30, 40] as const,
        panelBorder: [52, 62, 80] as const,
        panelSoft: [30, 36, 48] as const,
        textPrimary: [232, 238, 247] as const,
        textMuted: [154, 167, 186] as const,
        accent: [59, 130, 246] as const,
        link: [96, 165, 250] as const
      };
      const shorten = (value: string, max = 72): string =>
        value.length > max ? `${value.slice(0, Math.max(0, max - 1))}\u2026` : value;

      const paintPageBackground = (): void => {
        doc.setFillColor(...theme.pageBg);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
      };

      const ensureSpace = (needed = 16): number => {
        if (y + needed <= pageHeight - margin) return y;
        doc.addPage();
        paintPageBackground();
        y = margin;
        return y;
      };

      paintPageBackground();

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.setTextColor(...theme.textPrimary);
      // Exports this period's commitments only — Previous is compare-only history.
      doc.text('SEO Action Plan — Work Items (this period)', margin, y);
      y += 18;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...theme.textMuted);
      doc.text(
        `Domain: ${domain || 'N/A'}   |   Exported: ${exportDate}   |   Pages: ${pageCount}   |   Tasks: ${items.length}`,
        margin,
        y
      );
      y += 20;

      const overviewRows = summarizeWorkItemsOverview(items);
      if (overviewRows.length) {
        y += this.renderWorkItemsOverviewTable(
          doc,
          overviewRows,
          margin,
          y,
          contentWidth,
          pageHeight,
          theme,
          paintPageBackground
        );
      }

      for (const group of groups) {
        const bodyMaxWidth = contentWidth - 24;
        const contentX = margin + 12;
        const maxPageContent = pageHeight - margin * 2;
        let firstCardEstimate = 48;
        if (group.items.length) {
          firstCardEstimate = this.estimateWorkItemPdfCardHeight(group.items[0], doc, bodyMaxWidth).totalHeight;
        }
        y = ensureSpace(28 + Math.min(firstCardEstimate, maxPageContent) + 6);

        doc.setFillColor(...theme.panelSoft);
        doc.setDrawColor(...theme.panelBorder);
        doc.rect(margin, y, contentWidth, 24, 'FD');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        if (group.kind === 'site_wide') {
          // No page behind it, so no link — a header that looked like a URL
          // would be inviting the writer to go and edit one.
          doc.setTextColor(...theme.textPrimary);
          doc.text(group.label, margin + 8, y + 16);
        } else {
          doc.setTextColor(...theme.link);
          workItemPdfTextWithLink(doc, shorten(this.formatUrl(group.url, false), 88), margin + 8, y + 16, group.url);
        }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...theme.textMuted);
        const taskLabel = `${group.items.length} task${group.items.length === 1 ? '' : 's'}`;
        doc.text(taskLabel, margin + contentWidth - doc.getTextWidth(taskLabel) - 8, y + 16);
        y += 28;

        for (let itemIndex = 0; itemIndex < group.items.length; itemIndex++) {
          const item = group.items[itemIndex];
          const { lines, totalHeight } = this.estimateWorkItemPdfCardHeight(item, doc, bodyMaxWidth);
          if (totalHeight > maxPageContent) {
            y = this.renderWorkItemPdfCardPaginated(
              doc,
              item,
              lines,
              margin,
              contentWidth,
              contentX,
              bodyMaxWidth,
              pageHeight,
              theme,
              paintPageBackground,
              ensureSpace
            );
          } else {
            y = this.renderWorkItemPdfCardAtomic(
              doc,
              item,
              lines,
              totalHeight,
              itemIndex,
              margin,
              contentWidth,
              contentX,
              bodyMaxWidth,
              theme,
              ensureSpace
            );
          }
        }
        y += 8;
      }

      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...theme.textMuted);
        doc.text(`Page ${i} / ${totalPages}`, pageWidth - margin - 44, pageHeight - 16);
      }

      const safeDomain = (domain || 'domain').replace(/[^a-z0-9.-]+/gi, '-').replace(/^-+|-+$/g, '');
      saveWorkItemsPdf(doc, `work-items-${safeDomain || 'domain'}-${exportDate}.pdf`);
      this.notifications.success('Work items exported as PDF.');
    } catch {
      this.notifications.error('PDF export failed.');
    } finally {
      this.workItemsExporting = false;
    }
  }

  /** Rich lines for work-item PDF cards (matches on-screen work item body). */
  private layoutWorkItemsOverviewTable(
    doc: import('jspdf').jsPDF,
    rows: WorkItemOverviewRow[],
    width: number
  ): { height: number; rowHeights: number[] } {
    const cols = this.workItemsOverviewColumns(width);
    const headerH = 18;
    const rowPadY = 5;
    const lineH = 10;
    const rowHeights: number[] = [];

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    for (const row of rows) {
      const descLines = doc.splitTextToSize(row.description, cols.description - 10);
      rowHeights.push(Math.max(22, descLines.length * lineH + rowPadY * 2));
    }

    const bodyH = rowHeights.reduce((sum, h) => sum + h, 0);
    return { height: 16 + headerH + bodyH + 10, rowHeights };
  }

  private workItemsOverviewColumns(width: number): {
    type: number;
    count: number;
    impact: number;
    effort: number;
    description: number;
    typeX: number;
    countX: number;
    impactX: number;
    effortX: number;
    descX: number;
  } {
    const type = 112;
    const count = 26;
    const impact = 42;
    const effort = 42;
    const description = width - type - count - impact - effort;
    return {
      type,
      count,
      impact,
      effort,
      description,
      typeX: 0,
      countX: type,
      impactX: type + count,
      effortX: type + count + impact,
      descX: type + count + impact + effort
    };
  }

  private drawWorkItemsOverviewTableHeader(
    doc: import('jspdf').jsPDF,
    x: number,
    y: number,
    width: number,
    cols: {
      countX: number;
      impactX: number;
      effortX: number;
      descX: number;
    },
    theme: { panelSoft: readonly [number, number, number]; panelBorder: readonly [number, number, number]; textMuted: readonly [number, number, number] }
  ): number {
    const headerH = 18;
    doc.setFillColor(...theme.panelSoft);
    doc.setDrawColor(...theme.panelBorder);
    doc.rect(x, y, width, headerH, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...theme.textMuted);
    doc.text('Issue type', x + 6, y + 12);
    doc.text('Count', x + cols.countX + 6, y + 12);
    doc.text('Impact', x + cols.impactX + 6, y + 12);
    doc.text('Effort', x + cols.effortX + 6, y + 12);
    doc.text('What it is & why it matters', x + cols.descX + 6, y + 12);
    return headerH;
  }

  private renderWorkItemsOverviewTable(
    doc: import('jspdf').jsPDF,
    rows: WorkItemOverviewRow[],
    x: number,
    y: number,
    width: number,
    pageHeight: number,
    theme: {
      pageBg: readonly [number, number, number];
      textPrimary: readonly [number, number, number];
      textMuted: readonly [number, number, number];
      accent: readonly [number, number, number];
      panelBg: readonly [number, number, number];
      panelSoft: readonly [number, number, number];
      panelBorder: readonly [number, number, number];
    },
    paintPageBackground: () => void
  ): number {
    const margin = 40;
    const bottom = pageHeight - margin;
    const cols = this.workItemsOverviewColumns(width);
    const layout = this.layoutWorkItemsOverviewTable(doc, rows, width);
    const startY = y;
    let tableTop = y + 16;

    const ensureOverviewSpace = (needed: number): void => {
      if (y + needed <= bottom) return;
      doc.addPage();
      paintPageBackground();
      y = margin;
      tableTop = y;
    };

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...theme.textPrimary);
    doc.text('Overview', x, y + 12);
    y += 16;

    const headerH = 18;
    ensureOverviewSpace(headerH + 22);
    y += this.drawWorkItemsOverviewTableHeader(doc, x, y, width, cols, theme);

    rows.forEach((row, index) => {
      const rowH = layout.rowHeights[index];
      ensureOverviewSpace(rowH);
      if (y === margin && index > 0) {
        y += this.drawWorkItemsOverviewTableHeader(doc, x, y, width, cols, theme);
        ensureOverviewSpace(rowH);
      }

      if (index % 2 === 0) {
        doc.setFillColor(...theme.panelBg);
        doc.rect(x, y, width, rowH, 'F');
      }
      doc.setDrawColor(...theme.panelBorder);
      doc.line(x, y + rowH, x + width, y + rowH);

      const textY = y + 13;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...theme.textPrimary);
      const typeLines = doc.splitTextToSize(row.type, cols.type - 10);
      doc.text(typeLines[0], x + 6, textY);

      doc.setFont('helvetica', 'normal');
      doc.text(String(row.count), x + cols.countX + 6, textY);

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...this.workItemOverviewLevelColor(row.impact));
      doc.text(row.impact, x + cols.impactX + 6, textY);

      doc.setTextColor(...this.workItemOverviewLevelColor(row.effort));
      doc.text(row.effort, x + cols.effortX + 6, textY);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(...theme.textMuted);
      const descLines = doc.splitTextToSize(row.description, cols.description - 10);
      doc.text(descLines, x + cols.descX + 6, textY);

      y += rowH;
    });

    doc.setDrawColor(...theme.panelBorder);
    if (y > tableTop) {
      doc.rect(x, tableTop, width, y - tableTop, 'S');
    }

    return y - startY + 10;
  }

  private workItemOverviewLevelColor(level: WorkItemOverviewRow['impact']): readonly [number, number, number] {
    if (level === 'High') return [248, 113, 113]; // red
    if (level === 'Medium') return [251, 146, 60]; // orange
    return [74, 222, 128]; // green (Low)
  }

  /** Rich lines for work-item PDF cards (matches on-screen work item body). */
  private resetWorkItemPdfFont(doc: import('jspdf').jsPDF): void {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
  }

  private estimateWorkItemPdfCardHeight(
    item: PlanV2IssueDispositionRecord,
    doc: import('jspdf').jsPDF,
    bodyMaxWidth: number
  ): { lines: WorkItemPdfLine[]; bodyHeight: number; totalHeight: number } {
    this.resetWorkItemPdfFont(doc);
    const lines = this.collectWorkItemPdfLines(item, doc, bodyMaxWidth);
    const bodyHeight = this.measureWorkItemPdfContentHeight(lines, doc, bodyMaxWidth);
    const chipH = 13;
    const totalHeight = Math.max(36, 6 + chipH + 8 + bodyHeight + 8);
    return { lines, bodyHeight, totalHeight };
  }

  private renderWorkItemPdfCardAtomic(
    doc: import('jspdf').jsPDF,
    item: PlanV2IssueDispositionRecord,
    bodyLines: WorkItemPdfLine[],
    estimatedHeight: number,
    itemIndex: number,
    margin: number,
    contentWidth: number,
    contentX: number,
    bodyMaxWidth: number,
    theme: {
      panelBg: readonly [number, number, number];
      panelBorder: readonly [number, number, number];
      pageBg: readonly [number, number, number];
      textPrimary: readonly [number, number, number];
      textMuted: readonly [number, number, number];
      accent: readonly [number, number, number];
      panelSoft: readonly [number, number, number];
      link: readonly [number, number, number];
    },
    ensureSpace: (needed?: number) => number
  ): number {
    const cardTop = ensureSpace(estimatedHeight + 6);
    const chipH = 13;
    const chipTop = cardTop + 6;

    doc.setDrawColor(...theme.panelBorder);
    if (itemIndex % 2 === 0) {
      doc.setFillColor(...theme.panelBg);
      doc.roundedRect(margin, cardTop, contentWidth, estimatedHeight, 6, 6, 'FD');
    } else {
      doc.roundedRect(margin, cardTop, contentWidth, estimatedHeight, 6, 6, 'S');
    }

    this.drawWorkItemPdfPillChip(doc, workItemPdfChipLabel(item), contentX, chipTop, chipH, theme, 'type');

    let lineY = chipTop + chipH + 8;
    for (let lineIndex = 0; lineIndex < bodyLines.length; lineIndex++) {
      lineY += this.renderWorkItemPdfLine(doc, bodyLines[lineIndex], contentX, lineY, bodyMaxWidth, theme);
      if (lineIndex < bodyLines.length - 1) {
        lineY += WORK_ITEM_PDF_PARAGRAPH_GAP;
      }
    }

    const actualHeight = Math.max(36, lineY - cardTop + 8);
    if (actualHeight + 6 < estimatedHeight - 2) {
      doc.setFillColor(...theme.pageBg);
      doc.rect(margin + 1, cardTop + actualHeight, contentWidth - 2, estimatedHeight - actualHeight, 'F');
      doc.setDrawColor(...theme.panelBorder);
      doc.line(margin, cardTop + actualHeight, margin + contentWidth, cardTop + actualHeight);
    }

    return cardTop + actualHeight + 6;
  }

  private renderWorkItemPdfCardPaginated(
    doc: import('jspdf').jsPDF,
    item: PlanV2IssueDispositionRecord,
    bodyLines: WorkItemPdfLine[],
    margin: number,
    contentWidth: number,
    contentX: number,
    bodyMaxWidth: number,
    pageHeight: number,
    theme: {
      textPrimary: readonly [number, number, number];
      textMuted: readonly [number, number, number];
      accent: readonly [number, number, number];
      panelSoft: readonly [number, number, number];
      panelBorder: readonly [number, number, number];
      link: readonly [number, number, number];
      pageBg: readonly [number, number, number];
    },
    paintPageBackground: () => void,
    ensureSpace: (needed?: number) => number
  ): number {
    const chipH = 13;
    const cardTop = ensureSpace(6 + chipH + 8 + WORK_ITEM_PDF_LINE_HEIGHT);
    this.drawWorkItemPdfPillChip(doc, workItemPdfChipLabel(item), contentX, cardTop + 6, chipH, theme, 'type');
    let lineY = cardTop + 6 + chipH + 8;

    for (let lineIndex = 0; lineIndex < bodyLines.length; lineIndex++) {
      const lineHeight =
        this.measureWorkItemPdfLine(doc, bodyLines[lineIndex], bodyMaxWidth) * WORK_ITEM_PDF_LINE_HEIGHT;
      const gap = lineIndex < bodyLines.length - 1 ? WORK_ITEM_PDF_PARAGRAPH_GAP : 0;
      if (lineY + lineHeight + gap > pageHeight - margin) {
        doc.addPage();
        paintPageBackground();
        lineY = margin;
      }
      lineY += this.renderWorkItemPdfLine(doc, bodyLines[lineIndex], contentX, lineY, bodyMaxWidth, theme);
      if (gap) lineY += gap;
    }

    return lineY + 8;
  }

  private collectWorkItemPdfLines(
    item: PlanV2IssueDispositionRecord,
    doc: import('jspdf').jsPDF,
    maxWidth: number
  ): WorkItemPdfLine[] {
    const lines: WorkItemPdfLine[] = [];
    const pushText = (text: string, style: WorkItemPdfSegmentStyle = 'normal'): void => {
      const trimmed = (text || '').trim();
      if (!trimmed) return;
      this.applyWorkItemPdfSegmentStyle(doc, { text: trimmed, style });
      for (const wrapped of doc.splitTextToSize(trimmed, maxWidth)) {
        const line = String(wrapped).trim();
        if (line) lines.push({ segments: [{ text: line, style }] });
      }
    };
    const pushLine = (line: WorkItemPdfLine): void => {
      lines.push(line);
    };

    const detail = parseWorkItemDetail(item.actionDetail);
    if (!detail) {
      if (item.why) pushText(item.why, 'muted');
      if (item.changeSummary) pushText(item.changeSummary);
      return lines;
    }

    this.pushWorkItemDetailPdfLines(detail, item, pushText, pushLine);
    if (!lines.length && item.changeSummary) pushText(item.changeSummary);
    return lines;
  }

  private measureWorkItemPdfContentHeight(
    lines: WorkItemPdfLine[],
    doc: import('jspdf').jsPDF,
    maxWidth: number
  ): number {
    if (!lines.length) return 0;
    let height = 0;
    for (let index = 0; index < lines.length; index++) {
      height += this.measureWorkItemPdfLine(doc, lines[index], maxWidth) * WORK_ITEM_PDF_LINE_HEIGHT;
      if (index < lines.length - 1) height += WORK_ITEM_PDF_PARAGRAPH_GAP;
    }
    return height;
  }

  private measureWorkItemPdfLine(
    doc: import('jspdf').jsPDF,
    line: WorkItemPdfLine,
    maxWidth: number
  ): number {
    const indent = line.indentPt || 0;
    const contentWidth = Math.max(48, maxWidth - indent);
    if (
      line.segments.length === 1 &&
      line.segments[0].style !== 'link' &&
      line.segments[0].style !== 'headingChip' &&
      line.segments[0].style !== 'chip'
    ) {
      this.applyWorkItemPdfSegmentStyle(doc, line.segments[0]);
      return doc.splitTextToSize(line.segments[0].text, contentWidth).length || 1;
    }
    return this.layoutWorkItemPdfLine(doc, line, maxWidth).lineCount;
  }

  private measurePdfSegmentTokenWidth(doc: import('jspdf').jsPDF, seg: WorkItemPdfSegment, token: string): number {
    if (seg.style === 'headingChip') return this.measureWorkItemPdfPillChipWidth(doc, token, 'heading');
    if (seg.style === 'chip') return this.measureWorkItemPdfPillChipWidth(doc, token, 'direction');
    return doc.getTextWidth(token);
  }

  private measureWorkItemPdfPillChipWidth(
    doc: import('jspdf').jsPDF,
    text: string,
    variant: 'type' | 'heading' | 'direction'
  ): number {
    const fontSize = variant === 'type' ? 8 : 7.5;
    const padX = variant === 'type' ? 5 : 3;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(fontSize);
    return doc.getTextWidth(text) + padX * 2 + (variant === 'type' ? 0 : 3);
  }

  private drawWorkItemPdfPillChip(
    doc: import('jspdf').jsPDF,
    text: string,
    x: number,
    chipTop: number,
    chipH: number,
    theme: {
      textPrimary: readonly [number, number, number];
      textMuted: readonly [number, number, number];
      accent: readonly [number, number, number];
      panelSoft: readonly [number, number, number];
      panelBorder: readonly [number, number, number];
    },
    variant: 'type' | 'heading' | 'direction'
  ): number {
    const fontSize = variant === 'type' ? 8 : 7.5;
    const padX = variant === 'type' ? 5 : 3;
    const radius = variant === 'type' ? 4 : 2;
    const colors =
      variant === 'direction'
        ? { fill: [35, 45, 68] as const, border: theme.accent, text: theme.accent }
        : variant === 'heading'
          ? { fill: theme.panelSoft, border: theme.panelBorder, text: theme.textMuted }
          : { fill: theme.panelSoft, border: theme.panelBorder, text: theme.textPrimary };

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(fontSize);
    const chipW = doc.getTextWidth(text) + padX * 2;
    doc.setFillColor(...colors.fill);
    doc.setDrawColor(...colors.border);
    doc.roundedRect(x, chipTop, chipW, chipH, radius, radius, 'FD');
    doc.setTextColor(...colors.text);
    doc.text(text, x + padX, chipTop + chipH / 2, { baseline: 'middle' });
    return chipW;
  }

  private layoutWorkItemPdfLine(
    doc: import('jspdf').jsPDF,
    line: WorkItemPdfLine,
    maxWidth: number
  ): { lineCount: number } {
    const indent = line.indentPt || 0;
    const contentWidth = Math.max(48, maxWidth - indent);
    const wrapAt = indent + contentWidth;
    doc.setFontSize(8.5);
    let cursorX = indent;
    let lineCount = 1;

    const startNewLine = (): void => {
      lineCount += 1;
      cursorX = indent;
    };

    for (const seg of line.segments) {
      this.applyWorkItemPdfSegmentStyle(doc, seg);
      const tokens =
        seg.style === 'link' || seg.style === 'chip' || seg.style === 'headingChip'
          ? [seg.text]
          : seg.text.split(/(\s+)/).filter((part) => part.length);
      for (const token of tokens) {
        const tokenWidth = this.measurePdfSegmentTokenWidth(doc, seg, token);
        if (cursorX + tokenWidth > wrapAt && cursorX > indent) startNewLine();
        cursorX += tokenWidth;
      }
    }

    return { lineCount: Math.max(1, lineCount) };
  }

  private applyWorkItemPdfSegmentStyle(doc: import('jspdf').jsPDF, seg: WorkItemPdfSegment): void {
    doc.setFontSize(seg.style === 'chip' || seg.style === 'headingChip' ? 7.5 : 8.5);
    const italic = seg.style === 'italic';
    const bold =
      seg.style === 'bold' || seg.style === 'chip' || seg.style === 'headingChip' || seg.style === 'missingHeading';
    doc.setFont('helvetica', italic ? 'italic' : bold ? 'bold' : 'normal');
    if (seg.style === 'link') doc.setTextColor(96, 165, 250);
    else if (seg.style === 'chip') doc.setTextColor(59, 130, 246);
    else if (seg.style === 'missingHeading') doc.setTextColor(248, 113, 113);
    else if (seg.style === 'headingChip' || seg.style === 'muted' || seg.style === 'italic') doc.setTextColor(154, 167, 186);
    else doc.setTextColor(232, 238, 247);
  }

  private workItemPdfInlineRowMidY(lineY: number): number {
    return lineY - 2;
  }

  private workItemPdfInlineChipTop(lineY: number, chipH: number): number {
    return this.workItemPdfInlineRowMidY(lineY) - chipH / 2;
  }

  private renderWorkItemPdfLine(
    doc: import('jspdf').jsPDF,
    line: WorkItemPdfLine,
    x: number,
    y: number,
    maxWidth: number,
    theme: {
      textPrimary: readonly [number, number, number];
      textMuted: readonly [number, number, number];
      link: readonly [number, number, number];
      accent: readonly [number, number, number];
      panelSoft: readonly [number, number, number];
      panelBorder: readonly [number, number, number];
    }
  ): number {
    const lineHeight = WORK_ITEM_PDF_LINE_HEIGHT;
    const indent = line.indentPt || 0;
    const contentX = x + indent;
    const wrapAt = x + maxWidth;
    let cursorX = contentX;
    let lineY = y;
    let linesUsed = 1;
    const useInlineMid =
      line.segments.some((seg) => seg.style === 'chip' || seg.style === 'headingChip') &&
      line.segments.some((seg) => seg.style === 'link');

    const startNewLine = (): void => {
      lineY += lineHeight;
      cursorX = contentX;
      linesUsed += 1;
    };

    for (const seg of line.segments) {
      this.applyWorkItemPdfSegmentStyle(doc, seg);
      const tokens =
        seg.style === 'link' || seg.style === 'chip' || seg.style === 'headingChip'
          ? [seg.text]
          : seg.text.split(/(\s+)/).filter((part) => part.length);
      for (const token of tokens) {
        const tokenWidth = this.measurePdfSegmentTokenWidth(doc, seg, token);
        if (cursorX + tokenWidth > wrapAt && cursorX > contentX) startNewLine();

        if (seg.style === 'headingChip') {
          const chipH = 10;
          const chipTop = useInlineMid
            ? this.workItemPdfInlineChipTop(lineY, chipH)
            : lineY - chipH / 2 - 1;
          cursorX += this.drawWorkItemPdfPillChip(doc, token, cursorX, chipTop, chipH, theme, 'heading') + 3;
        } else if (seg.style === 'link' && seg.url) {
          doc.setTextColor(...theme.link);
          if (useInlineMid) {
            workItemPdfTextWithLink(doc, token, cursorX, this.workItemPdfInlineRowMidY(lineY), seg.url, {
              baseline: 'middle'
            });
          } else {
            workItemPdfTextWithLink(doc, token, cursorX, lineY, seg.url);
          }
          cursorX += doc.getTextWidth(token);
        } else if (seg.style === 'chip') {
          const chipH = 10;
          const chipTop = useInlineMid
            ? this.workItemPdfInlineChipTop(lineY, chipH)
            : lineY - chipH / 2 - 1;
          cursorX += this.drawWorkItemPdfPillChip(doc, token, cursorX, chipTop, chipH, theme, 'direction') + 3;
        } else if (seg.style === 'muted' || seg.style === 'italic') {
          doc.setTextColor(...theme.textMuted);
          doc.text(token, cursorX, lineY);
          cursorX += doc.getTextWidth(token);
        } else if (seg.style === 'bold') {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8.5);
          doc.setTextColor(...theme.textPrimary);
          doc.text(token, cursorX, lineY);
          cursorX += doc.getTextWidth(token);
        } else if (seg.style === 'missingHeading') {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8.5);
          doc.setTextColor(248, 113, 113);
          doc.text(token, cursorX, lineY);
          cursorX += doc.getTextWidth(token);
        } else {
          doc.setTextColor(...theme.textPrimary);
          doc.text(token, cursorX, lineY);
          cursorX += doc.getTextWidth(token);
        }
      }
    }

    return linesUsed * lineHeight;
  }

  private pushWorkItemDetailPdfLines(
    detail: PlanV2WorkItemDetailV1,
    item: PlanV2IssueDispositionRecord,
    pushText: (text: string, style?: WorkItemPdfSegmentStyle) => void,
    pushLine: (line: WorkItemPdfLine) => void
  ): void {
    const showLead =
      item.why &&
      !detail.freshness?.paragraphs?.length &&
      !detail.intentPlacement &&
      !detail.technical &&
      !detail.technicalItems?.length &&
      !detail.internalLinkItems?.length &&
      !detail.supportingGaps?.length;
    if (showLead && item.why) pushText(item.why, 'muted');

    if (detail.intentPlacement) {
      const ip = detail.intentPlacement;
      pushText(
        `Strongest ${ip.familyBold}${ip.familySuffix} match in your content is lower down the page, under “${ip.sectionLabel}”. Consider moving that answer up.`,
        'muted'
      );
      if (ip.passage) pushText(`${ip.sectionLabel} — “${ip.passage}”`, 'italic');
    }

    if (detail.freshness?.paragraphs?.length) {
      pushFreshnessPdfLines(detail.freshness.paragraphs, pushLine);
    }

    if (detail.supportingGaps?.length) {
      pushSupportingGapPdfLines(detail.supportingGaps, (text, style) => pushText(text, style), pushLine);
    }

    for (const link of detail.internalLinkItems || []) {
      pushInternalLinkPdfLines(link, pushLine);
    }

    const hasSpecialBlocks =
      !!detail.freshness?.paragraphs?.length ||
      !!detail.intentPlacement ||
      !!detail.supportingGaps?.length ||
      !!detail.technical ||
      !!detail.technicalItems?.length ||
      !!detail.internalLinkItems?.length;

    if (!hasSpecialBlocks) {
      if (detail.missing?.length) pushText(`Missing phrases: ${detail.missing.join(', ')}`);
      if (detail.misaligned?.length) pushText(`Wrong context: ${detail.misaligned.join(', ')}`);
      for (const ex of workItemExampleEntries(detail)) {
        for (const line of ex.lines) pushText(`${ex.term}: ${line}`, 'italic');
      }
      if (detail.anchor && detail.targetPath) pushText(`Link: ${detail.anchor} → ${detail.targetPath}`);
      if (detail.sentence) pushText(`“${detail.sentence}”`, 'italic');
      if (detail.placement) pushText(`Where: ${detail.placement}`, 'muted');

      if (!detail.supportingGaps?.length) {
        for (const page of detail.gapPages || []) {
          pushText(`${page.url} — Missing topic: ${page.missingTopic}`);
        }
      }

      if (!detail.gapPages?.length && !detail.supportingGaps?.length) {
        for (const page of detail.pages || []) {
          pushText(page.topics ? `${page.label} · ${page.topics}` : page.label);
        }
      }

      for (const bullet of detail.bullets || []) pushText(`• ${bullet}`);
    }

    pushTechnicalItemsPdfLines(detail.technicalItems, detail.technical, pushText);
  }

  planV2GoToPage(page: number): void {
    const total = this.planV2Meta?.totalPages ?? 1;
    const next = Math.min(Math.max(1, page), total);
    if (next === this.planV2Page) return;
    this.loadPlanV2(next);
  }

  isPlanV2DrawerOpenFor(row: ActionPlanV2Row): boolean {
    return this.planV2DrawerRow?.urlId === row.urlId;
  }

  closePlanV2Drawer(): void {
    this.planV2DrawerRow = null;
  }

  togglePlanV2Drawer(row: ActionPlanV2Row, event?: Event): void {
    event?.stopPropagation();
    if (this.planV2DrawerRow?.urlId === row.urlId) {
      this.closePlanV2Drawer();
      return;
    }
    this.planV2DrawerRow = row;
  }

  onPlanV2IntentOverrideCompleted(): void {
    const drawerUrlId = this.planV2DrawerRow?.urlId ?? null;
    this.loadPlanV2(this.planV2Page, { preserveDrawerUrlId: drawerUrlId });
  }

  @HostListener('document:keydown.escape')
  onPlanV2DrawerEscape(): void {
    if (this.planV2DrawerRow) {
      this.closePlanV2Drawer();
    }
  }

  planV2OpenUrlDetail(row: ActionPlanV2Row, event?: Event): void {
    event?.stopPropagation();
    if (!row?.url) return;
    this.closePlanV2Drawer();
    this.router.navigate(['/inventory', encodeURIComponent(row.url)]);
  }

  openUrlDetailForUrl(url: string, event?: Event): void {
    event?.stopPropagation();
    if (!url?.trim()) return;
    this.router.navigate(['/inventory', encodeURIComponent(url.trim())]);
  }

  trackWorkItem(_index: number, item: PlanV2IssueDispositionRecord): string {
    return item.issueKey;
  }

  trackWorkItemGroup(_index: number, group: PlanV2WorkItemsUrlGroup): string {
    return group.urlId;
  }

  planV2SignalLabel(kind: string): string {
    if (kind === 'cannibalization') return 'Cannibal';
    if (kind === 'internal_link') return 'Link';
    if (kind === 'unclustered') return 'Unclustered';
    if (kind === 'no_cluster') return 'No cluster';
    if (kind === 'content_gap') return 'Sections';
    return kind;
  }

  /** The pair item's shared searches in one line: the first two named, then the card's count line. */
  planV2PairSearchesLabel(pair: NonNullable<ActionPlanV2Row['cannibal']>): string {
    const named = pair.searches.slice(0, 2).map((s) => `“${s.search}”`).join(', ');
    return named ? `${named}. ${pair.searchesLine}` : pair.searchesLine;
  }

  /** Each shared search with both pages' numbers, as the card's table lists them. */
  planV2PairSearchesTitle(pair: NonNullable<ActionPlanV2Row['cannibal']>): string {
    return pair.searches.map((s) => `“${s.search}”: this page ${s.thisPage}; the other page ${s.otherPage}`).join('\n');
  }

  planV2SignalTooltip(kind: string, row: ActionPlanV2Row): string {
    if (kind === 'content_gap' && (row.contentGapSectionCount ?? 0) > 0) {
      return contentGapSignalTooltip({
        sectionCount: row.contentGapSectionCount ?? 0,
        source: row.contentGapSource,
        measuredAt: row.contentGapMeasuredAt
      });
    }
    return this.planV2SignalLabel(kind);
  }

  trackPlanV2Row(_index: number, row: ActionPlanV2Row): string {
    return row.urlId;
  }

  planV2HealthClass(band: string): string {
    if (band === 'recently_updated') return 'score-neutral';
    return pageHealthBandClass(band as PageHealthBand);
  }

  planV2QueueRankFor(row: ActionPlanV2Row): number {
    const index = this.planV2ActiveRows().findIndex((active) => active.urlId === row.urlId);
    return index >= 0 ? index + 1 : 0;
  }

  planV2RealizationGapLabel(row: ActionPlanV2Row): string {
    return formatInventoryRealizationNumber(row.metrics.realizationGap);
  }

  planV2RealizationRatioLabel(row: ActionPlanV2Row): string {
    return formatInventoryRealizationRatio(row.metrics.realizationRatio);
  }

  planV2RealizationGapTitle(row: ActionPlanV2Row): string {
    if (row.metrics.gapGate !== 'scored') {
      return `Not scored (${row.metrics.gapGate}). Run demand labeling and ranked keywords for this URL.`;
    }
    return `Gap ${this.planV2RealizationGapLabel(row)} for demand claim: ${row.metrics.demandClaim || '—'}`;
  }

  planV2RealizationRatioTitle(row: ActionPlanV2Row): string {
    if (row.metrics.gapGate !== 'scored') {
      return `Not scored (${row.metrics.gapGate}). Ratio applies only when demand gap is scored.`;
    }
    return `Realization ratio ${this.planV2RealizationRatioLabel(row)} (realized ÷ ceiling)`;
  }

  /**
   * I1: the brief shows the PATH, not the absolute URL. Every row in a
   * 149-row queue was repeating the same 25-character origin, which is
   * already in the header, the domain picker and the page title -- so the
   * only part carrying information was pushed right and truncated.
   *
   * A URL on another origin keeps its host, because that is the case where
   * the origin is the information. The full URL stays in the title
   * attribute either way.
   */
  planV2UrlLabel(row: ActionPlanV2Row): string {
    const raw = (row.url || '').trim();
    if (!raw) return '';
    try {
      const parsed = new URL(raw);
      const active = (this.selectedDomain || '')
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0];
      const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
      if (!active || host !== active) return raw;
      const path = parsed.pathname + parsed.search;
      return path === '/' ? '/' : path.replace(/\/$/, '');
    } catch {
      return raw;
    }
  }

  planV2SerpKeywordDifficultyLabel(row: ActionPlanV2Row): string {
    if (!Number.isFinite(row.serpKeywordDifficulty)) return '—';
    return String(Math.round(Number(row.serpKeywordDifficulty)));
  }

  readonly planV2OpportunityColumnHint =
    'Composite attractiveness (gap + ratio + KD). Higher = work this URL sooner within its action tier. Default queue sort uses this score.';
  readonly planV2PriorityTierHint =
    'Action-type severity tier (e.g. 70 = Prioritize gap). When this domain has two captured crawls, the tier shown is the ' +
    'tier after this period’s movement — a decaying page rises, a winning page falls. Rows sort by opportunity within the same tier.';
  readonly planV2ChangeColumnHint =
    'What this URL did since the previous crawl — the same estimated-traffic and SERP signals the dashboard movers use. ' +
    'Decay pushes a row up the brief; a win pushes it down. Blank means it did not move (or there is no previous crawl to compare).';

  planV2OpportunityTitle(row: ActionPlanV2Row): string {
    const gap = row.metrics.realizationGap;
    const ratio = row.metrics.realizationRatio;
    const kd = row.serpKeywordDifficulty;
    const parts = [
      this.planV2OpportunityColumnHint,
      `Score ${row.opportunityScore}.`,
      gap !== null ? `Gap ${Math.round(gap)}.` : 'Gap unscored.',
      ratio !== null ? `Ratio ${formatInventoryRealizationRatio(ratio)}.` : '',
      kd !== null && Number.isFinite(kd) ? `KD ${Math.round(kd)}.` : 'KD unknown.'
    ].filter(Boolean);
    return parts.join(' ');
  }

  /** LB5: the tier the queue is actually ordered on. */
  planV2DisplayTier(row: ActionPlanV2Row): number {
    return row.periodPriorityScore ?? row.priorityScore;
  }

  planV2HasPeriodPair(): boolean {
    return !!this.planV2Meta?.period;
  }

  /** Short chip copy: `-48 traffic`, `+3 positions`. Empty when the URL did not move. */
  planV2ChangeLabel(row: ActionPlanV2Row): string {
    const change = row.periodChange;
    if (!change) return '';
    // `display` is the diff layer's canonical `72 (-48)` — take its signed part
    // rather than re-deriving a delta the backend already rendered.
    const signed = change.display.match(/\(([^)]+)\)/)?.[1] ?? change.display;
    return change.metricKey === 'serpPosition' ? `${signed} pos` : signed;
  }

  planV2ChangeTitle(row: ActionPlanV2Row): string {
    const change = row.periodChange;
    if (!change) {
      return this.planV2HasPeriodPair()
        ? 'No comparable move on estimated traffic or SERP position this period.'
        : 'No previous crawl to compare against yet.';
    }
    const effect =
      change.direction === 'decay'
        ? `Moved up the brief (+${change.changeScore} priority).`
        : `Moved down the brief (${change.changeScore} priority).`;
    return `${change.evidence}. ${effect}`;
  }

  planV2ChangeClass(row: ActionPlanV2Row): string {
    // Tone classes from the shared badge in src/components.css. "flat" is a
    // change that happened and did not matter, which is --muted, not a tone.
    const direction = row.periodChange?.direction;
    if (direction === 'decay') return 'badge--danger';
    if (direction === 'win') return 'badge--success';
    return 'badge--muted';
  }

  planV2SerpKeywordDifficultyTitle(row: ActionPlanV2Row): string {
    if (!Number.isFinite(row.serpKeywordDifficulty)) {
      return 'No keyword difficulty yet. Run Refresh URL SERP for this domain.';
    }
    return this.serpKeywordDifficultyColumnHint;
  }

  async exportActionPlanPdf(): Promise<void> {
    if (this.actionPlanExporting) return;
    const groups = this.actionPlanTargetGroups;
    const hasClusterSummaries = this.actionPlanClusterSummaries.length > 0;
    const hasInternalLinkRows = groups.length > 0;
    const hasUnclusteredRows = this.actionPlanUnclusteredRows.length > 0;
    if (!hasClusterSummaries && !hasInternalLinkRows && !hasUnclusteredRows) {
      this.notifications.error('No visible action-plan suggestions to export.');
      return;
    }

    this.actionPlanExporting = true;
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;
      const contentWidth = pageWidth - margin * 2;
      let y = margin;
      const exportDate = new Date().toISOString().slice(0, 10);
      const domain = (this.selectedDomain || '').trim();
      const theme = {
        pageBg: [18, 21, 27] as const,
        panelBg: [25, 30, 40] as const,
        panelBorder: [52, 62, 80] as const,
        panelSoft: [30, 36, 48] as const,
        textPrimary: [232, 238, 247] as const,
        textMuted: [154, 167, 186] as const,
        accent: [59, 130, 246] as const,
        link: [96, 165, 250] as const,
        pillBg: [37, 45, 61] as const,
        pillBorder: [78, 92, 115] as const
      };
      const shorten = (value: string, max = 68): string =>
        value.length > max ? `${value.slice(0, Math.max(0, max - 1))}\u2026` : value;

      const paintPageBackground = (): void => {
        doc.setFillColor(...theme.pageBg);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
      };

      const ensureSpace = (needed = 16): void => {
        if (y + needed <= pageHeight - margin) return;
        doc.addPage();
        paintPageBackground();
        y = margin;
      };

      paintPageBackground();

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.setTextColor(...theme.textPrimary);
      doc.text('SEO Action Plan', margin, y);
      y += 18;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...theme.textMuted);
      doc.text(
        `Domain: ${domain || 'N/A'}   |   Exported: ${exportDate}   |   Clusters: ${this.actionPlanClusterSummaries.length}   |   Internal-link targets: ${groups.length}   |   Unclustered pages: ${this.actionPlanUnclusteredRows.length}`,
        margin,
        y
      );
      y += 16;

      if (hasClusterSummaries) {
        ensureSpace(40);
        doc.setTextColor(...theme.textPrimary);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('Cluster Overview', margin, y);
        y += 14;
        for (const cluster of this.actionPlanClusterSummaries) {
          const corePages = this.getActionPlanClusterCorePages(cluster);
          const supportingPages = this.getActionPlanClusterSupportingPages(cluster);
          const description = (this.getActionPlanClusterDescription(cluster) || '').trim();
          const descriptionLines = description
            ? doc.splitTextToSize(description, contentWidth - 20)
            : [];
          const corePills = corePages.map((p) => ({
            label: `${shorten(this.formatUrl(p.url, false), 62)} (${Math.round(p.estimatedOrganicTraffic || 0)})`,
            href: p.url
          }));
          const cardHeight = 44 + (descriptionLines.length * 10) + 4 + (Math.max(1, corePills.length) * 16) + 12;
          ensureSpace(cardHeight + 10);

          doc.setDrawColor(...theme.panelBorder);
          doc.setFillColor(...theme.panelBg);
          doc.roundedRect(margin, y, contentWidth, cardHeight, 6, 6, 'FD');

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.setTextColor(...theme.textPrimary);
          doc.text(cluster.label, margin + 10, y + 15);

          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.setTextColor(...theme.textMuted);
          let metaY = y + 29;
          if (descriptionLines.length > 0) {
            doc.text(descriptionLines, margin + 10, metaY);
            metaY += descriptionLines.length * 10 + 4;
          }
          doc.text('Core pages', margin + 10, metaY);

          let pillY = metaY + 16;
          if (corePills.length === 0) {
            doc.setTextColor(90, 90, 90);
            doc.text('No core pages selected', margin + 10, pillY);
            pillY += 16;
          } else {
            for (const pill of corePills) {
              const pillWidth = Math.min(contentWidth - 24, Math.max(132, doc.getTextWidth(pill.label) + 14));
              const pillHeight = 12;
              doc.setDrawColor(...theme.pillBorder);
              doc.setFillColor(...theme.pillBg);
              doc.roundedRect(margin + 10, pillY - 8, pillWidth, pillHeight, 6, 6, 'FD');
              doc.setTextColor(...theme.link);
              doc.setFontSize(8.3);
              doc.textWithLink(pill.label, margin + 15, pillY, { url: pill.href });
              pillY += 16;
            }
          }

          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8.5);
          doc.setTextColor(...theme.textMuted);
          doc.text(`Supporting pages: ${supportingPages.length}`, margin + 10, y + cardHeight - 8);

          y += cardHeight + 8;
        }
        y += 6;
      }

      const col1 = Math.floor(contentWidth * 0.25);
      const col2 = Math.floor(contentWidth * 0.16);
      const col4 = Math.floor(contentWidth * 0.19);
      const col3 = contentWidth - col1 - col2 - col4;

      const drawTableHeader = (): void => {
        ensureSpace(24);
        doc.setDrawColor(...theme.panelBorder);
        doc.setFillColor(...theme.panelSoft);
        doc.rect(margin, y, contentWidth, 22, 'FD');
        doc.setTextColor(...theme.textMuted);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text('Link from', margin + 6, y + 14);
        doc.text('Anchor', margin + col1 + 6, y + 14);
        doc.text('Example sentence', margin + col1 + col2 + 6, y + 14);
        doc.text('Signals', margin + col1 + col2 + col3 + 6, y + 14);
        y += 22;
      };

      if (this.actionPlanUnclusteredRows.length > 0) {
        if (hasClusterSummaries) y += 6;
        ensureSpace(40);
        doc.setTextColor(...theme.textPrimary);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('Cluster Management (Unclustered Pages)', margin, y);
        y += 14;

        const colA = Math.floor(contentWidth * 0.32);
        const colB = Math.floor(contentWidth * 0.12);
        const colC = Math.floor(contentWidth * 0.26);
        const colD = contentWidth - colA - colB - colC;
        ensureSpace(24);
        doc.setDrawColor(...theme.panelBorder);
        doc.setFillColor(...theme.panelSoft);
        doc.rect(margin, y, contentWidth, 22, 'FD');
        doc.setTextColor(...theme.textMuted);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text('URL', margin + 6, y + 14);
        doc.text('Traffic', margin + colA + 6, y + 14);
        doc.text('Recommendation', margin + colA + colB + 6, y + 14);
        doc.text('Top topics', margin + colA + colB + colC + 6, y + 14);
        y += 22;

        for (let rowIndex = 0; rowIndex < this.actionPlanUnclusteredRows.length; rowIndex++) {
          const row = this.actionPlanUnclusteredRows[rowIndex];
          const sourceText = shorten(this.formatUrl(row.url, false), 58);
          const trafficText = String(Math.round(row.estimatedOrganicTraffic || 0));
          const recText = this.getActionPlanEditedRecommendationText(row);
          const topicsText = row.topTopics.length > 0 ? row.topTopics.join(', ') : '-';

          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          const sourceLines = doc.splitTextToSize(sourceText, colA - 12);
          const trafficLines = doc.splitTextToSize(trafficText, colB - 12);
          const recLines = doc.splitTextToSize(recText, colC - 12);
          const topicLines = doc.splitTextToSize(topicsText, colD - 12);
          const lineCount = Math.max(sourceLines.length, trafficLines.length, recLines.length, topicLines.length, 1);
          const rowHeight = Math.max(24, lineCount * 11 + 8);

          ensureSpace(rowHeight + 2);
          doc.setDrawColor(...theme.panelBorder);
          if (rowIndex % 2 === 0) {
            doc.setFillColor(...theme.panelBg);
            doc.rect(margin, y, contentWidth, rowHeight, 'FD');
          } else {
            doc.rect(margin, y, contentWidth, rowHeight);
          }
          doc.line(margin + colA, y, margin + colA, y + rowHeight);
          doc.line(margin + colA + colB, y, margin + colA + colB, y + rowHeight);
          doc.line(margin + colA + colB + colC, y, margin + colA + colB + colC, y + rowHeight);

          const textY = y + 14;
          doc.setTextColor(...theme.link);
          for (let i = 0; i < sourceLines.length; i++) {
            doc.textWithLink(String(sourceLines[i]), margin + 6, textY + i * 10, { url: row.url });
          }
          doc.setTextColor(...theme.textPrimary);
          doc.text(trafficLines, margin + colA + 6, textY);
          doc.text(recLines, margin + colA + colB + 6, textY);
          doc.text(topicLines, margin + colA + colB + colC + 6, textY);
          y += rowHeight;
        }
        y += 12;
      }

      if (hasInternalLinkRows) {
        if (this.actionPlanUnclusteredRows.length > 0) y += 6;
        let groupIndex = 0;
        for (const group of groups) {
          ensureSpace(46);
          doc.setFillColor(...theme.panelSoft);
          doc.setDrawColor(...theme.panelBorder);
          doc.rect(margin, y, contentWidth, 24, 'FD');
          doc.setTextColor(...theme.textMuted);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.text('Target:', margin + 8, y + 16);
          doc.setTextColor(...theme.link);
          doc.textWithLink(shorten(this.formatUrl(group.targetUrl, false), 84), margin + 44, y + 16, { url: group.targetUrl });
          y += 24;

          drawTableHeader();

          for (let rowIndex = 0; rowIndex < group.rows.length; rowIndex++) {
            const row = group.rows[rowIndex];
            const sourceText = shorten(this.formatUrl(row.sourceUrl, false), 62);
            const editedAnchor = this.getActionPlanEditedAnchor(row).trim();
            const editedSentence = this.getActionPlanEditedSentence(row).trim();
            const anchorText = editedAnchor || '-';
            const sentenceText = editedSentence || '-';
            const incomingContextualLinks = Math.round(row.signals?.contextualIncomingTarget || 0);
            const sharedClusterNames = Array.isArray(row.signals?.sharedCluster) ? row.signals.sharedCluster : [];
            const sharedClusterText = sharedClusterNames.length > 0
              ? `Shared cluster: ${sharedClusterNames.join(', ')}`
              : 'No shared cluster';
            const signalsText = `${incomingContextualLinks} incoming contextual links in target | ${sharedClusterText}`;

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            const sourceLines = doc.splitTextToSize(sourceText, col1 - 12);
            const anchorLines = doc.splitTextToSize(anchorText, col2 - 12);
            const sentenceLines = doc.splitTextToSize(sentenceText, col3 - 12);
            const signalsLines = doc.splitTextToSize(signalsText, col4 - 12);
            const lineCount = Math.max(sourceLines.length, anchorLines.length, sentenceLines.length, signalsLines.length, 1);
            const rowHeight = Math.max(24, lineCount * 11 + 8);

            ensureSpace(rowHeight + 2);
            doc.setDrawColor(...theme.panelBorder);
            if ((groupIndex + rowIndex) % 2 === 0) {
              doc.setFillColor(...theme.panelBg);
              doc.rect(margin, y, contentWidth, rowHeight, 'FD');
            } else {
              doc.rect(margin, y, contentWidth, rowHeight);
            }
            doc.line(margin + col1, y, margin + col1, y + rowHeight);
            doc.line(margin + col1 + col2, y, margin + col1 + col2, y + rowHeight);
            doc.line(margin + col1 + col2 + col3, y, margin + col1 + col2 + col3, y + rowHeight);

            const textY = y + 14;
            doc.setTextColor(...theme.link);
            for (let i = 0; i < sourceLines.length; i++) {
              doc.textWithLink(String(sourceLines[i]), margin + 6, textY + i * 10, { url: row.sourceUrl });
            }
            doc.setTextColor(...theme.textPrimary);
            doc.text(anchorLines, margin + col1 + 6, textY);
            doc.text(sentenceLines, margin + col1 + col2 + 6, textY);
            doc.text(signalsLines, margin + col1 + col2 + col3 + 6, textY);
            y += rowHeight;
          }
          y += 12;
          groupIndex += 1;
        }
      }

      if (hasClusterSummaries) {
        doc.addPage();
        paintPageBackground();
        y = margin;
        doc.setTextColor(...theme.textPrimary);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('Annex — Supporting Pages by Cluster', margin, y);
        y += 14;
        for (const cluster of this.actionPlanClusterSummaries) {
          const supportingPages = this.getActionPlanClusterSupportingPages(cluster);
          ensureSpace(24);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.text(`${cluster.label} (${supportingPages.length} supporting page${supportingPages.length === 1 ? '' : 's'})`, margin, y);
          y += 12;
          if (supportingPages.length === 0) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(...theme.textMuted);
            doc.text('- none', margin + 8, y);
            y += 12;
            continue;
          }
          for (const page of supportingPages) {
            const line = `- ${shorten(this.formatUrl(page.url, false), 88)}`;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(...theme.textPrimary);
            const lines = doc.splitTextToSize(line, contentWidth - 8);
            for (const wrapped of lines) {
              ensureSpace(12);
              doc.textWithLink(String(wrapped), margin + 8, y, { url: page.url });
              y += 11;
            }
          }
          y += 6;
        }
      }

      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...theme.textMuted);
        doc.text(`Page ${i} / ${totalPages}`, pageWidth - margin - 44, pageHeight - 16);
      }

      const safeDomain = (domain || 'domain').replace(/[^a-z0-9.-]+/gi, '-').replace(/^-+|-+$/g, '');
      doc.save(`action-plan-${safeDomain || 'domain'}-${exportDate}.pdf`);
      this.notifications.success('Action plan exported as PDF.');
    } catch {
      this.notifications.error('Could not export action plan PDF.');
    } finally {
      this.actionPlanExporting = false;
    }
  }

  refreshActionPlanSuggestions(): void {
    this.actionPlanDeletedTargets = new Set<string>();
    this.actionPlanWhyExpandedRows = new Set<string>();
    this.actionPlanEdits = {};
    this.actionPlanUnclusteredEdits = {};
    this.actionPlanClusterDescriptions = {};
    this.loadActionPlan();
  }

  loadActionPlan(): void {
    const domain = (this.selectedDomain || '').trim();
    if (!domain) {
      this.actionPlanRows = [];
      this.actionPlanUnclusteredRows = [];
      this.actionPlanClusterSummaries = [];
      this.actionPlanError = null;
      this.actionPlanLoading = false;
      this.actionPlanDeletedTargets = new Set<string>();
      this.actionPlanWhyExpandedRows = new Set<string>();
      this.actionPlanEdits = {};
      this.actionPlanUnclusteredEdits = {};
      this.actionPlanClusterDescriptions = {};
      this.actionPlanDeletedTargetsDomain = '';
      return;
    }
    if (domain !== this.actionPlanDeletedTargetsDomain) {
      this.actionPlanDeletedTargets = new Set<string>();
      this.actionPlanWhyExpandedRows = new Set<string>();
      this.actionPlanEdits = {};
      this.actionPlanUnclusteredEdits = {};
      this.actionPlanClusterDescriptions = {};
      this.actionPlanDeletedTargetsDomain = domain;
    }
    this.actionPlanLoading = true;
    this.actionPlanError = null;
    this.subscriptions.add(
      this.inventoryPlanClusters
        .getInternalLinkActionPlan(domain, {
          limit: 5000,
          includeCompleted: this.actionPlanIncludeDone,
          includeDismissed: this.actionPlanIncludeDismissed
        })
        .pipe(
          switchMap((res) => {
            this.actionPlanRows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
            return forkJoin({
              unclustered: this.inventoryPlanClusters.getUnclusteredPageActionPlan(domain, {
                limit: 200,
                includeCompleted: this.actionPlanIncludeDone,
                includeDismissed: this.actionPlanIncludeDismissed
              }),
              clusters: this.inventoryPlanClusters.getClusters(domain)
            });
          })
        )
        .subscribe({
          next: (res) => {
            this.actionPlanUnclusteredRows = Array.isArray(res?.unclustered?.data?.rows) ? res.unclustered.data.rows : [];
            this.actionPlanClusterSummaries = this.buildActionPlanClusterSummaries(
              Array.isArray(res?.clusters?.data?.topics) ? res.clusters.data.topics : []
            );
            this.syncActionPlanEdits();
            this.syncActionPlanUnclusteredEdits();
            this.syncActionPlanClusterDescriptions();
            this.collapseAllActionPlanGroups();
            this.actionPlanLoading = false;
            this.rebuildPageSignalsIndex();
          },
          error: (err) => {
            this.actionPlanRows = [];
            this.actionPlanUnclusteredRows = [];
            this.actionPlanClusterSummaries = [];
            this.actionPlanLoading = false;
            this.actionPlanError = err?.error?.message || 'Could not load action plan suggestions.';
            this.rebuildPageSignalsIndex();
          }
        })
    );
  }

  private buildActionPlanClusterSummaries(clusters: PlanCluster[]): ActionPlanClusterSummary[] {
    const toTraffic = (value: unknown): number => {
      const num = Number(value);
      return Number.isFinite(num) ? num : 0;
    };
    const normalizeRows = (rows: PlanCluster['urls']): Array<{ url: string; estimatedOrganicTraffic: number; topicClass: 'core' | 'supporting' | 'mention' | null }> =>
      (Array.isArray(rows) ? rows : [])
        .map((row) => ({
          url: String(row?.url || '').trim(),
          estimatedOrganicTraffic: toTraffic((row as any)?.estimatedOrganicTraffic),
          topicClass:
            row?.topicClass === 'core' || row?.topicClass === 'supporting' || row?.topicClass === 'mention'
              ? row.topicClass
              : null
        }))
        .filter((row) => row.url.length > 0)
        .sort((a, b) => b.estimatedOrganicTraffic - a.estimatedOrganicTraffic);

    const summaries = (Array.isArray(clusters) ? clusters : []).map((cluster) => {
      const rows = normalizeRows(cluster.urls);
      const core = rows.filter((r) => r.topicClass === 'core').map(({ url, estimatedOrganicTraffic }) => ({ url, estimatedOrganicTraffic }));
      const supporting = rows
        .filter((r) => r.topicClass !== 'core')
        .map(({ url, estimatedOrganicTraffic }) => ({ url, estimatedOrganicTraffic }));
      return {
        id: cluster.id,
        label: cluster.label,
        corePages: core,
        supportingPages: supporting
      };
    });

    return summaries.sort((a, b) => a.label.localeCompare(b.label));
  }

  private actionPlanTargetGroupKey(group: ActionPlanTargetGroup): string {
    return group.targetUrlId || group.targetUrl;
  }

  private syncActionPlanCollapsedTargets(): void {
    const valid = new Set(this.actionPlanTargetGroups.map((group) => this.actionPlanTargetGroupKey(group)));
    const next = new Set<string>();
    this.actionPlanCollapsedTargets.forEach((key) => {
      if (valid.has(key)) next.add(key);
    });
    this.actionPlanCollapsedTargets = next;
  }

  private collapseAllActionPlanGroups(): void {
    const collapsed = new Set<string>();
    for (const group of this.actionPlanTargetGroups) {
      collapsed.add(this.actionPlanTargetGroupKey(group));
    }
    this.actionPlanCollapsedTargets = collapsed;
  }

  deleteActionPlanTarget(group: ActionPlanTargetGroup): void {
    const key = this.actionPlanTargetGroupKey(group);
    const deletedTargets = new Set(this.actionPlanDeletedTargets);
    deletedTargets.add(key);
    this.actionPlanDeletedTargets = deletedTargets;
    this.actionPlanRows = this.actionPlanRows.filter((row) => (row.targetUrlId || row.targetUrl) !== key);
    const nextWhy = new Set(this.actionPlanWhyExpandedRows);
    for (const row of group.rows) {
      nextWhy.delete(row.suggestionKey);
    }
    this.actionPlanWhyExpandedRows = nextWhy;
    const nextEdits = { ...this.actionPlanEdits };
    for (const row of group.rows) {
      delete nextEdits[row.suggestionKey];
    }
    this.actionPlanEdits = nextEdits;
    this.syncActionPlanCollapsedTargets();
  }

  getActionPlanEditedAnchor(row: ActionPlanInternalLinkRow): string {
    const edited = this.actionPlanEdits[row.suggestionKey]?.anchor;
    return edited ?? row.suggestedAnchor ?? '';
  }

  getActionPlanEditedSentence(row: ActionPlanInternalLinkRow): string {
    const edited = this.actionPlanEdits[row.suggestionKey]?.sentence;
    return edited ?? row.where?.sentence ?? '';
  }

  onActionPlanAnchorChange(row: ActionPlanInternalLinkRow, value: string): void {
    const currentSentence = this.getActionPlanEditedSentence(row);
    this.actionPlanEdits = {
      ...this.actionPlanEdits,
      [row.suggestionKey]: {
        anchor: value,
        sentence: currentSentence
      }
    };
  }

  onActionPlanSentenceChange(row: ActionPlanInternalLinkRow, value: string): void {
    const currentAnchor = this.getActionPlanEditedAnchor(row);
    this.actionPlanEdits = {
      ...this.actionPlanEdits,
      [row.suggestionKey]: {
        anchor: currentAnchor,
        sentence: value
      }
    };
  }

  private syncActionPlanEdits(): void {
    const valid = new Set(this.actionPlanRows.map((row) => row.suggestionKey));
    const next: Record<string, { anchor: string; sentence: string }> = {};
    for (const [key, value] of Object.entries(this.actionPlanEdits)) {
      if (valid.has(key)) next[key] = value;
    }
    this.actionPlanEdits = next;
  }

  isActionPlanWhyExpanded(row: ActionPlanInternalLinkRow): boolean {
    return this.actionPlanWhyExpandedRows.has(row.suggestionKey);
  }

  toggleActionPlanWhyExpanded(row: ActionPlanInternalLinkRow): void {
    const next = new Set(this.actionPlanWhyExpandedRows);
    if (next.has(row.suggestionKey)) {
      next.delete(row.suggestionKey);
    } else {
      next.add(row.suggestionKey);
    }
    this.actionPlanWhyExpandedRows = next;
  }

  isActionPlanGroupCollapsed(group: ActionPlanTargetGroup): boolean {
    return this.actionPlanCollapsedTargets.has(this.actionPlanTargetGroupKey(group));
  }

  toggleActionPlanGroupCollapsed(group: ActionPlanTargetGroup): void {
    const key = this.actionPlanTargetGroupKey(group);
    const next = new Set(this.actionPlanCollapsedTargets);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.actionPlanCollapsedTargets = next;
  }

  trackActionPlanTargetGroup = (_index: number, group: ActionPlanTargetGroup): string =>
    this.actionPlanTargetGroupKey(group);

  trackActionPlanRow = (_index: number, row: { suggestionKey: string }): string =>
    row.suggestionKey;

  updateActionPlanSuggestionStatus(row: ActionPlanInternalLinkRow, status: ActionPlanSuggestionStatus): void {
    if (!row?.suggestionKey || this.actionPlanUpdatingKey) return;
    const domain = (this.selectedDomain || '').trim();
    if (!domain) return;
    this.actionPlanUpdatingKey = row.suggestionKey;
    this.subscriptions.add(
      this.inventoryPlanClusters
        .updateInternalLinkActionPlanStatus(domain, row.suggestionKey, status)
        .subscribe({
          next: () => {
            this.actionPlanUpdatingKey = null;
            this.loadActionPlan();
          },
          error: (err) => {
            this.actionPlanUpdatingKey = null;
            this.actionPlanError = err?.error?.message || 'Could not update action plan status.';
          }
        })
    );
  }

  updateUnclusteredPageSuggestionStatus(row: ActionPlanUnclusteredPageRow, status: ActionPlanSuggestionStatus): void {
    if (!row?.suggestionKey || this.actionPlanUpdatingKey) return;
    const domain = (this.selectedDomain || '').trim();
    if (!domain) return;
    this.actionPlanUpdatingKey = row.suggestionKey;
    this.subscriptions.add(
      this.inventoryPlanClusters
        .updateUnclusteredPageActionPlanStatus(domain, row.suggestionKey, status)
        .subscribe({
          next: () => {
            this.actionPlanUpdatingKey = null;
            this.loadActionPlan();
          },
          error: (err) => {
            this.actionPlanUpdatingKey = null;
            this.actionPlanError = err?.error?.message || 'Could not update cluster-management action status.';
          }
        })
    );
  }

  actionPlanRecommendationLabel(rec: UnclusteredPageRecommendation): string {
    return unclusteredRecommendationLabel(rec);
  }

  getActionPlanEditedRecommendationText(row: ActionPlanUnclusteredPageRow): string {
    return this.actionPlanUnclusteredEdits[row.suggestionKey]?.recommendationText
      || this.actionPlanRecommendationLabel(row.recommendation);
  }

  onActionPlanRecommendationChange(row: ActionPlanUnclusteredPageRow, value: string): void {
    const recommendationText = String(value || '').trim();
    this.actionPlanUnclusteredEdits = {
      ...this.actionPlanUnclusteredEdits,
      [row.suggestionKey]: {
        recommendationText: recommendationText || this.actionPlanRecommendationLabel(row.recommendation)
      }
    };
  }

  private syncActionPlanUnclusteredEdits(): void {
    const valid = new Set(this.actionPlanUnclusteredRows.map((row) => row.suggestionKey));
    const next: Record<string, { recommendationText: string }> = {};
    for (const [key, value] of Object.entries(this.actionPlanUnclusteredEdits)) {
      if (valid.has(key)) next[key] = value;
    }
    this.actionPlanUnclusteredEdits = next;
  }

  getActionPlanClusterCorePages(cluster: ActionPlanClusterSummary): Array<{ url: string; estimatedOrganicTraffic: number }> {
    return cluster.corePages;
  }

  getActionPlanClusterSupportingPages(cluster: ActionPlanClusterSummary): Array<{ url: string; estimatedOrganicTraffic: number }> {
    const coreSet = new Set(this.getActionPlanClusterCorePages(cluster).map((page) => page.url));
    return [...cluster.corePages, ...cluster.supportingPages]
      .filter((page) => !coreSet.has(page.url))
      .sort((a, b) => b.estimatedOrganicTraffic - a.estimatedOrganicTraffic);
  }

  getActionPlanClusterDescription(cluster: ActionPlanClusterSummary): string {
    return this.actionPlanClusterDescriptions[cluster.id] || '';
  }

  onActionPlanClusterDescriptionChange(cluster: ActionPlanClusterSummary, value: string): void {
    this.actionPlanClusterDescriptions = {
      ...this.actionPlanClusterDescriptions,
      [cluster.id]: String(value || '')
    };
  }

  private syncActionPlanClusterDescriptions(): void {
    const validClusterIds = new Set(this.actionPlanClusterSummaries.map((cluster) => cluster.id));
    const next: Record<string, string> = {};
    for (const [clusterId, value] of Object.entries(this.actionPlanClusterDescriptions)) {
      if (validClusterIds.has(clusterId)) next[clusterId] = value;
    }
    this.actionPlanClusterDescriptions = next;
  }

  actionPlanStatusLabel(status: ActionPlanSuggestionStatus): string {
    if (status === 'done') return 'Done';
    if (status === 'dismissed') return 'Dismissed';
    return 'Open';
  }

  actionPlanStatusClass(status: ActionPlanSuggestionStatus): string {
    if (status === 'done') return 'action-plan-status--done';
    if (status === 'dismissed') return 'action-plan-status--dismissed';
    return 'action-plan-status--open';
  }

  get actionPlanTargetGroups(): ActionPlanTargetGroup[] {
    const byTarget = new Map<string, ActionPlanTargetGroup>();
    for (const row of this.actionPlanRows) {
      const key = row.targetUrlId || row.targetUrl;
      if (this.actionPlanDeletedTargets.has(key)) continue;
      if (!byTarget.has(key)) {
        byTarget.set(key, {
          targetUrlId: row.targetUrlId,
          targetUrl: row.targetUrl,
          targetTraffic: Number.isFinite(row.signals?.targetTraffic) ? row.signals.targetTraffic : 0,
          maxScore: row.score,
          rows: []
        });
      }
      const group = byTarget.get(key)!;
      group.rows.push(row);
      if (row.score > group.maxScore) group.maxScore = row.score;
      if ((row.signals?.targetTraffic || 0) > group.targetTraffic) {
        group.targetTraffic = row.signals.targetTraffic;
      }
    }

    const groups = Array.from(byTarget.values());
    for (const group of groups) {
      group.rows.sort((a, b) => b.score - a.score);
    }
    groups.sort((a, b) => b.maxScore - a.maxScore);
    return groups;
  }

  private refreshPlanClusters(): void {
    const active = this.selectedDomain.trim();
    if (!active) {
      this.planClusters = [];
      this.planUrlSearchState = {};
      return;
    }
    this.planLoading = true;
    this.planError = null;
    this.subscriptions.add(
      this.inventoryPlanClusters.getClusters(active).subscribe({
        next: (res) => {
          this.planClusters = Array.isArray(res?.data?.topics) ? res.data.topics : [];
          this.planUrlSearchState = {};
          this.planMoveTargetClusterIdBySource = {};
          this.planMoveSelectedByCluster = {};
          this.planBulkClassByCluster = {};
          this.planLoading = false;
        },
        error: (err) => {
          this.planLoading = false;
          this.planError = err?.error?.message || 'Could not load plan topics.';
        }
      })
    );
  }

  bootstrapTopPlanTopics(): void {
    const active = this.selectedDomain.trim();
    if (!active) return;
    this.planBootstrapping = true;
    this.planError = null;
    this.subscriptions.add(
      this.inventoryPlanClusters.bootstrapDefaults(active).subscribe({
        next: (res) => {
          this.planClusters = Array.isArray(res?.data?.topics) ? res.data.topics : [];
          this.planUrlSearchState = {};
          this.planBootstrapping = false;
        },
        error: (err) => {
          this.planBootstrapping = false;
          this.planError = err?.error?.message || 'Could not find top topics.';
        }
      })
    );
  }

  get visiblePlanTopicSuggestions(): TopicPickerOption[] {
    const query = this.planTopicSearchQuery.trim().toLowerCase();
    const filtered = query
      ? this.planAvailableTopics.filter((topic) => {
          const aliasHaystack = (topic.aliasLabels || []).join(' ').toLowerCase();
          return (
            topic.label.toLowerCase().includes(query) ||
            topic.slug.includes(query) ||
            topic.parentLabel.toLowerCase().includes(query) ||
            aliasHaystack.includes(query)
          );
        })
      : this.planAvailableTopics;
    return filtered.slice(0, this.maxTopicSuggestions);
  }

  onPlanTopicInputFocus(): void {
    this.showPlanTopicSuggestions = true;
  }

  onPlanTopicInputBlur(): void {
    setTimeout(() => {
      this.showPlanTopicSuggestions = false;
    }, 100);
  }

  onPlanTopicInputChange(): void {
    this.showPlanTopicSuggestions = true;
  }

  applyPlanTopicFromInput(): void {
    const query = this.planTopicSearchQuery.trim().toLowerCase();
    if (!query) return;
    const exactMatch = this.planAvailableTopics.find(
      (topic) => topic.slug === query || topic.label.toLowerCase() === query
    );
    if (exactMatch) {
      this.addPlanClusterFromTopic(exactMatch);
      return;
    }
    const [first] = this.visiblePlanTopicSuggestions;
    if (first) {
      this.addPlanClusterFromTopic(first);
    }
  }

  addPlanClusterFromTopic(topic: TopicPickerOption): void {
    const slug = topic.slug.trim().toLowerCase();
    this.planLoading = true;
    this.planAddTopicError = null;
    this.subscriptions.add(
      this.inventoryPlanClusters.addCluster(this.selectedDomain, slug, topic.label).subscribe({
        next: (res) => {
          this.planClusters = Array.isArray(res?.data?.topics) ? res.data.topics : [];
          this.planTopicSearchQuery = '';
          this.showPlanTopicSuggestions = false;
          this.planLoading = false;
          this.planUrlSearchState = {};
        },
        error: (err) => {
          this.planLoading = false;
          this.planAddTopicError = err?.error?.message || 'Could not add topic to plan.';
        }
      })
    );
  }

  removePlanCluster(clusterId: string): void {
    this.planLoading = true;
    this.clearPlanSuccess();
    this.subscriptions.add(
      this.inventoryPlanClusters.removeCluster(this.selectedDomain, clusterId).subscribe({
        next: (res) => {
          this.planClusters = Array.isArray(res?.data?.topics) ? res.data.topics : [];
          if (this.bulkTargetClusterId === clusterId) {
            this.bulkTargetClusterId = null;
          }
          delete this.planUrlSearchState[clusterId];
          delete this.planMoveTargetClusterIdBySource[clusterId];
          delete this.planMoveSelectedByCluster[clusterId];
          delete this.planBulkClassByCluster[clusterId];
          this.planLoading = false;
        },
        error: (err) => {
          this.planLoading = false;
          this.planError = err?.error?.message || 'Could not remove plan topic.';
        }
      })
    );
  }

  beginRenamePlanCluster(cluster: PlanCluster): void {
    this.editingPlanTopicId = cluster.id;
    this.planTopicLabelDraft[cluster.id] = (cluster.label || '').trim();
    this.planError = null;
    this.clearPlanSuccess();
  }

  cancelRenamePlanCluster(clusterId: string): void {
    if (this.editingPlanTopicId === clusterId) {
      this.editingPlanTopicId = null;
    }
    delete this.planTopicLabelDraft[clusterId];
  }

  savePlanClusterLabel(cluster: PlanCluster): void {
    const next = (this.planTopicLabelDraft[cluster.id] || '').trim();
    if (!next) {
      this.planError = 'Topic label cannot be empty.';
      return;
    }
    if (next === (cluster.label || '').trim()) {
      this.cancelRenamePlanCluster(cluster.id);
      return;
    }
    this.savingPlanTopicLabelId = cluster.id;
    this.planError = null;
    this.clearPlanSuccess();
    this.subscriptions.add(
      this.inventoryPlanClusters.updateClusterLabel(this.selectedDomain, cluster.id, next).subscribe({
        next: (res) => {
          this.planClusters = Array.isArray(res?.data?.topics) ? res.data.topics : this.planClusters;
          this.savingPlanTopicLabelId = null;
          this.editingPlanTopicId = null;
          delete this.planTopicLabelDraft[cluster.id];
          this.setPlanSuccess('Topic renamed.');
        },
        error: (err) => {
          this.savingPlanTopicLabelId = null;
          this.planError = err?.error?.message || 'Could not rename topic.';
        }
      })
    );
  }

  hasPlanUrlRows(topicId: string): boolean {
    return (this.planUrlSearchState[topicId]?.rows?.length || 0) > 0;
  }

  planUrlRows(topicId: string): PlanTopicCandidateRow[] {
    return this.planUrlSearchState[topicId]?.rows || [];
  }

  private planClassSortRank(topicClass: 'core' | 'supporting' | 'mention' | null | undefined): number {
    if (topicClass === 'core') return 0;
    if (topicClass === 'mention') return 1;
    if (topicClass === 'supporting') return 2;
    return 3;
  }

  sortedPlanClusterUrls(cluster: PlanCluster): PlanCluster['urls'] {
    const rows = [...(Array.isArray(cluster?.urls) ? cluster.urls : [])];
    rows.sort((a, b) => {
      const baseRankA = this.planClassSortRank(a?.topicClass);
      const baseRankB = this.planClassSortRank(b?.topicClass);
      const rankA = this.planClassSortDirection === 'reverse' ? 3 - baseRankA : baseRankA;
      const rankB = this.planClassSortDirection === 'reverse' ? 3 - baseRankB : baseRankB;
      if (rankA !== rankB) return rankA - rankB;

      const relevanceDiff = Number(b?.topicRelevanceScore || 0) - Number(a?.topicRelevanceScore || 0);
      if (relevanceDiff !== 0) return relevanceDiff;
      return String(a?.url || '').localeCompare(String(b?.url || ''));
    });
    return rows;
  }

  togglePlanClassSortDirection(): void {
    this.planClassSortDirection = this.planClassSortDirection === 'default' ? 'reverse' : 'default';
  }

  onPlanFindOtherTopicInputChange(topicId: string, value: string): void {
    this.planFindOtherTopicInput[topicId] = (value || '').toString();
    this.planFindOtherTopicSuggestionsOpen[topicId] = true;
  }

  onPlanFindOtherTopicInputFocus(topicId: string): void {
    this.planFindOtherTopicSuggestionsOpen[topicId] = true;
  }

  onPlanFindOtherTopicInputBlur(topicId: string): void {
    setTimeout(() => {
      this.planFindOtherTopicSuggestionsOpen[topicId] = false;
    }, 100);
  }

  visiblePlanOtherTopicSuggestions(topicId: string): TopicPickerOption[] {
    const query = (this.planFindOtherTopicInput[topicId] || '').trim().toLowerCase();
    const filtered = query
      ? this.planAvailableTopics.filter((topic) => {
          const aliasHaystack = (topic.aliasLabels || []).join(' ').toLowerCase();
          return (
            topic.label.toLowerCase().includes(query) ||
            topic.slug.includes(query) ||
            topic.parentLabel.toLowerCase().includes(query) ||
            aliasHaystack.includes(query)
          );
        })
      : this.planAvailableTopics;
    return filtered.slice(0, this.maxTopicSuggestions);
  }

  selectPlanOtherTopicSuggestion(clusterId: string, topic: TopicPickerOption): void {
    this.planFindOtherTopicInput[clusterId] = topic.label;
    this.planFindOtherTopicSuggestionsOpen[clusterId] = false;
  }

  private resolvePlanOtherTopicSlugFromInput(clusterId: string): string {
    const query = (this.planFindOtherTopicInput[clusterId] || '').trim().toLowerCase();
    if (!query) return '';
    const exactMatch = this.planAvailableTopics.find(
      (topic) => topic.slug === query || topic.label.toLowerCase() === query
    );
    if (exactMatch) return exactMatch.slug;
    const [first] = this.visiblePlanOtherTopicSuggestions(clusterId);
    return first?.slug || query;
  }

  findUrlsForTopic(cluster: PlanCluster, topicSlugOverride = ''): void {
    const topicId = cluster.id;
    this.findingUrlsTopicId = topicId;
    this.planUrlSearchState[topicId] = { loading: true, rows: [], error: null };
    this.subscriptions.add(
      this.inventoryPlanClusters
        .findUrlsForTopic(this.selectedDomain, topicId, {
          search: this.searchQuery,
          pathPrefixes: this.selectedPathPrefixes,
          limit: 200,
          topicSlugOverride,
          inclusionFilter: this.trackingFilter
        })
        .subscribe({
          next: (res) => {
            const rows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
            this.planUrlSearchState[topicId] = { loading: false, rows, error: null };
            this.planClusters = Array.isArray(res?.data?.topics) ? res.data.topics : this.planClusters;
            this.findingUrlsTopicId = null;
          },
          error: (err) => {
            this.planUrlSearchState[topicId] = {
              loading: false,
              rows: [],
              error: err?.error?.message || 'Could not find URLs for this topic.'
            };
            this.findingUrlsTopicId = null;
          }
        })
    );
  }

  findUrlsForAnotherTopic(cluster: PlanCluster): void {
    const topicSlug = this.resolvePlanOtherTopicSlugFromInput(cluster.id);
    if (!topicSlug) return;
    this.findUrlsForTopic(cluster, topicSlug);
  }

  addCandidateUrlToCluster(cluster: PlanCluster, row: PlanTopicCandidateRow): void {
    this.subscriptions.add(
      this.inventoryPlanClusters
        .addUrlsToCluster(this.selectedDomain, cluster.id, [{ urlId: row.urlId, url: row.url }], 'manual')
        .subscribe({
          next: (res) => {
            const nextClusters = Array.isArray(res?.data?.topics) ? res.data.topics : this.planClusters;
            this.planClusters = nextClusters;
            const clusterLabel = nextClusters.find((c) => c.id === cluster.id)?.label || cluster.label;
            this.addClusterLabelsToInventoryRows([row.url], clusterLabel);
            this.notifications.success(`1 page added to "${clusterLabel}" cluster.`);
          },
          error: (err) => {
            this.planError = err?.error?.message || 'Could not add URL to cluster.';
            this.notifications.error('Failed to add page to cluster.');
          }
        })
    );
  }

  clusterHasUrl(cluster: PlanCluster, url: string): boolean {
    const target = (url || '').trim();
    return cluster.urls.some((u) => (u.url || '').trim() === target);
  }

  removeUrlFromCluster(cluster: PlanCluster, urlId: string): void {
    this.subscriptions.add(
      this.inventoryPlanClusters.removeUrlFromCluster(this.selectedDomain, cluster.id, urlId).subscribe({
        next: (res) => {
          this.planClusters = Array.isArray(res?.data?.topics) ? res.data.topics : this.planClusters;
            this.notifications.success('Page removed from cluster.');
        },
        error: (err) => {
          this.planError = err?.error?.message || 'Could not remove URL from cluster.';
            this.notifications.error('Failed to remove page from cluster.');
        }
      })
    );
  }

  excludePlanUrl(row: { urlId: string }): void {
    const urlId = (row.urlId || '').trim();
    if (!urlId || this.isUpdatingInclusion) return;
    this.isUpdatingInclusion = true;
    this.subscriptions.add(
      this.inventoryPlanClusters.updateUrlInclusionState(urlId, 'force_exclude', 'manual').subscribe({
        next: () => {
          this.isUpdatingInclusion = false;
          this.applyInclusionStateToInventoryRows([urlId], 'force_exclude');
          this.notifications.success('Page excluded from tracking.');
        },
        error: (err) => {
          this.planError = err?.error?.message || 'Could not exclude page from tracking.';
          this.notifications.error('Failed to exclude page.');
          this.isUpdatingInclusion = false;
        }
      })
    );
  }

  excludeSelectedPlanUrls(cluster: PlanCluster): void {
    const selected = this.planMoveSelectedByCluster[cluster.id] || [];
    const urlIds = selected.map((row) => (row.urlId || '').trim()).filter(Boolean);
    if (urlIds.length === 0 || this.isUpdatingInclusion) return;
    this.isUpdatingInclusion = true;
    this.subscriptions.add(
      this.inventoryPlanClusters.updateUrlsInclusionState(urlIds, 'force_exclude', 'manual').subscribe({
        next: () => {
          this.isUpdatingInclusion = false;
          this.applyInclusionStateToInventoryRows(urlIds, 'force_exclude');
          this.clearPlanMoveSelection(cluster.id);
          this.notifications.success(
            `${urlIds.length} ${urlIds.length === 1 ? 'page' : 'pages'} excluded from tracking.`
          );
        },
        error: (err) => {
          this.planError = err?.error?.message || 'Could not exclude selected pages.';
          this.notifications.error('Failed to exclude selected pages.');
          this.isUpdatingInclusion = false;
        }
      })
    );
  }

  planMoveTargetsForCluster(clusterId: string): PlanCluster[] {
    return this.planClusters.filter((c) => c.id !== clusterId);
  }

  isPlanMoveSelected(clusterId: string, urlId: string): boolean {
    const rows = this.planMoveSelectedByCluster[clusterId] || [];
    return rows.some((r) => r.urlId === urlId);
  }

  togglePlanMoveSelect(clusterId: string, row: { urlId: string; url: string }): void {
    const current = this.planMoveSelectedByCluster[clusterId] || [];
    const has = current.some((r) => r.urlId === row.urlId);
    const next = has ? current.filter((r) => r.urlId !== row.urlId) : [...current, { urlId: row.urlId, url: row.url }];
    this.planMoveSelectedByCluster = { ...this.planMoveSelectedByCluster, [clusterId]: next };
  }

  onPlanMoveCheckboxClick(
    clusterId: string,
    rows: Array<{ urlId: string; url: string }>,
    index: number,
    row: { urlId: string; url: string },
    event: MouseEvent
  ): void {
    const input = event.target as HTMLInputElement | null;
    const checked = !!input?.checked;
    const clickedId = (row.urlId || '').trim();
    if (!clickedId) return;

    const current = this.planMoveSelectedByCluster[clusterId] || [];
    const nextMap = new Map(current.map((r) => [r.urlId, r]));
    const anchorId = this.planMoveLastAnchorByCluster[clusterId] || null;
    const anchorIndex = anchorId ? rows.findIndex((r) => (r.urlId || '').trim() === anchorId) : -1;
    const canRangeSelect = !!event.shiftKey && anchorIndex >= 0 && rows.length > 0;

    if (canRangeSelect) {
      const start = Math.min(anchorIndex, index);
      const end = Math.max(anchorIndex, index);
      for (let i = start; i <= end; i++) {
        const r = rows[i];
        const id = (r?.urlId || '').trim();
        if (!id) continue;
        if (checked) {
          nextMap.set(id, nextMap.get(id) || { urlId: id, url: r.url });
        } else {
          nextMap.delete(id);
        }
      }
    } else {
      if (checked) {
        nextMap.set(clickedId, nextMap.get(clickedId) || { urlId: clickedId, url: row.url });
      } else {
        nextMap.delete(clickedId);
      }
    }

    this.planMoveSelectedByCluster = {
      ...this.planMoveSelectedByCluster,
      [clusterId]: Array.from(nextMap.values())
    };
    this.planMoveLastAnchorByCluster = {
      ...this.planMoveLastAnchorByCluster,
      [clusterId]: clickedId
    };
  }

  planMoveSelectionCount(clusterId: string): number {
    return (this.planMoveSelectedByCluster[clusterId] || []).length;
  }

  clearPlanMoveSelection(clusterId: string): void {
    this.planMoveSelectedByCluster = { ...this.planMoveSelectedByCluster, [clusterId]: [] };
    this.planMoveTargetClusterIdBySource = { ...this.planMoveTargetClusterIdBySource, [clusterId]: '' };
    this.planBulkClassByCluster = { ...this.planBulkClassByCluster, [clusterId]: 'auto' };
    this.planMoveLastAnchorByCluster = { ...this.planMoveLastAnchorByCluster, [clusterId]: null };
  }

  isMovingPlanCluster(clusterId: string): boolean {
    return this.movingPlanClusterId === clusterId;
  }

  confirmMoveSelectedUrls(cluster: PlanCluster): void {
    const selected = this.planMoveSelectedByCluster[cluster.id] || [];
    const toClusterId = (this.planMoveTargetClusterIdBySource[cluster.id] || '').trim();
    if (!selected.length || !toClusterId || toClusterId === cluster.id || this.isMovingPlanCluster(cluster.id)) return;

    this.movingPlanClusterId = cluster.id;
    this.planError = null;
    this.clearPlanSuccess();
    const targetCluster = this.planClusters.find((c) => c.id === toClusterId);
    const items = selected.map((row) => ({ urlId: row.urlId, url: row.url }));
    const removals = selected.map((row) =>
      this.inventoryPlanClusters.removeUrlFromCluster(this.selectedDomain, cluster.id, row.urlId)
    );

    this.subscriptions.add(
      this.inventoryPlanClusters
        .addUrlsToCluster(this.selectedDomain, toClusterId, items, 'manual')
        .pipe(
          switchMap(() => (removals.length > 0 ? forkJoin(removals) : of([]))),
          switchMap(() => this.inventoryPlanClusters.getClusters(this.selectedDomain))
        )
        .subscribe({
          next: (res) => {
            this.planClusters = Array.isArray(res?.data?.topics) ? res.data.topics : this.planClusters;
            this.movingPlanClusterId = null;
            this.clearPlanMoveSelection(cluster.id);
            this.setPlanSuccess(
              `${items.length} URL${items.length === 1 ? '' : 's'} moved to ${targetCluster?.label || 'selected topic'}.`
            );
          },
          error: (err) => {
            this.movingPlanClusterId = null;
            this.planError = err?.error?.message || 'Could not move selected URLs to the chosen topic.';
          }
        })
    );
  }

  classOverrideLabel(value: PlanClusterClassOption): string {
    if (value === 'core') return 'Core';
    if (value === 'supporting') return 'Supporting';
    if (value === 'mention') return 'Mention';
    return 'Auto';
  }

  getPlanClassSelectValue(row: { topicClassOverride?: 'core' | 'supporting' | 'mention' | null }): PlanClusterClassOption {
    const v = row?.topicClassOverride;
    return v === 'core' || v === 'supporting' || v === 'mention' ? v : 'auto';
  }

  isPlanClassUpdating(clusterId: string, urlId?: string): boolean {
    const rowKey = `${clusterId}:${(urlId || '').trim()}`;
    const bulkKey = `${clusterId}:bulk`;
    return this.updatingPlanClassKey === rowKey || this.updatingPlanClassKey === bulkKey;
  }

  planClassMenuKey(clusterId: string, urlId: string): string {
    return `${clusterId}:${(urlId || '').trim()}`;
  }

  isPlanClassMenuOpen(clusterId: string, urlId: string): boolean {
    return this.openPlanClassMenuKey === this.planClassMenuKey(clusterId, urlId);
  }

  togglePlanClassMenu(clusterId: string, urlId: string, event?: MouseEvent): void {
    event?.stopPropagation();
    const key = this.planClassMenuKey(clusterId, urlId);
    this.openPlanClassMenuKey = this.openPlanClassMenuKey === key ? null : key;
  }

  updatePlanUrlClass(cluster: PlanCluster, row: { urlId: string }, nextClass: PlanClusterClassOption): void {
    if (!row?.urlId || this.isPlanClassUpdating(cluster.id, row.urlId)) return;
    this.openPlanClassMenuKey = null;
    this.updatingPlanClassKey = `${cluster.id}:${row.urlId}`;
    this.planError = null;
    this.subscriptions.add(
      this.inventoryPlanClusters
        .updateClusterUrlClass(this.selectedDomain, cluster.id, row.urlId, nextClass)
        .subscribe({
          next: (res) => {
            this.planClusters = Array.isArray(res?.data?.topics) ? res.data.topics : this.planClusters;
            this.updatingPlanClassKey = null;
            this.notifications.success(`Class set to ${this.classOverrideLabel(nextClass)}.`);
          },
          error: (err) => {
            this.updatingPlanClassKey = null;
            this.planError = err?.error?.message || 'Could not update class.';
            this.notifications.error('Failed to update class.');
          }
        })
    );
  }

  selectedPlanMoveUrlIds(clusterId: string): string[] {
    return Array.from(
      new Set(
        (this.planMoveSelectedByCluster[clusterId] || [])
          .map((row) => (row?.urlId || '').trim())
          .filter(Boolean)
      )
    );
  }

  updateSelectedPlanUrlsClass(cluster: PlanCluster): void {
    const urlIds = this.selectedPlanMoveUrlIds(cluster.id);
    const nextClass = this.planBulkClassByCluster[cluster.id] || 'auto';
    if (urlIds.length === 0 || this.isPlanClassUpdating(cluster.id)) return;

    this.updatingPlanClassKey = `${cluster.id}:bulk`;
    this.planError = null;
    this.subscriptions.add(
      this.inventoryPlanClusters
        .updateClusterUrlsClassBulk(this.selectedDomain, cluster.id, urlIds, nextClass)
        .subscribe({
          next: (res) => {
            this.planClusters = Array.isArray(res?.data?.topics) ? res.data.topics : this.planClusters;
            this.clearPlanMoveSelection(cluster.id);
            this.updatingPlanClassKey = null;
            this.notifications.success(
              `${urlIds.length} URL${urlIds.length === 1 ? '' : 's'} set to ${this.classOverrideLabel(nextClass)}.`
            );
          },
          error: (err) => {
            this.updatingPlanClassKey = null;
            this.planError = err?.error?.message || 'Could not update selected classes.';
            this.notifications.error('Failed to update selected classes.');
          }
        })
    );
  }

  private setPlanSuccess(message: string): void {
    this.planSuccess = message;
    if (this.planSuccessTimeout) {
      clearTimeout(this.planSuccessTimeout);
    }
    this.planSuccessTimeout = setTimeout(() => {
      this.planSuccess = null;
      this.planSuccessTimeout = null;
    }, 3000);
  }

  private clearPlanSuccess(): void {
    this.planSuccess = null;
    if (this.planSuccessTimeout) {
      clearTimeout(this.planSuccessTimeout);
      this.planSuccessTimeout = null;
    }
  }

  isBulkSelected(url: string): boolean {
    const u = (url || '').trim();
    return this.bulkSelectedUrls.some((x) => x.url === u);
  }

  toggleBulkSelect(page: InventoryPage): void {
    const u = page.url.trim();
    const next = this.bulkSelectedUrls.some((x) => x.url === u)
      ? this.bulkSelectedUrls.filter((x) => x.url !== u)
      : [...this.bulkSelectedUrls, { urlId: page.urlId || '', url: u }];
    this.bulkSelectedUrls = next;
  }

  onBulkCheckboxClick(page: InventoryPage, rows: InventoryPage[], index: number, event: MouseEvent): void {
    const input = event.target as HTMLInputElement | null;
    const checked = !!input?.checked;
    const clickedUrl = (page.url || '').trim();
    if (!clickedUrl) return;

    const nextMap = new Map(this.bulkSelectedUrls.map((x) => [x.url, x]));
    const anchorUrl = this.lastBulkSelectAnchorUrl;
    const anchorIndex = anchorUrl ? rows.findIndex((r) => (r.url || '').trim() === anchorUrl) : -1;
    const canRangeSelect = !!event.shiftKey && anchorIndex >= 0 && rows.length > 0;

    if (canRangeSelect) {
      const start = Math.min(anchorIndex, index);
      const end = Math.max(anchorIndex, index);
      for (let i = start; i <= end; i++) {
        const u = (rows[i]?.url || '').trim();
        if (!u) continue;
        if (checked) {
          nextMap.set(u, nextMap.get(u) || { urlId: rows[i]?.urlId || '', url: u });
        } else {
          nextMap.delete(u);
        }
      }
    } else {
      if (checked) {
        nextMap.set(clickedUrl, nextMap.get(clickedUrl) || { urlId: page.urlId || '', url: clickedUrl });
      } else {
        nextMap.delete(clickedUrl);
      }
    }

    this.bulkSelectedUrls = Array.from(nextMap.values());
    this.lastBulkSelectAnchorUrl = clickedUrl;
  }

  clearBulkClusterSelection(): void {
    this.bulkSelectedUrls = [];
    this.bulkTargetClusterId = null;
    this.bulkRemoveClusterId = null;
    this.lastBulkSelectAnchorUrl = null;
  }

  get bulkClusterSelectionCount(): number {
    return this.bulkSelectedUrls.length;
  }

  confirmBulkAddToCluster(): void {
    if (!this.bulkTargetClusterId || this.bulkSelectedUrls.length === 0) return;
    this.isBulkSaving = true;
    const targetClusterId = this.bulkTargetClusterId;
    const selectedUrls = this.bulkSelectedUrls.map((s) => s.url).filter(Boolean);
    const items = this.bulkSelectedUrls.map((s) => ({ urlId: s.urlId, url: s.url }));
    this.subscriptions.add(
      this.inventoryPlanClusters
        .addUrlsToCluster(this.selectedDomain, this.bulkTargetClusterId, items, 'bulk')
        .subscribe({
          next: (res) => {
            const nextClusters = Array.isArray(res?.data?.topics) ? res.data.topics : this.planClusters;
            this.planClusters = nextClusters;
            const clusterLabel = nextClusters.find((c) => c.id === targetClusterId)?.label || '';
            if (clusterLabel) {
              this.addClusterLabelsToInventoryRows(selectedUrls, clusterLabel);
            }
            const addedCount = items.length;
            this.notifications.success(
              `${addedCount} ${addedCount === 1 ? 'page' : 'pages'} added to "${clusterLabel || 'selected'}" cluster.`
            );
            this.clearBulkClusterSelection();
            this.isBulkSaving = false;
          },
          error: (err) => {
            this.planError = err?.error?.message || 'Could not add selected pages to cluster.';
            this.notifications.error('Failed to add selected pages to cluster.');
            this.isBulkSaving = false;
          }
        })
    );
  }

  confirmBulkRemoveFromCluster(): void {
    if (!this.bulkRemoveClusterId || this.bulkSelectedUrls.length === 0) return;
    const clusterId = this.bulkRemoveClusterId;
    const clusterLabel = this.planClusters.find((c) => c.id === clusterId)?.label || 'selected';
    const urlIds = this.selectedBulkUrlIds();
    if (urlIds.length === 0) {
      this.notifications.error('Selected pages are missing URL IDs. Refresh and try again.');
      return;
    }

    this.isBulkRemoving = true;
    const selectedUrls = this.bulkSelectedUrls.map((s) => s.url).filter(Boolean);
    const removals = urlIds.map((urlId) =>
      this.inventoryPlanClusters.removeUrlFromCluster(this.selectedDomain, clusterId, urlId)
    );

    this.subscriptions.add(
      forkJoin(removals).subscribe({
        next: (responses) => {
          const last = responses[responses.length - 1];
          this.planClusters = Array.isArray(last?.data?.topics) ? last.data.topics : this.planClusters;
          this.removeClusterLabelsFromInventoryRows(selectedUrls, clusterLabel);
          this.notifications.success(
            `${urlIds.length} ${urlIds.length === 1 ? 'page' : 'pages'} removed from "${clusterLabel}" cluster.`
          );
          this.clearBulkClusterSelection();
          this.isBulkRemoving = false;
        },
        error: (err) => {
          this.planError = err?.error?.message || 'Could not remove selected pages from cluster.';
          this.notifications.error('Failed to remove selected pages from cluster.');
          this.isBulkRemoving = false;
        }
      })
    );
  }

  private selectedBulkUrlIds(): string[] {
    return Array.from(new Set(this.bulkSelectedUrls.map((s) => (s.urlId || '').trim()).filter(Boolean)));
  }

  private addClusterLabelsToInventoryRows(urls: string[], clusterLabel: string): void {
    const normalizedUrls = new Set(urls.map((u) => (u || '').trim()).filter(Boolean));
    const label = (clusterLabel || '').trim();
    if (!label || normalizedUrls.size === 0) return;

    const apply = (rows: InventoryPage[]): InventoryPage[] =>
      rows.map((row) => {
        const rowUrl = (row.url || '').trim();
        if (!normalizedUrls.has(rowUrl)) return row;
        const current = Array.isArray(row.clusters) ? row.clusters : [];
        if (current.includes(label)) return row;
        return { ...row, clusters: [...current, label].sort((a, b) => a.localeCompare(b)) };
      });

    this.pages = apply(this.pages);
    this.comparePages = apply(this.comparePages);
  }

  private removeClusterLabelsFromInventoryRows(urls: string[], clusterLabel: string): void {
    const normalizedUrls = new Set(urls.map((u) => (u || '').trim()).filter(Boolean));
    const label = (clusterLabel || '').trim();
    if (!label || normalizedUrls.size === 0) return;

    const apply = (rows: InventoryPage[]): InventoryPage[] =>
      rows.map((row) => {
        const rowUrl = (row.url || '').trim();
        if (!normalizedUrls.has(rowUrl)) return row;
        const current = Array.isArray(row.clusters) ? row.clusters : [];
        if (!current.includes(label)) return row;
        const next = current.filter((c) => c !== label);
        return { ...row, clusters: next };
      });

    this.pages = apply(this.pages);
    this.comparePages = apply(this.comparePages);
  }

  private shouldRowBeVisibleForTrackingFilter(
    row: InventoryPage,
    filter: InventoryTrackingFilter
  ): boolean {
    const state = row.inventoryInclusionState || 'default';
    if (filter === 'all') return true;
    if (filter === 'excluded') return state === 'force_exclude';
    return state !== 'force_exclude';
  }

  private applyInclusionStateToInventoryRows(urlIds: string[], state: 'default' | 'force_exclude' | 'force_include'): void {
    const idSet = new Set(urlIds.map((id) => (id || '').trim()).filter(Boolean));
    if (idSet.size === 0) return;

    const apply = (rows: InventoryPage[], filter: InventoryTrackingFilter): InventoryPage[] => {
      const updated = rows.map((row) => {
        const rowId = (row.urlId || '').trim();
        if (!idSet.has(rowId)) return row;
        return { ...row, inventoryInclusionState: state };
      });
      return updated.filter((row) => this.shouldRowBeVisibleForTrackingFilter(row, filter));
    };

    this.pages = apply(this.pages, this.trackingFilter);
    this.comparePages = apply(this.comparePages, this.trackingFilter);
    this.syncPlanClustersAfterInclusionChange(Array.from(idSet), state);
  }

  private syncPlanClustersAfterInclusionChange(
    urlIds: string[],
    state: 'default' | 'force_exclude' | 'force_include'
  ): void {
    if (this.planClusters.length === 0) return;
    const idSet = new Set(urlIds.map((id) => (id || '').trim()).filter(Boolean));
    if (idSet.size === 0) return;

    if (state === 'force_exclude') {
      this.planClusters = this.planClusters.map((cluster) => ({
        ...cluster,
        urls: cluster.urls.filter((row) => !idSet.has((row.urlId || '').trim()))
      }));
      const nextMoveSelected: Record<string, Array<{ urlId: string; url: string }>> = {};
      for (const [clusterId, rows] of Object.entries(this.planMoveSelectedByCluster)) {
        nextMoveSelected[clusterId] = rows.filter((row) => !idSet.has((row.urlId || '').trim()));
      }
      this.planMoveSelectedByCluster = nextMoveSelected;
      return;
    }

    this.refreshPlanClusters();
  }

  updateTrackingFilter(next: InventoryTrackingFilter): void {
    if (this.trackingFilter === next) return;
    this.trackingFilter = next;
    this.clearQuickCompareSelection();
    this.pagination.page = 1;
    this.comparePagination.page = 1;
    this.clearInventorySourceCache();
    this.clearBulkClusterSelection();
    this.loadInventory();
  }

  isHealthBandFilterSelected(band: InventoryHealthFilterBand): boolean {
    return this.selectedHealthFilterBands.includes(band);
  }

  toggleHealthBandFilter(band: InventoryHealthFilterBand): void {
    const selected = new Set(this.selectedHealthFilterBands);
    if (selected.has(band)) {
      if (selected.size <= 1) return;
      selected.delete(band);
    } else {
      selected.add(band);
    }
    this.selectedHealthFilterBands = HEALTH_FILTER_BANDS.filter((value) => selected.has(value));
    this.clearQuickCompareSelection();
    this.pagination.page = 1;
    this.comparePagination.page = 1;
    this.clearBulkClusterSelection();
    if (this.canApplyInventoryClientView()) {
      this.applyInventoryClientView();
      return;
    }
    this.loadInventory();
  }

  private activeHealthBandFilters(): InventoryHealthFilterBand[] {
    if (this.selectedHealthFilterBands.length === HEALTH_FILTER_BANDS.length) return [];
    return [...this.selectedHealthFilterBands];
  }

  isPageExcluded(page: InventoryPage): boolean {
    return page.inventoryInclusionState === 'force_exclude';
  }

  setPageInclusionState(page: InventoryPage, state: 'default' | 'force_exclude' | 'force_include'): void {
    const urlId = (page.urlId || '').trim();
    if (!urlId || this.isUpdatingInclusion) return;
    this.isUpdatingInclusion = true;
    this.subscriptions.add(
      this.inventoryPlanClusters.updateUrlInclusionState(urlId, state, 'manual').subscribe({
        next: () => {
          this.isUpdatingInclusion = false;
          this.clearBulkClusterSelection();
          this.applyInclusionStateToInventoryRows([urlId], state);
          this.notifications.success(
            state === 'force_exclude' ? 'Page excluded from tracking.' : 'Page included in tracking.'
          );
        },
        error: (err) => {
          this.planError = err?.error?.message || 'Could not update page tracking state.';
          this.notifications.error('Failed to update tracking state.');
          this.isUpdatingInclusion = false;
        }
      })
    );
  }

  markBulkSelectionIncluded(): void {
    const urlIds = this.selectedBulkUrlIds();
    if (urlIds.length === 0 || this.isUpdatingInclusion) return;
    this.isUpdatingInclusion = true;
    this.subscriptions.add(
      this.inventoryPlanClusters.updateUrlsInclusionState(urlIds, 'default', 'manual').subscribe({
        next: () => {
          this.isUpdatingInclusion = false;
          this.applyInclusionStateToInventoryRows(urlIds, 'default');
          this.clearBulkClusterSelection();
          this.notifications.success(
            `${urlIds.length} ${urlIds.length === 1 ? 'page' : 'pages'} included in tracking.`
          );
        },
        error: (err) => {
          this.planError = err?.error?.message || 'Could not include selected pages.';
          this.notifications.error('Failed to include selected pages.');
          this.isUpdatingInclusion = false;
        }
      })
    );
  }

  markBulkSelectionExcluded(): void {
    const urlIds = this.selectedBulkUrlIds();
    if (urlIds.length === 0 || this.isUpdatingInclusion) return;
    this.isUpdatingInclusion = true;
    this.subscriptions.add(
      this.inventoryPlanClusters.updateUrlsInclusionState(urlIds, 'force_exclude', 'manual').subscribe({
        next: () => {
          this.isUpdatingInclusion = false;
          this.applyInclusionStateToInventoryRows(urlIds, 'force_exclude');
          this.clearBulkClusterSelection();
          this.notifications.success(
            `${urlIds.length} ${urlIds.length === 1 ? 'page' : 'pages'} excluded from tracking.`
          );
        },
        error: (err) => {
          this.planError = err?.error?.message || 'Could not exclude selected pages.';
          this.notifications.error('Failed to exclude selected pages.');
          this.isUpdatingInclusion = false;
        }
      })
    );
  }

  openPlanTabFromBulkHint(): void {
    this.setInventorySection('plan');
  }

  ngOnInit() {
    this.loadVisibleColumnsFromStorage();
    this.loadPagesFiltersExpandedFromStorage();
    const sectionParam = (this.route.snapshot.queryParamMap.get('section') || '').trim() as InventorySection;
    if (this.isActionPlanV2Enabled() && PLAN_V2_DEEP_LINK_SECTIONS.includes(sectionParam)) {
      this.inventorySection = sectionParam;
    }
    if (sectionParam === 'competing') this.inventorySection = sectionParam;
    this.domainContext.ensureInitialized();
    this.subscriptions.add(
      this.route.queryParamMap
        .pipe(
          distinctUntilChanged(
            (a, b) =>
              a.get(QUICK_COMPARE_PRIMARY_QP) === b.get(QUICK_COMPARE_PRIMARY_QP) &&
              a.get(QUICK_COMPARE_COMPARE_QP) === b.get(QUICK_COMPARE_COMPARE_QP)
          )
        )
        .subscribe((map) => this.applyQuickCompareFromQueryParamMap(map))
    );

    this.subscriptions.add(this.domainContext.loaded$.subscribe((loaded) => {
      this.isContextLoaded = loaded;
      if (!loaded) return;
      this.selectedDomain = this.domainContext.selectedDomain;
      this.loadTopicsForDomain(this.selectedDomain);
      this.pagination.page = 1;
      this.comparePagination.page = 1;
      this.selectedPathPrefixes = [];
      this.loadPathPrefixesForActiveDomain();
      this.resetClusterFilterRules();
      this.loadInventory();
      this.applyQuickCompareFromQueryParamMap(this.route.snapshot.queryParamMap);
      this.refreshPlanClusters();
      this.loadActionPlan();
      if (this.inventorySection === 'planV2') {
        this.loadPlanV2IssueDispositions(() => this.loadPlanV2());
      } else if (this.inventorySection === 'workItems' || this.inventorySection === 'previous') {
        this.loadPlanV2IssueDispositions();
      }
      this.clearBulkClusterSelection();
    }));

    this.subscriptions.add(this.domainContext.selectedDomain$.subscribe((domain) => {
      if (!this.isContextLoaded) return;
      if (domain === this.selectedDomain) return;
      this.clearQuickCompareSelection();
      this.clearBulkClusterSelection();
      this.selectedDomain = domain;
      this.clearTopicSuggestionCompetitorUiState();
      this.selectedTopicSlug = '';
      this.topicSearchQuery = '';
      this.loadTopicsForDomain(domain);
      this.pagination.page = 1;
      this.comparePagination.page = 1;
      this.selectedPathPrefixes = [];
      this.selectedComparePathPrefixes = [];
      this.comparePathPrefixError = null;
      this.resetClusterFilterRules();
      this.loadPathPrefixesForActiveDomain();
      this.loadInventory();
      this.refreshPlanClusters();
      this.loadActionPlan();
      if (this.inventorySection === 'planV2') {
        this.loadPlanV2IssueDispositions(() => this.loadPlanV2(1));
      } else if (this.inventorySection === 'workItems' || this.inventorySection === 'previous') {
        this.loadPlanV2IssueDispositions();
      }
    }));

    this.compareToDomain = this.compareDomainContext.selectedCompareDomain;
    this.subscriptions.add(this.compareDomainContext.selectedCompareDomain$.subscribe((domain) => {
      this.compareToDomain = domain;
      if (!this.isContextLoaded) return;
      if (this.isCompareTopicMode) {
        this.clearQuickCompareSelection();
        this.invalidateCompetitorTopicPillsRequests();
        this.resetCompetitorTopicPillsState();
        this.comparePagination.page = 1;
        this.selectedComparePathPrefixes = [];
        this.loadPathPrefixesForCompareDomain();
        this.loadInventory();
      } else {
        this.clearCompareInventoryState();
        this.refreshCompetitorTopicPillsIfEligible();
      }
    }));

    // Deferred — runs after all BehaviorSubject subscriptions have synchronously settled,
    // so clearQuickCompareSelection() cannot undo the drawer open.
    void Promise.resolve().then(() => this.applyPendingQuickCompareLaunch());
  }

  get quickCompareBothSelected(): boolean {
    return !!this.quickComparePrimaryUrl && !!this.quickCompareCompareUrl;
  }

  /** Host only (no port); pair with normalizeDomainForCompare for panel checks. */
  private hostnameFromInventoryUrl(url: string): string | null {
    const raw = (url || '').trim();
    if (!raw) return null;
    try {
      const u = new URL(raw);
      return u.hostname || null;
    } catch {
      try {
        const u = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
        return u.hostname || null;
      } catch {
        return null;
      }
    }
  }

  private urlBelongsToPanel(url: string, side: 'primary' | 'compare'): boolean {
    const host = this.hostnameFromInventoryUrl(url);
    if (!host) return false;
    const normalizedHost = this.normalizeDomainForCompare(host);
    const expected =
      side === 'primary'
        ? this.normalizeDomainForCompare(this.selectedDomain)
        : this.normalizeDomainForCompare(this.compareToDomain);
    return normalizedHost !== null && expected !== null && normalizedHost === expected;
  }

  isQuickCompareSelected(side: 'primary' | 'compare', url: string): boolean {
    const u = (url || '').trim();
    if (!u) return false;
    if (side === 'primary') {
      return this.quickComparePrimaryUrl === u;
    }
    return this.quickCompareCompareUrl === u;
  }

  toggleQuickCompareSelection(side: 'primary' | 'compare', page: InventoryPage): void {
    if (!this.urlBelongsToPanel(page.url, side)) {
      return;
    }
    const url = page.url.trim();
    if (side === 'primary') {
      this.quickComparePrimaryUrl = this.quickComparePrimaryUrl === url ? null : url;
    } else {
      this.quickCompareCompareUrl = this.quickCompareCompareUrl === url ? null : url;
    }
  }

  clearQuickCompareSelection(): void {
    this.quickComparePrimaryUrl = null;
    this.quickCompareCompareUrl = null;
    this.quickCompareDrawerOpen = false;
    this.clearQuickCompareQueryParamsFromUrl();
  }

  onQuickCompareCtaClick(): void {
    if (!this.quickCompareBothSelected) return;
    this.quickCompareDrawerOpen = true;
    this.setQuickCompareQueryParamsFromUrl(
      this.quickComparePrimaryUrl!.trim(),
      this.quickCompareCompareUrl!.trim()
    );
  }

  onQuickCompareDrawerClose(): void {
    this.quickCompareDrawerOpen = false;
    this.clearQuickCompareQueryParamsFromUrl();
  }

  onOpenFullCompareFromQuickDrawer(): void {
    const your = this.normalizeQuickCompareUrlCandidate(this.quickComparePrimaryUrl);
    const competitor = this.normalizeQuickCompareUrlCandidate(this.quickCompareCompareUrl);
    if (!your || !competitor) return;
    this.quickCompareDrawerOpen = false;
    this.router.navigate(['/page-comparison'], {
      queryParams: {
        [FULL_COMPARE_YOUR_QP]: your,
        [FULL_COMPARE_COMPETITOR_QP]: competitor
      }
    });
  }

  private parseQuickCompareUrls(
    primary: string | null,
    compare: string | null
  ): { primaryUrl: string; compareUrl: string } | null {
    const primaryUrl = this.normalizeQuickCompareUrlCandidate(primary);
    const compareUrl = this.normalizeQuickCompareUrlCandidate(compare);
    if (!primaryUrl || !compareUrl) return null;
    return { primaryUrl, compareUrl };
  }

  private normalizeQuickCompareUrlCandidate(raw: string | null | undefined): string | null {
    const t = (raw || '').trim();
    if (!t) return null;
    try {
      const u = new URL(t);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
      return u.href;
    } catch {
      try {
        const u = new URL(/^https?:\/\//i.test(t) ? t : `https://${t}`);
        if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
        return u.href;
      } catch {
        return null;
      }
    }
  }

  private applyQuickCompareFromQueryParamMap(map: ParamMap): void {
    if (this.updatingQuickCompareRoute || !this.isContextLoaded) return;

    const rawP = map.get(QUICK_COMPARE_PRIMARY_QP);
    const rawC = map.get(QUICK_COMPARE_COMPARE_QP);
    const hasP = !!(rawP && rawP.trim());
    const hasC = !!(rawC && rawC.trim());

    if (quickCompareQueryParamValueTooLarge(rawP) || quickCompareQueryParamValueTooLarge(rawC)) {
      this.clearQuickCompareSelection();
      return;
    }

    if (hasP !== hasC) {
      if (!environment.production && (hasP || hasC)) {
        console.warn(
          'Inventory Quick Compare: both quickComparePrimary and quickCompareCompare are required; stripping partial query.'
        );
      }
      if (hasP || hasC) {
        this.clearQuickCompareSelection();
      }
      return;
    }

    if (!hasP && !hasC) return;

    const parsed = this.parseQuickCompareUrls(rawP, rawC);
    if (!parsed) {
      this.quickComparePrimaryUrl = null;
      this.quickCompareCompareUrl = null;
      this.quickCompareDrawerOpen = false;
      this.clearQuickCompareQueryParamsFromUrl();
      return;
    }

    if (this.isCompareTopicMode) {
      if (
        !this.urlBelongsToPanel(parsed.primaryUrl, 'primary') ||
        !this.urlBelongsToPanel(parsed.compareUrl, 'compare')
      ) {
        this.quickComparePrimaryUrl = null;
        this.quickCompareCompareUrl = null;
        this.quickCompareDrawerOpen = false;
        this.clearQuickCompareQueryParamsFromUrl();
        return;
      }
    }

    this.quickComparePrimaryUrl = parsed.primaryUrl;
    this.quickCompareCompareUrl = parsed.compareUrl;
    this.quickCompareDrawerOpen = true;
  }

  private applyPendingQuickCompareLaunch(): void {
    const pending = this.quickCompareLaunchService.consumePendingLaunch();
    if (!pending) return;

    const parsed = this.parseQuickCompareUrls(pending.primaryUrl, pending.compareUrl);
    if (!parsed) {
      if (!environment.production) {
        console.warn('[QuickCompareLaunch] URL parse failed', pending);
      }
      return;
    }

    // Set compareToDomain directly — bypasses the compareDomainContext subscription
    // that would call clearQuickCompareSelection() and close the drawer.
    if (pending.compareDomain && !this.compareToDomain) {
      this.compareToDomain = pending.compareDomain;
      this.compareDomainContext.setSelectedCompareDomain(pending.compareDomain);
    }

    this.quickComparePrimaryUrl = parsed.primaryUrl;
    this.quickCompareCompareUrl = parsed.compareUrl;
    this.quickCompareDrawerOpen = true;

    if (this.compareToDomain) {
      this.loadPathPrefixesForCompareDomain();
    }
  }

  private setQuickCompareQueryParamsFromUrl(primaryUrl: string, compareUrl: string): void {
    if (!canSyncQuickCompareUrlsToQueryParams(primaryUrl, compareUrl)) {
      if (!environment.production) {
        console.warn(
          'Inventory Quick Compare: URLs too long for address-bar sync; drawer stays open without query params.'
        );
      }
      return;
    }
    this.updatingQuickCompareRoute = true;
    void this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: {
          [QUICK_COMPARE_PRIMARY_QP]: primaryUrl,
          [QUICK_COMPARE_COMPARE_QP]: compareUrl
        },
        queryParamsHandling: 'merge',
        replaceUrl: true
      })
      .then(
        () => {
          this.updatingQuickCompareRoute = false;
        },
        () => {
          this.updatingQuickCompareRoute = false;
        }
      );
  }

  private clearQuickCompareQueryParamsFromUrl(): void {
    const snap = this.route.snapshot.queryParamMap;
    if (!snap.get(QUICK_COMPARE_PRIMARY_QP) && !snap.get(QUICK_COMPARE_COMPARE_QP)) {
      return;
    }
    this.updatingQuickCompareRoute = true;
    void this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: {
          [QUICK_COMPARE_PRIMARY_QP]: null,
          [QUICK_COMPARE_COMPARE_QP]: null
        },
        queryParamsHandling: 'merge',
        replaceUrl: true
      })
      .then(
        () => {
          this.updatingQuickCompareRoute = false;
        },
        () => {
          this.updatingQuickCompareRoute = false;
        }
      );
  }

  /** Same normalization as CompareDomainContextService for consistent equality checks. */
  private normalizeDomainForCompare(value: string | null | undefined): string | null {
    const normalized = (value || '')
      .toLowerCase()
      .trim()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0]
      .split('?')[0]
      .split('#')[0]
      .split(':')[0]
      .trim();
    return normalized || null;
  }

  get isCompareTopicMode(): boolean {
    const topic = this.selectedTopicSlug.trim();
    if (!topic || !this.compareToDomain) return false;
    const activeNorm = this.normalizeDomainForCompare(this.selectedDomain);
    return this.compareToDomain !== activeNorm;
  }

  /** Inline competitor strip: topic selected, not side-by-side, main shell visible. */
  get showTopicCompetitorStrip(): boolean {
    if (!this.selectedTopicSlug.trim()) return false;
    if (this.isCompareTopicMode) return false;
    if (this.isEmpty) return false;
    if (this.loading || this.error) return false;
    return true;
  }

  private resetCompetitorTopicPillsState(): void {
    this.competitorTopicPills = [];
    this.competitorTopicPillsLoading = false;
    this.competitorTopicPillsNoMarked = false;
    this.competitorTopicPillsSettingsError = null;
  }

  private invalidateCompetitorTopicPillsRequests(): void {
    this.competitorTopicPillsRequestGen++;
  }

  /**
   * Match inventory topic slug to a Domain Topics slug: exact or conservative inflection (singular/plural, etc.).
   * Picker “primary” child slugs can differ from another domain’s `childTopicSlugs` strings while inventory still matches pages.
   */
  private slugMatchesTopicSlugForAuthority(selected: string, candidate: string): boolean {
    const a = selected.trim().toLowerCase();
    const b = candidate.trim().toLowerCase();
    if (!a || !b) return false;
    if (a === b) return true;
    return isInflectionEquivalentSlug(a, b);
  }

  /** Authority for selected slug: parent or mapped child; same inflection rules as topic option clustering. */
  private resolveAuthorityForSelectedSlug(parentTopics: ParentTopic[], slug: string): number | null {
    const s = slug.trim().toLowerCase();
    if (!s) return null;

    for (const p of parentTopics) {
      const score = p.authorityScore;
      if (!Number.isFinite(score)) continue;

      const parentSlug = (p.parentTopicSlug || '').trim().toLowerCase();
      if (this.slugMatchesTopicSlugForAuthority(s, parentSlug)) {
        return score;
      }
      const children = Array.isArray(p.childTopicSlugs) ? p.childTopicSlugs : [];
      for (const raw of children) {
        const c = (raw || '').trim().toLowerCase();
        if (this.slugMatchesTopicSlugForAuthority(s, c)) {
          return score;
        }
      }
    }
    return null;
  }

  private competitorTopicLoadFailedMessage(domain: string, err: any): string {
    const status = err?.status;
    const serverMsg = (err?.error?.message || '').toString();
    if (status === 404 || /not found in your workspace/i.test(serverMsg)) {
      return (
        `${domain} is not in this workspace (there are no URLs for that domain here), so we cannot load topic authority the same way we do for your active domain. ` +
        `Add URLs for that domain to this workspace if you want scores and side-by-side inventory here, or adjust your marked competitor list.`
      );
    }
    return (
      `We could not load Domain Topics for ${domain}. ${serverMsg || 'Check your connection and try again.'}`
    );
  }

  private competitorTopicAggregatesMissingMessage(domain: string, apiMessage?: string): string {
    return (
      `We have no aggregated Domain Topics for ${domain} yet, so there is no authority score to show. ` +
      `Add analyzed pages for that domain in this workspace and run your topic aggregation pipeline (for example topics:aggregate) so scores can appear here.` +
      (apiMessage ? ` (${apiMessage})` : '')
    );
  }

  private competitorTopicNotPresentMessage(domain: string): string {
    return (
      `This selected topic does not appear in aggregated Domain Topics for ${domain}, so there is no authority score to show. ` +
      `They may have little or no analyzed content tagged with this topic in your data, or the topic model may differ from your active domain.`
    );
  }

  private clearTopicSuggestionCompetitorUiState(): void {
    this.markedCompetitorsForPreview = null;
    this.topicSuggestionCompetitorCache = {};
    if (this.topicSuggestionHoverTimer) {
      clearTimeout(this.topicSuggestionHoverTimer);
      this.topicSuggestionHoverTimer = null;
    }
  }

  /** Marked competitors for the active domain (cached for typeahead hover previews). */
  private getMarkedCompetitorsPreview$() {
    const active = this.selectedDomain.trim();
    if (!active) {
      return of<string[]>([]);
    }
    if (this.markedCompetitorsForPreview !== null) {
      return of(this.markedCompetitorsForPreview);
    }
    return this.compareContext.getSettings(active).pipe(
      map((res) => {
        const m = Array.isArray(res?.data?.markedCompetitorDomains)
          ? res.data.markedCompetitorDomains
          : [];
        this.markedCompetitorsForPreview = m;
        return m;
      }),
      catchError(() => {
        this.markedCompetitorsForPreview = [];
        return of<string[]>([]);
      })
    );
  }

  /** One parallel batch: compact Domain Topics per marked competitor for this slug. */
  private fetchCompetitorPillRowsForSlug$(slug: string, marked: string[]) {
    const s = slug.trim().toLowerCase();
    if (!marked.length) {
      return of<CompetitorTopicPillRow[]>([]);
    }
    const requests = marked.map((domain) =>
      this.domainTopicsService.getDomainTopics(domain, { compact: true }).pipe(
        map((response): CompetitorTopicPillRow => {
          const parents = Array.isArray(response?.data?.parentTopics) ? response.data.parentTopics : [];
          const apiMessage =
            typeof (response?.data as { message?: string })?.message === 'string'
              ? (response?.data as { message?: string }).message
              : undefined;
          if (parents.length === 0) {
            return {
              domain,
              kind: 'unavailable',
              message: this.competitorTopicAggregatesMissingMessage(domain, apiMessage)
            };
          }
          const auth = this.resolveAuthorityForSelectedSlug(parents, s);
          if (auth === null) {
            return { domain, kind: 'unavailable', message: this.competitorTopicNotPresentMessage(domain) };
          }
          return { domain, kind: 'ready', authorityScore: auth };
        }),
        catchError((err) =>
          of<CompetitorTopicPillRow>({
            domain,
            kind: 'unavailable',
            message: this.competitorTopicLoadFailedMessage(domain, err)
          })
        )
      )
    );
    return forkJoin(requests);
  }

  onTopicSuggestionRowEnter(topic: TopicPickerOption): void {
    if (this.topicSuggestionHoverTimer) {
      clearTimeout(this.topicSuggestionHoverTimer);
      this.topicSuggestionHoverTimer = null;
    }
    const slug = topic.slug.trim().toLowerCase();
    this.topicSuggestionHoverTimer = setTimeout(() => {
      this.topicSuggestionHoverTimer = null;
      this.prefetchCompetitorPreviewForSlug(slug);
    }, 120);
  }

  onTopicSuggestionRowLeave(): void {
    if (this.topicSuggestionHoverTimer) {
      clearTimeout(this.topicSuggestionHoverTimer);
      this.topicSuggestionHoverTimer = null;
    }
  }

  private prefetchCompetitorPreviewForSlug(slug: string): void {
    const s = slug.trim().toLowerCase();
    const existing = this.topicSuggestionCompetitorCache[s];
    if (existing?.status === 'ready' || existing?.status === 'loading') {
      return;
    }

    this.topicSuggestionCompetitorCache = { ...this.topicSuggestionCompetitorCache, [s]: { status: 'loading' } };

    this.subscriptions.add(
      this.getMarkedCompetitorsPreview$()
        .pipe(switchMap((marked) => this.fetchCompetitorPillRowsForSlug$(s, marked)))
        .subscribe({
          next: (rows) => {
            this.topicSuggestionCompetitorCache = {
              ...this.topicSuggestionCompetitorCache,
              [s]: { status: 'ready', rows }
            };
          },
          error: (err) => {
            this.topicSuggestionCompetitorCache = {
              ...this.topicSuggestionCompetitorCache,
              [s]: {
                status: 'error',
                error:
                  err?.error?.message ||
                  'Could not load competitor topic scores. Check your connection and try again.'
              }
            };
          }
        })
    );
  }

  topicSuggestionCompetitorEntry(slug: string):
    | { status: 'loading' | 'ready' | 'error'; rows?: CompetitorTopicPillRow[]; error?: string }
    | undefined {
    return this.topicSuggestionCompetitorCache[slug.trim().toLowerCase()];
  }

  /**
   * Apply topic filter and Compare To in one gesture from the typeahead row.
   * Uses mousedown + stopPropagation so the row button does not steal the event.
   */
  compareWithTopicFromDropdown(domain: string, topic: TopicPickerOption, ev: MouseEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.selectTopic(topic);
    this.selectCompareDomainFromTopicPill(domain);
  }

  refreshCompetitorTopicPillsIfEligible(): void {
    if (!this.showTopicCompetitorStrip) {
      this.invalidateCompetitorTopicPillsRequests();
      this.resetCompetitorTopicPillsState();
      return;
    }
    this.loadCompetitorTopicPills();
  }

  private loadCompetitorTopicPills(): void {
    const active = this.selectedDomain.trim();
    const slug = this.selectedTopicSlug.trim().toLowerCase();
    if (!active || !slug) {
      this.resetCompetitorTopicPillsState();
      return;
    }

    const gen = ++this.competitorTopicPillsRequestGen;
    this.competitorTopicPillsLoading = true;
    this.competitorTopicPillsNoMarked = false;
    this.competitorTopicPillsSettingsError = null;
    this.competitorTopicPills = [];

    this.subscriptions.add(
      this.compareContext.getSettings(active).subscribe({
        next: (res) => {
          if (gen !== this.competitorTopicPillsRequestGen) return;
          const marked = Array.isArray(res?.data?.markedCompetitorDomains)
            ? res.data.markedCompetitorDomains
            : [];
          if (marked.length === 0) {
            this.competitorTopicPillsLoading = false;
            this.competitorTopicPillsNoMarked = true;
            return;
          }

          this.subscriptions.add(
            this.fetchCompetitorPillRowsForSlug$(slug, marked).subscribe({
              next: (rows) => {
                if (gen !== this.competitorTopicPillsRequestGen) return;
                this.competitorTopicPills = rows;
                this.competitorTopicPillsLoading = false;
              },
              error: () => {
                if (gen !== this.competitorTopicPillsRequestGen) return;
                this.competitorTopicPillsLoading = false;
                this.competitorTopicPillsSettingsError =
                  'Could not load competitor topic data. Check your connection and try again.';
              }
            })
          );
        },
        error: (err) => {
          if (gen !== this.competitorTopicPillsRequestGen) return;
          this.competitorTopicPillsLoading = false;
          this.competitorTopicPillsSettingsError =
            err?.error?.message ||
            'Could not load your marked competitor list. Open the header Competitors menu and try again.';
        }
      })
    );
  }

  selectCompareDomainFromTopicPill(domain: string): void {
    if (this.isSavingCompareFromTopicPill) return;
    const d = domain.trim();
    if (!d || !this.selectedDomain.trim()) return;

    this.isSavingCompareFromTopicPill = true;
    this.compareTopicPillSaveError = null;

    this.subscriptions.add(
      this.compareContext
        .updateSettings({
          activeDomain: this.selectedDomain.trim(),
          selectedCompareDomain: d
        })
        .subscribe({
          next: (res) => {
            this.compareDomainContext.setSelectedCompareDomain(res?.data?.selectedCompareDomain ?? null);
            this.isSavingCompareFromTopicPill = false;
          },
          error: (err) => {
            this.compareTopicPillSaveError =
              err?.error?.message ||
              'Could not set Compare To. Check your connection or use the header Compare controls.';
            this.isSavingCompareFromTopicPill = false;
          }
        })
    );
  }

  private clearCompareInventoryState(invalidateInFlight = false) {
    this.comparePathPrefixesRequestGen++;
    if (invalidateInFlight) {
      this.inventoryRequestGen++;
    }
    this.compareLoading = false;
    this.compareError = null;
    this.comparePages = [];
    this.comparePagination = { page: 1, limit: 50, total: 0, totalPages: 0 };
    this.availableComparePathPrefixes = [];
    this.selectedComparePathPrefixes = [];
    this.loadingComparePathPrefixes = false;
    this.comparePathPrefixError = null;
    this.clearQuickCompareSelection();
  }

  private loadPathPrefixesForActiveDomain() {
    const requestGen = ++this.activePathPrefixesRequestGen;
    this.pathPrefixError = null;
    this.availablePathPrefixes = [];
    if (!this.selectedDomain.trim()) {
      this.loadingPathPrefixes = false;
      return;
    }
    this.loadingPathPrefixes = true;
    const domain = encodeURIComponent(this.selectedDomain.trim());
    this.http
      .get<{ success: boolean; data: { prefixes: string[] } }>(
        `${this.API_URL}/urls/content-inventory/path-prefixes?domain=${domain}`
      )
      .subscribe({
        next: (res) => {
          if (requestGen !== this.activePathPrefixesRequestGen) return;
          this.availablePathPrefixes = Array.isArray(res.data?.prefixes) ? res.data.prefixes : [];
          this.loadingPathPrefixes = false;
        },
        error: (err) => {
          if (requestGen !== this.activePathPrefixesRequestGen) return;
          this.pathPrefixError = err?.error?.message || 'Failed to load path filters.';
          this.loadingPathPrefixes = false;
        },
      });
  }

  private loadPathPrefixesForCompareDomain() {
    const requestGen = ++this.comparePathPrefixesRequestGen;
    this.comparePathPrefixError = null;
    this.availableComparePathPrefixes = [];
    const d = this.compareToDomain?.trim();
    if (!d) {
      this.loadingComparePathPrefixes = false;
      return;
    }
    this.loadingComparePathPrefixes = true;
    this.http
      .get<{ success: boolean; data: { prefixes: string[] } }>(
        `${this.API_URL}/urls/content-inventory/path-prefixes?domain=${encodeURIComponent(d)}`
      )
      .subscribe({
        next: (res) => {
          if (requestGen !== this.comparePathPrefixesRequestGen) return;
          this.availableComparePathPrefixes = Array.isArray(res.data?.prefixes) ? res.data.prefixes : [];
          this.loadingComparePathPrefixes = false;
        },
        error: (err) => {
          if (requestGen !== this.comparePathPrefixesRequestGen) return;
          this.comparePathPrefixError = err?.error?.message || 'Failed to load path filters.';
          this.loadingComparePathPrefixes = false;
        },
      });
  }

  /** Path chip options: Homepage, top-level (one segment), then discovered subfolder segments. */
  primaryPathChipOptions(): string[] {
    const rest = this.availablePathPrefixes.filter((p) => p !== '/');
    return ['/', PATH_PREFIX_ONE_SEGMENT, ...rest];
  }

  comparePathChipOptions(): string[] {
    const rest = this.availableComparePathPrefixes.filter((p) => p !== '/');
    return ['/', PATH_PREFIX_ONE_SEGMENT, ...rest];
  }

  pathPrefixChipLabel(prefix: string): string {
    if (prefix === '/') return 'Homepage';
    if (prefix === PATH_PREFIX_ONE_SEGMENT) return 'Top-level';
    return prefix;
  }

  isPathPrefixSelected(prefix: string, side: 'primary' | 'compare'): boolean {
    const list = side === 'primary' ? this.selectedPathPrefixes : this.selectedComparePathPrefixes;
    return list.includes(prefix);
  }

  togglePathPrefix(prefix: string, side: 'primary' | 'compare') {
    const list = side === 'primary' ? this.selectedPathPrefixes : this.selectedComparePathPrefixes;
    const next = list.includes(prefix) ? list.filter((p) => p !== prefix) : [...list, prefix];
    if (side === 'primary') {
      this.selectedPathPrefixes = next;
    } else {
      this.selectedComparePathPrefixes = next;
    }
    this.clearQuickCompareSelection();
    this.pagination.page = 1;
    this.comparePagination.page = 1;
    this.loadInventory();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest?.('[data-cluster-filter-root]')) return;
    if (target?.closest?.('[data-column-picker-root]')) return;
    if (target?.closest?.('[data-plan-class-menu-root]')) return;
    this.clusterFilterOpenRuleUid = null;
    this.columnPickerOpen = false;
    this.openPlanClassMenuKey = null;
  }

  private defaultVisibleColumns(): Record<InventoryColumnKey, boolean> {
    return {
      score: true,
      issues: true,
      health: true,
      realizationGap: true,
      realizationRatio: true,
      serpKeywordDifficulty: true,
      demandClaim: true,
      signals: true,
      topTopics: true,
      clusters: true,
      topicClass: true,
      topicRelevance: true,
      topicWeight: true,
      keywords: true,
      estimatedOrganicTraffic: true,
      analyzedAt: true
    };
  }

  private loadVisibleColumnsFromStorage(): void {
    const defaults = this.defaultVisibleColumns();
    this.visibleColumns = defaults;
    try {
      const raw = globalThis.localStorage?.getItem(INVENTORY_COLUMN_PREFS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<Record<string, unknown>>;
      const next = { ...defaults };
      (Object.keys(defaults) as InventoryColumnKey[]).forEach((key) => {
        const value = parsed[key];
        if (typeof value === 'boolean') {
          next[key] = value;
        }
      });
      this.visibleColumns = next;
    } catch {
      this.visibleColumns = defaults;
    }
  }

  private loadPagesFiltersExpandedFromStorage(): void {
    this.pagesFiltersExpanded = false;
    try {
      const raw = globalThis.localStorage?.getItem(INVENTORY_PAGES_FILTERS_EXPANDED_STORAGE_KEY);
      if (raw === 'true') {
        this.pagesFiltersExpanded = true;
      }
    } catch {
      this.pagesFiltersExpanded = false;
    }
  }

  private persistPagesFiltersExpanded(): void {
    try {
      globalThis.localStorage?.setItem(
        INVENTORY_PAGES_FILTERS_EXPANDED_STORAGE_KEY,
        this.pagesFiltersExpanded ? 'true' : 'false'
      );
    } catch {
      // Ignore storage errors.
    }
  }

  usePagesFilterCollapse(): boolean {
    return !this.isCompareTopicMode;
  }

  togglePagesFiltersExpanded(): void {
    this.pagesFiltersExpanded = !this.pagesFiltersExpanded;
    this.persistPagesFiltersExpanded();
  }

  private persistVisibleColumns(): void {
    try {
      globalThis.localStorage?.setItem(
        INVENTORY_COLUMN_PREFS_STORAGE_KEY,
        JSON.stringify(this.visibleColumns)
      );
    } catch {
      // Ignore storage errors (private mode, disabled storage, etc).
    }
  }

  isColumnVisible(key: InventoryColumnKey): boolean {
    return this.visibleColumns[key] !== false;
  }

  isColumnOptionAvailable(option: InventoryColumnOption): boolean {
    if (!option.requiresTopicScope) return true;
    return this.hasTopicScope;
  }

  toggleColumnPicker(event?: MouseEvent): void {
    event?.stopPropagation();
    this.columnPickerOpen = !this.columnPickerOpen;
  }

  closeColumnPicker(event?: MouseEvent): void {
    event?.stopPropagation();
    this.columnPickerOpen = false;
  }

  toggleColumnVisibility(key: InventoryColumnKey, event?: Event): void {
    event?.stopPropagation();
    this.visibleColumns = {
      ...this.visibleColumns,
      [key]: !this.isColumnVisible(key)
    };
    this.persistVisibleColumns();
  }

  trackPlanCluster(_index: number, cluster: PlanCluster): string {
    return cluster.id;
  }

  trackPlanTopicCandidateRow(_index: number, row: PlanTopicCandidateRow): string {
    return row.urlId || row.url || String(_index);
  }

  trackClusterFilterRule(_index: number, rule: ClusterFilterRuleRow): string {
    return rule.uid;
  }

  private newClusterFilterRuleUid(): string {
    const c: any = globalThis as any;
    if (c?.crypto?.randomUUID) return c.crypto.randomUUID() as string;
    return `r_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }

  private makeClusterFilterRule(partial?: Partial<ClusterFilterRuleRow>): ClusterFilterRuleRow {
    return {
      uid: partial?.uid || this.newClusterFilterRuleUid(),
      mode: partial?.mode || 'or',
      join: partial?.join || 'and',
      topicIds: partial?.topicIds ? [...partial.topicIds] : [],
      searchQuery: partial?.searchQuery || '',
    };
  }

  resetClusterFilterRules(): void {
    this.clusterFilterOpenRuleUid = null;
    const next = [this.makeClusterFilterRule()];
    this.clusterFilterRules = next;
    this.appliedClusterFilterRules = this.cloneClusterRules(next);
  }

  private cloneClusterRules(rules: ClusterFilterRuleRow[]): ClusterFilterRuleRow[] {
    return rules.map((r) => ({
      uid: r.uid,
      mode: r.mode,
      join: r.join,
      topicIds: [...r.topicIds],
      searchQuery: r.searchQuery,
    }));
  }

  /** Same canonical JSON as sent in `clusterRules` (sorted ids per rule). */
  private buildClusterRulesQueryPayload(rules: ClusterFilterRuleRow[]): string | null {
    const active = rules.filter((r) => r.topicIds.length > 0);
    if (active.length === 0) return null;
    const payload = active.map((r, idx) => ({
      mode: r.mode,
      ids: [...r.topicIds].sort((a, b) => a.localeCompare(b)),
      join: idx === 0 ? 'and' : r.join,
    }));
    return JSON.stringify(payload);
  }

  get clusterFilterApplyVisible(): boolean {
    if (this.isCompareTopicMode || this.planClusters.length === 0) return false;
    const draft = this.buildClusterRulesQueryPayload(this.clusterFilterRules);
    const applied = this.buildClusterRulesQueryPayload(this.appliedClusterFilterRules);
    return draft !== applied;
  }

  applyClusterFilterDraft(): void {
    if (!this.clusterFilterApplyVisible) return;
    this.appliedClusterFilterRules = this.cloneClusterRules(this.clusterFilterRules);
    this.clusterFilterOpenRuleUid = null;
    this.pagination.page = 1;
    this.comparePagination.page = 1;
    this.loadInventory();
  }

  addClusterFilterRule(): void {
    this.clusterFilterRules = [...this.clusterFilterRules, this.makeClusterFilterRule({ join: 'and' })];
  }

  removeClusterFilterRule(index: number): void {
    if (this.clusterFilterRules.length <= 1) {
      this.resetClusterFilterRules();
      this.pagination.page = 1;
      this.comparePagination.page = 1;
      this.loadInventory();
      return;
    }
    const next = this.clusterFilterRules.filter((_, i) => i !== index);
    this.clusterFilterRules = next.length > 0 ? next : [this.makeClusterFilterRule()];
    this.clusterFilterOpenRuleUid = null;
  }

  toggleClusterFilterDropdown(ruleUid: string, event?: MouseEvent): void {
    event?.stopPropagation();
    this.clusterFilterOpenRuleUid = this.clusterFilterOpenRuleUid === ruleUid ? null : ruleUid;
  }

  planClusterById(clusterId: string): PlanCluster | undefined {
    return this.planClusters.find((c) => c.id === clusterId);
  }

  clustersForClusterFilterRule(rule: ClusterFilterRuleRow): PlanCluster[] {
    const q = (rule.searchQuery || '').trim().toLowerCase();
    if (!q) return this.planClusters;
    return this.planClusters.filter((c) => {
      const label = (c.label || '').toLowerCase();
      const slug = (c.topicSlug || '').toLowerCase();
      return label.includes(q) || slug.includes(q);
    });
  }

  isClusterSelectedInRule(rule: ClusterFilterRuleRow, clusterId: string): boolean {
    return rule.topicIds.includes(clusterId);
  }

  toggleClusterInRule(rule: ClusterFilterRuleRow, clusterId: string, event?: Event): void {
    event?.stopPropagation();
    if (!clusterId) return;
    const has = rule.topicIds.includes(clusterId);
    rule.topicIds = has ? rule.topicIds.filter((id) => id !== clusterId) : [...rule.topicIds, clusterId];
  }

  setClusterRuleMode(rule: ClusterFilterRuleRow, mode: ClusterFilterRuleMode): void {
    if (rule.mode === mode) return;
    rule.mode = mode;
  }

  setClusterRuleJoin(rule: ClusterFilterRuleRow, join: ClusterFilterRuleJoin): void {
    if (rule.join === join) return;
    rule.join = join;
  }

  private clusterRulesQueryJson(): string | null {
    return this.buildClusterRulesQueryPayload(this.appliedClusterFilterRules);
  }

  private inventoryQueryForDomain(
    domain: string,
    page: number,
    pathPrefixes: string[],
    opts?: {
      limit?: number | 'all';
      includeHealthBands?: boolean;
      sortBy?: SortField;
      sortOrder?: 'asc' | 'desc';
    }
  ): URLSearchParams {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(opts?.limit ?? this.pagination.limit));
    params.set('sortBy', opts?.sortBy ?? this.sortBy);
    params.set('sortOrder', opts?.sortOrder ?? this.sortOrder);
    if (domain.trim()) {
      params.set('domain', domain.trim());
    }
    if (this.searchQuery.trim()) {
      params.set('search', this.searchQuery.trim());
    }
    if (this.selectedTopicSlug.trim()) {
      params.set('topic', this.selectedTopicSlug.trim());
    }
    for (const p of pathPrefixes) {
      params.append('pathPrefix', p);
    }
    if (!this.isCompareTopicMode) {
      const clusterRulesJson = this.clusterRulesQueryJson();
      if (clusterRulesJson) {
        params.set('clusterRules', clusterRulesJson);
      }
    }
    params.set('inclusionFilter', this.trackingFilter);
    const includeHealthBands = opts?.includeHealthBands ?? true;
    if (includeHealthBands) {
      for (const band of this.activeHealthBandFilters()) {
        params.append('healthBand', band);
      }
    }
    return params;
  }

  private buildInventorySourceQueryKey(): string {
    return JSON.stringify({
      domain: this.selectedDomain.trim().toLowerCase(),
      tracking: this.trackingFilter,
      topic: this.selectedTopicSlug.trim().toLowerCase(),
      paths: [...this.selectedPathPrefixes].sort(),
      search: this.searchQuery.trim().toLowerCase(),
      clusterRules: this.clusterRulesQueryJson()
    });
  }

  private clearInventorySourceCache(): void {
    this.inventorySourcePages = null;
    this.inventorySourceQueryKey = '';
  }

  private canApplyInventoryClientView(): boolean {
    return !this.isCompareTopicMode &&
      this.inventorySourcePages !== null &&
      this.inventorySourceQueryKey === this.buildInventorySourceQueryKey();
  }

  private applyInventoryClientView(): void {
    if (!this.inventorySourcePages) return;
    const view = buildInventoryClientView(this.inventorySourcePages, {
      healthBands: this.activeHealthBandFilters(),
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      page: this.pagination.page,
      limit: this.pagination.limit
    });
    this.pages = view.pages;
    this.pagination = view.pagination;
    this.refreshCompetitorTopicPillsIfEligible();
    this.rebuildPageSignalsIndex();
  }

  private fetchInventorySourcePages$(): Observable<InventoryPage[]> {
    const domain = this.selectedDomain;
    const pathPrefixes = this.selectedPathPrefixes;
    // Ask for the whole slice in one response. The server re-runs the full-slice aggregate per
    // request, so paging through it at 200 a time multiplied the cost of a cold load by the number
    // of pages (SS001-17). Health filter and sort are applied client-side, so the server ordering
    // only has to be cheap and stable, not the one on screen.
    const loadPage = (page: number, limit: number | 'all') => {
      const params = this.inventoryQueryForDomain(domain, page, pathPrefixes, {
        limit,
        includeHealthBands: false,
        sortBy: 'analyzedAt',
        sortOrder: 'desc'
      });
      return this.http.get<{ success: boolean; data: { pages: InventoryPage[]; pagination: Pagination } }>(
        `${this.API_URL}/urls/content-inventory?${params.toString()}`
      );
    };

    return loadPage(1, 'all').pipe(
      switchMap((first) => {
        const totalPages = Math.max(1, first.data.pagination.totalPages);
        if (totalPages <= 1) {
          return of(first.data.pages);
        }
        // Slice is larger than the server's full-slice cap; fetch the remainder at the page size it
        // actually gave us so the offsets line up.
        const limit = first.data.pagination.limit;
        const rest = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
        return forkJoin(rest.map((page) => loadPage(page, limit))).pipe(
          map((responses) => {
            const seen = new Set<string>();
            const pages: InventoryPage[] = [];
            for (const batch of [first.data.pages, ...responses.map((res) => res.data.pages)]) {
              for (const row of batch) {
                const urlId = (row.urlId || '').trim();
                if (!urlId || seen.has(urlId)) continue;
                seen.add(urlId);
                pages.push(row);
              }
            }
            return pages;
          })
        );
      })
    );
  }

  private fetchInventory$(domain: string, page: number, pathPrefixes: string[]) {
    const params = this.inventoryQueryForDomain(domain, page, pathPrefixes);
    return this.http.get<{ success: boolean; data: { pages: InventoryPage[]; pagination: Pagination } }>(
      `${this.API_URL}/urls/content-inventory?${params.toString()}`
    );
  }

  private loadTopicsForDomain(domain: string) {
    const requestGen = ++this.topicsRequestGen;
    this.topicError = null;
    this.parentTopics = [];
    this.availableTopics = [];
    this.planAvailableTopics = [];
    this.selectedTopicSlug = '';
    this.topicSearchQuery = '';

    if (!domain.trim()) {
      this.availablePathPrefixes = [];
      this.selectedPathPrefixes = [];
      this.pathPrefixError = null;
      this.clearTopicSuggestionCompetitorUiState();
      return;
    }

    this.clearTopicSuggestionCompetitorUiState();
    this.loadingTopics = true;
    this.domainTopicsService.getDomainTopics(domain, { compact: true }).subscribe({
      next: (response) => {
        if (requestGen !== this.topicsRequestGen) return;
        this.loadingTopics = false;
        this.parentTopics = Array.isArray(response?.data?.parentTopics) ? response.data.parentTopics : [];
        this.rebuildTopicOptions();
      },
      error: (err) => {
        if (requestGen !== this.topicsRequestGen) return;
        this.loadingTopics = false;
        this.topicError = err?.error?.message || 'Failed to load topics.';
      }
    });
  }

  private rebuildTopicOptions() {
    this.availableTopics = buildTopicOptionsFromParents(this.parentTopics, this.includeChildTopics);
    this.planAvailableTopics = buildTopicOptionsFromParents(this.parentTopics, true);
  }

  onIncludeChildTopicsChange() {
    const previouslySelectedSlug = this.selectedTopicSlug.trim().toLowerCase();
    this.rebuildTopicOptions();
    if (!previouslySelectedSlug) return;

    const selectedOption = this.availableTopics.find((topic) => topic.slug === previouslySelectedSlug);
    if (selectedOption) {
      this.topicSearchQuery = selectedOption.label;
      return;
    }

    // If user hides child topics while a child is selected, clear the filter to avoid hidden state.
    this.selectedTopicSlug = '';
    this.topicSearchQuery = '';
    this.onTopicChange();
  }

  loadInventory() {
    const requestGen = ++this.inventoryRequestGen;
    if (!this.isCompareTopicMode) {
      this.clearCompareInventoryState();
    }

    if (this.isCompareTopicMode) {
      this.loadPrimaryAndCompareInventory(requestGen);
      return;
    }

    const sourceKey = this.buildInventorySourceQueryKey();
    if (this.canApplyInventoryClientView()) {
      this.error = null;
      this.applyInventoryClientView();
      return;
    }

    this.inventorySourceQueryKey = sourceKey;
    this.loading = true;
    this.error = null;

    this.fetchInventorySourcePages$().subscribe({
      next: (allPages) => {
        if (requestGen !== this.inventoryRequestGen) return;
        this.inventorySourcePages = allPages;
        this.applyInventoryClientView();
        this.loading = false;
      },
      error: (err) => {
        if (requestGen !== this.inventoryRequestGen) return;
        console.error('Failed to load content inventory:', err);
        this.error = 'Could not load content inventory. Is the backend running?';
        this.loading = false;
        this.clearInventorySourceCache();
        this.invalidateCompetitorTopicPillsRequests();
        this.resetCompetitorTopicPillsState();
      }
    });
  }

  private loadPrimaryAndCompareInventory(requestGen: number) {
    const compareDomain = this.compareToDomain as string;

    this.loading = true;
    this.error = null;
    this.compareLoading = true;
    this.compareError = null;

    this.fetchInventory$(this.selectedDomain, this.pagination.page, this.selectedPathPrefixes).subscribe({
      next: (res) => {
        if (requestGen !== this.inventoryRequestGen) return;
        this.pages = res.data.pages;
        this.pagination = res.data.pagination;
        this.loading = false;
      },
      error: (err) => {
        if (requestGen !== this.inventoryRequestGen) return;
        console.error('Failed to load content inventory (active domain):', err);
        this.error = 'Could not load content inventory. Is the backend running?';
        this.loading = false;
      }
    });

    this.fetchInventory$(compareDomain, this.comparePagination.page, this.selectedComparePathPrefixes).subscribe({
      next: (res) => {
        if (requestGen !== this.inventoryRequestGen) return;
        this.comparePages = res.data.pages;
        this.comparePagination = res.data.pagination;
        this.compareLoading = false;
      },
      error: (err) => {
        if (requestGen !== this.inventoryRequestGen) return;
        console.error('Failed to load content inventory (compare domain):', err);
        this.compareError = 'Could not load compare domain inventory. Is the backend running?';
        this.compareLoading = false;
      }
    });
  }

  private loadPrimaryInventoryPageOnly() {
    const requestGen = ++this.inventoryRequestGen;
    this.loading = true;
    this.error = null;
    this.fetchInventory$(this.selectedDomain, this.pagination.page, this.selectedPathPrefixes).subscribe({
      next: (res) => {
        if (requestGen !== this.inventoryRequestGen) return;
        this.pages = res.data.pages;
        this.pagination = res.data.pagination;
        this.loading = false;
      },
      error: (err) => {
        if (requestGen !== this.inventoryRequestGen) return;
        console.error('Failed to load content inventory (active domain):', err);
        this.error = 'Could not load content inventory. Is the backend running?';
        this.loading = false;
      }
    });
  }

  private loadCompareInventoryPageOnly() {
    if (!this.compareToDomain) return;
    const requestGen = ++this.inventoryRequestGen;
    this.compareLoading = true;
    this.compareError = null;
    this.fetchInventory$(this.compareToDomain, this.comparePagination.page, this.selectedComparePathPrefixes).subscribe({
      next: (res) => {
        if (requestGen !== this.inventoryRequestGen) return;
        this.comparePages = res.data.pages;
        this.comparePagination = res.data.pagination;
        this.compareLoading = false;
      },
      error: (err) => {
        if (requestGen !== this.inventoryRequestGen) return;
        console.error('Failed to load content inventory (compare domain):', err);
        this.compareError = 'Could not load compare domain inventory. Is the backend running?';
        this.compareLoading = false;
      }
    });
  }

  retryPrimaryInventory() {
    if (this.isCompareTopicMode) {
      this.loadPrimaryInventoryPageOnly();
      return;
    }
    this.loadInventory();
  }

  retryCompareInventory() {
    if (!this.isCompareTopicMode || !this.compareToDomain) return;
    this.loadCompareInventoryPageOnly();
  }

  /** Navigate to the url-detail page */
  goToUrlDetail(url: string) {
    this.router.navigate(['/inventory', encodeURIComponent(url)]);
  }

  /** Debounced search — waits 300ms after user stops typing */
  onSearchChange() {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.clearQuickCompareSelection();
      this.pagination.page = 1;
      this.comparePagination.page = 1;
      this.loadInventory();
    }, 300);
  }

  onTopicChange() {
    this.clearQuickCompareSelection();
    if (this.selectedTopicSlug.trim()) {
      this.sortBy = 'topicRelevanceScore';
      this.sortOrder = 'desc';
    } else if (this.sortBy === 'topicWeight' || this.sortBy === 'topicRelevanceScore') {
      this.sortBy = 'analyzedAt';
      this.sortOrder = 'desc';
    }
    this.pagination.page = 1;
    this.comparePagination.page = 1;
    if (this.isCompareTopicMode) {
      this.loadPathPrefixesForCompareDomain();
    }
    this.loadInventory();
  }

  onTopicInputFocus() {
    this.showTopicSuggestions = true;
  }

  onTopicInputBlur() {
    // Delay to allow suggestion click handlers to run first.
    setTimeout(() => {
      this.showTopicSuggestions = false;
    }, 100);
  }

  onTopicInputChange() {
    this.showTopicSuggestions = true;
    if (!this.topicSearchQuery.trim() && this.selectedTopicSlug) {
      this.selectedTopicSlug = '';
      this.onTopicChange();
    }
  }

  applyTopicFromInput() {
    const query = this.topicSearchQuery.trim().toLowerCase();
    if (!query) {
      if (this.selectedTopicSlug) {
        this.selectedTopicSlug = '';
        this.onTopicChange();
      }
      return;
    }

    const exactMatch = this.availableTopics.find((topic) =>
      topic.slug === query ||
      topic.label.toLowerCase() === query
    );

    if (exactMatch) {
      this.selectTopic(exactMatch);
      return;
    }

    const [firstMatch] = this.visibleTopicSuggestions;
    if (firstMatch) {
      this.selectTopic(firstMatch);
    }
  }

  clearTopicFilter() {
    const hasChange = !!this.selectedTopicSlug || !!this.topicSearchQuery.trim();
    this.selectedTopicSlug = '';
    this.topicSearchQuery = '';
    this.showTopicSuggestions = false;
    if (hasChange) {
      this.onTopicChange();
    }
  }

  /** Clears URL search, topic filter, and cluster rules; one reload. */
  clearSearchTopicAndClusterFilters(): void {
    this.searchQuery = '';
    this.selectedTopicSlug = '';
    this.topicSearchQuery = '';
    this.showTopicSuggestions = false;
    this.resetClusterFilterRules();
    this.selectedHealthFilterBands = [...HEALTH_FILTER_BANDS];
    this.pagination.page = 1;
    this.comparePagination.page = 1;
    this.loadInventory();
  }

  selectTopic(topic: TopicPickerOption) {
    const normalizedSlug = topic.slug.trim().toLowerCase();
    const hasChanged = this.selectedTopicSlug !== normalizedSlug;
    this.selectedTopicSlug = normalizedSlug;
    this.topicSearchQuery = topic.label;
    this.showTopicSuggestions = false;
    if (hasChanged) {
      this.onTopicChange();
    }
  }

  get visibleTopicSuggestions(): TopicPickerOption[] {
    const query = this.topicSearchQuery.trim().toLowerCase();
    const filtered = query
      ? this.availableTopics.filter((topic) => {
          const aliasHaystack = (topic.aliasLabels || []).join(' ').toLowerCase();
          return (
            topic.label.toLowerCase().includes(query) ||
            topic.slug.includes(query) ||
            topic.parentLabel.toLowerCase().includes(query) ||
            aliasHaystack.includes(query)
          );
        })
      : this.availableTopics;

    return filtered.slice(0, this.maxTopicSuggestions);
  }

  formatAuthorityScore(score: number): string {
    return Number.isFinite(score) ? score.toFixed(1) : '0.0';
  }

  formatTopicWeight(weight: number | null | undefined): string {
    if (!Number.isFinite(weight)) return '—';
    return (weight as number).toFixed(3);
  }

  formatTopicRelevancePct(pct: number | null | undefined): string {
    if (!Number.isFinite(pct)) return '—';
    return `${Math.round(pct as number)}%`;
  }

  topTopicsForPage(page: InventoryPage): Array<{ label: string; relevancePct: number }> {
    const list = Array.isArray(page.topTopicSignals) ? page.topTopicSignals : [];
    return list
      .filter((s) => s && typeof s.label === 'string')
      .slice(0, this.topTopicsPillsLimit)
      .map((s) => ({
        label: (s.label || '').trim() || (s.topicSlug || '').trim(),
        relevancePct: Number.isFinite(s.relevancePct) ? Number(s.relevancePct) : 0
      }));
  }

  topTopicsOverflowCount(page: InventoryPage): number {
    const list = Array.isArray(page.topTopicSignals) ? page.topTopicSignals : [];
    return Math.max(0, list.length - this.topTopicsForPage(page).length);
  }

  primaryClusterForPage(page: InventoryPage): string | null {
    const labels = Array.isArray(page.clusters) ? page.clusters.filter((c) => typeof c === 'string' && c.trim()) : [];
    return labels.length > 0 ? labels[0] : null;
  }

  clusterOverflowCount(page: InventoryPage): number {
    const labels = Array.isArray(page.clusters) ? page.clusters.filter((c) => typeof c === 'string' && c.trim()) : [];
    return Math.max(0, labels.length - (this.primaryClusterForPage(page) ? 1 : 0));
  }

  clusterOverflowLabels(page: InventoryPage): string[] {
    const labels = Array.isArray(page.clusters) ? page.clusters.filter((c) => typeof c === 'string' && c.trim()) : [];
    if (labels.length <= 1) return [];
    return labels.slice(1);
  }

  pageHealthFor(page: InventoryPage) {
    return classifyPageHealth({
      statusCode: page.statusCode,
      estimatedOrganicTraffic: page.estimatedOrganicTraffic,
      realization: page.pageRealization || null,
      pageHealth: page.pageHealth || null
    });
  }

  pageHealthLabel(page: InventoryPage): string {
    return pageHealthBandLabel(this.pageHealthFor(page).band);
  }

  pageHealthClass(page: InventoryPage): string {
    return healthBandDotClass(this.pageHealthFor(page).band);
  }

  healthFilterLabel(band: InventoryHealthFilterBand): string {
    return inventoryHealthFilterLabel(band);
  }

  pageEffectiveHealthBand(page: InventoryPage): InventoryHealthFilterBand {
    if (page.effectiveHealthBand) return page.effectiveHealthBand;
    return resolveEffectiveHealthBand(
      this.pageHealthFor(page).band,
      page.pageSecondaryHealth || null
    );
  }

  pageHealthTitle(page: InventoryPage): string {
    return this.pageHealthFor(page).reasons.join(' · ');
  }

  pageSecondaryHealthLabel(page: InventoryPage): string {
    return secondaryHealthBandLabel(page.pageSecondaryHealth || null);
  }

  pageSecondaryHealthClass(page: InventoryPage): string {
    return secondaryHealthDotClass(page.pageSecondaryHealth || null);
  }

  pageSecondaryHealthTitle(page: InventoryPage): string {
    const secondary = page.pageSecondaryHealth;
    if (!secondary?.reasons?.length) return '';
    return secondary.reasons.join(' · ');
  }

  pageEffectiveHealthLabel(page: InventoryPage): string {
    return effectiveHealthBandLabel(
      this.pageHealthFor(page).band,
      page.pageSecondaryHealth || null
    );
  }

  pageEffectiveHealthClass(page: InventoryPage): string {
    return effectiveHealthDotClass(
      this.pageHealthFor(page).band,
      page.pageSecondaryHealth || null
    );
  }

  pageEffectiveHealthTitle(page: InventoryPage): string {
    const primary = this.pageHealthFor(page);
    if (primary.band !== 'unscored') {
      return primary.reasons.join(' · ');
    }
    return this.pageSecondaryHealthTitle(page) || primary.reasons.join(' · ');
  }

  pageRealizationGapLabel(page: InventoryPage): string {
    return formatInventoryRealizationNumber(page.pageRealization?.gap);
  }

  pageRealizationRatioLabel(page: InventoryPage): string {
    return formatInventoryRealizationRatio(page.pageRealization?.realizationRatio);
  }

  pageRealizationGapTitle(page: InventoryPage): string {
    return inventoryRealizationGapTitle(page.pageRealization);
  }

  pageRealizationRatioTitle(page: InventoryPage): string {
    return inventoryRealizationRatioTitle(page.pageRealization);
  }

  readonly realizationGapColumnHint =
    'Visibility upside (ceiling − realized). Higher gap = more room to grow for this URL’s demand claim. Sort descending to prioritize Weak pages.';
  readonly realizationRatioColumnHint =
    'Realized visibility ÷ ceiling. Lower ratio = more under-realized. Default sort is ascending (worst first).';
  readonly serpKeywordDifficultyColumnHint =
    'Labs keyword difficulty (0–100) for this URL’s SERP target query. Lower = easier top-10. Requires URL SERP refresh. Sort ascending for winnable Weak pages.';

  pageSerpKeywordDifficultyLabel(page: InventoryPage): string {
    if (!Number.isFinite(page.serpKeywordDifficulty)) return '—';
    return String(Math.round(Number(page.serpKeywordDifficulty)));
  }

  pageSerpKeywordDifficultyTitle(page: InventoryPage): string {
    if (!Number.isFinite(page.serpKeywordDifficulty)) {
      return 'No keyword difficulty yet. Run Refresh URL SERP (dev) for this domain.';
    }
    return this.serpKeywordDifficultyColumnHint;
  }

  pageDemandClaimLabel(page: InventoryPage): string {
    const label = page.pageDemand?.claim?.label?.trim();
    return label || '—';
  }

  pageDemandClaimClass(page: InventoryPage): string {
    const confidence = page.pageDemand?.claim?.confidence;
    if (confidence === 'low' || confidence === 'none') return 'page-demand-claim--low-confidence';
    if (confidence === 'medium') return 'page-demand-claim--medium-confidence';
    return 'page-demand-claim--high-confidence';
  }

  pageDemandClaimTitle(page: InventoryPage): string {
    const demand = page.pageDemand;
    if (!demand?.claim) return 'No demand claim stored. Run page demand labeling (dev).';
    const parts = [
      `Claim: ${demand.claim.label}`,
      `Source: ${demand.claim.source}`,
      `Confidence: ${demand.claim.confidence}`
    ];
    if (demand.claim.detail) parts.push(demand.claim.detail);
    if (demand.realization?.label) {
      parts.push(`Realization: ${demand.realization.label}`);
      if (demand.realization.position !== null) {
        parts.push(`Position: ${demand.realization.position}`);
      }
    }
    if (demand.ambientTopic?.label) parts.push(`Ambient topic: ${demand.ambientTopic.label}`);
    if (demand.splitReasons?.length) parts.push(demand.splitReasons.join(' · '));
    if (demand.computedAt) parts.push(`Computed: ${demand.computedAt}`);
    return parts.join(' · ');
  }

  pageDemandClaimSourceConfidence(page: InventoryPage): string {
    return formatPageDemandSourceConfidence(page.pageDemand?.claim);
  }

  pageDemandRealizationSourceConfidence(page: InventoryPage): string {
    return formatPageDemandSourceConfidence(page.pageDemand?.realization);
  }

  pageDemandSplitReasonsText(page: InventoryPage): string {
    return formatPageDemandSplitReasons(page.pageDemand?.splitReasons);
  }

  pageSignalsFor(page: InventoryPage): InventoryPageSignal[] {
    return this.pageSignalsByUrlId.get(page.urlId) || [];
  }

  pageSignalChipClass(kind: InventoryPageSignalKind | ActionPlanV2SignalType | string): string {
    // Badge tones, keyed by what the signal MEANS rather than by its name.
    // no_cluster is the absence of a verdict, so it takes the neutral
    // default; the five hand-tinted .page-signal-chip--* rules this
    // replaces were the same five colours at 14% of their surface.
    if (kind === 'cannibalization') return 'badge--danger';
    if (kind === 'internal_link') return 'badge--accent';
    if (kind === 'unclustered' || kind === 'content_gap') return 'badge--warning';
    return '';
  }

  /** The chips are built from the action-plan rows (links, unclustered pages). */
  pageSignalsLoading(): boolean {
    return this.actionPlanLoading;
  }

  private rebuildPageSignalsIndex(): void {
    if (this.inventorySection !== 'pages' || this.isCompareTopicMode) {
      this.pageSignalsByUrlId = new Map();
      return;
    }
    this.pageSignalsByUrlId = buildPageSignalsIndex(
      this.pages,
      this.actionPlanRows,
      this.actionPlanUnclusteredRows
    );
  }

  topTopicsOverflowTopics(page: InventoryPage): Array<{ label: string; relevancePct: number }> {
    const list = Array.isArray(page.topTopicSignals) ? page.topTopicSignals : [];
    if (list.length <= this.topTopicsPillsLimit) return [];
    return list
      .slice(this.topTopicsPillsLimit)
      .map((s) => {
        const label = (s?.label || '').trim() || (s?.topicSlug || '').trim() || 'topic';
        return {
          label,
          relevancePct: Number.isFinite(s?.relevancePct) ? Number(s.relevancePct) : 0
        };
      })
      .filter((t) => !!t.label);
  }

  openClusterPopover(page: InventoryPage, event: MouseEvent | FocusEvent): void {
    const overflow = this.clusterOverflowLabels(page);
    if (overflow.length === 0) return;
    const target = event.currentTarget as HTMLElement | null;
    const rect = target?.getBoundingClientRect();
    if (!rect) return;
    const margin = 12;
    let left = rect.right + 8;
    if (left + this.clusterPopoverWidthPx > window.innerWidth - margin) {
      left = rect.left - this.clusterPopoverWidthPx - 8;
    }
    if (left < margin) {
      left = margin;
    }
    let top = rect.top;
    if (top + 180 > window.innerHeight - margin) {
      top = Math.max(margin, window.innerHeight - margin - 180);
    }
    this.clusterPopover = {
      open: true,
      left,
      top,
      clusters: overflow
    };
  }

  scheduleClusterPopoverHide(): void {
    this.clusterPopoverHideTimer = setTimeout(() => {
      this.clusterPopover = { open: false, left: 0, top: 0, clusters: [] };
      this.clusterPopoverHideTimer = null;
    }, 120);
  }

  cancelClusterPopoverHide(): void {
    if (!this.clusterPopoverHideTimer) return;
    clearTimeout(this.clusterPopoverHideTimer);
    this.clusterPopoverHideTimer = null;
  }

  openTopTopicsPopover(page: InventoryPage, event: MouseEvent | FocusEvent): void {
    const topics = this.topTopicsOverflowTopics(page);
    if (topics.length === 0) return;
    this.cancelTopTopicsPopoverHide();
    const target = event.currentTarget as HTMLElement | null;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const margin = 10;
    let left = rect.right + 8;
    if (left + this.topTopicsPopoverWidthPx > window.innerWidth - margin) {
      left = rect.left - this.topTopicsPopoverWidthPx - 8;
    }
    if (left < margin) left = margin;
    let top = rect.top - 8;
    const estimatedHeight = Math.min(240, 48 + topics.length * 28);
    if (top + estimatedHeight > window.innerHeight - margin) {
      top = Math.max(margin, window.innerHeight - estimatedHeight - margin);
    }
    this.topTopicsPopover = {
      open: true,
      left,
      top,
      topics
    };
  }

  scheduleTopTopicsPopoverHide(): void {
    this.cancelTopTopicsPopoverHide();
    this.topTopicsPopoverHideTimer = setTimeout(() => {
      this.topTopicsPopover = { open: false, left: 0, top: 0, topics: [] };
      this.topTopicsPopoverHideTimer = null;
    }, 120);
  }

  cancelTopTopicsPopoverHide(): void {
    if (!this.topTopicsPopoverHideTimer) return;
    clearTimeout(this.topTopicsPopoverHideTimer);
    this.topTopicsPopoverHideTimer = null;
  }

  topTopicPillClass(relevancePct: number): string {
    // Badge tones. Excellent and good were two strengths of the same green
    // and are one tone now; the badge has one fill per meaning by design.
    if (relevancePct >= 65) return 'badge--success';
    if (relevancePct >= 45) return 'badge--warning';
    return 'badge--danger';
  }

  topicClassLabel(topicClass: 'core' | 'supporting' | 'mention' | null | undefined): string {
    if (topicClass === 'core') return 'Core';
    if (topicClass === 'supporting') return 'Supporting';
    if (topicClass === 'mention') return 'Mention';
    return '—';
  }

  topicClassBadgeClass(topicClass: 'core' | 'supporting' | 'mention' | null | undefined): string {
    // Badge tones. Unknown is a category with no verdict, which is the
    // badge's neutral default -- naming a tone for it would be a lie.
    if (topicClass === 'core') return 'badge--success';
    if (topicClass === 'supporting') return 'badge--warning';
    if (topicClass === 'mention') return 'badge--accent';
    return '';
  }

  topicWeightClass(weight: number | null | undefined): string {
    if (!Number.isFinite(weight)) return 'score-low';
    const scaled = Math.max(0, Math.min(100, (weight as number) * 100));
    return this.scoreClass(scaled);
  }

  get hasTopicScope(): boolean {
    return !!this.selectedTopicSlug.trim();
  }

  topicAuthorityTagClass(score: number): string {
    if (score >= 80) return 'topic-authority-high';
    if (score >= 60) return 'topic-authority-mid';
    if (score >= 40) return 'topic-authority-low';
    return 'topic-authority-very-low';
  }

  topicKindTagClass(topic: TopicPickerOption): string {
    return topicKindTagClassForOption(topic);
  }

  /** Toggle sort: if same field → flip order, else → set field desc (ratio defaults asc). */
  toggleSort(field: SortField) {
    if (this.sortBy === field) {
      this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
    } else {
      this.sortBy = field;
      this.sortOrder =
        field === 'realizationRatio' || field === 'serpKeywordDifficulty' ? 'asc' : 'desc';
    }
    this.clearQuickCompareSelection();
    this.pagination.page = 1;
    this.comparePagination.page = 1;
    if (!this.isCompareTopicMode && this.canApplyInventoryClientView()) {
      this.applyInventoryClientView();
      return;
    }
    this.loadInventory();
  }

  /** Sort indicator arrow */
  sortIcon(field: SortField): string {
    if (this.sortBy !== field) return '';
    return this.sortOrder === 'asc' ? ' ↑' : ' ↓';
  }

  /** Go to a specific page */
  goToPage(page: number) {
    if (page < 1 || page > this.pagination.totalPages) return;
    this.pagination.page = page;
    if (this.isCompareTopicMode) {
      this.loadPrimaryInventoryPageOnly();
      return;
    }
    if (this.canApplyInventoryClientView()) {
      this.applyInventoryClientView();
      return;
    }
    this.loadInventory();
  }

  goToComparePage(page: number) {
    if (page < 1 || page > this.comparePagination.totalPages) return;
    this.comparePagination.page = page;
    if (this.isCompareTopicMode) {
      this.loadCompareInventoryPageOnly();
    }
  }

  /**
   * Format URL for inventory display:
   * - Homepage only (/, no query/hash): full origin e.g. https://marker.io or https://www.marker.io
   * - With path: path + query + hash e.g. /blog/website-feedback-form
   */
  formatUrl(url: string, truncateLong = true): string {
    const raw = (url || '').trim();
    if (!raw) return '';

    let u: URL;
    try {
      u = new URL(raw);
    } catch {
      try {
        u = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
      } catch {
        return truncateLong && raw.length > 70 ? raw.slice(0, 67) + '…' : raw;
      }
    }

    const isRootPath = u.pathname === '/' || u.pathname === '';
    const hasQueryOrHash = !!u.search || !!u.hash;

    let display: string;
    if (isRootPath && !hasQueryOrHash) {
      display = `${u.protocol}//${u.host}`;
    } else {
      display = `${u.pathname}${u.search}${u.hash}`;
    }

    if (truncateLong && display.length > 70) {
      return display.slice(0, 67) + '…';
    }
    return display;
  }

  /** Newest `analyzedAt` among the given rows (current table page only). */
  private newestAnalyzedAtIso(rows: InventoryPage[] | null | undefined): string | null {
    if (!rows?.length) return null;
    let best = rows[0].analyzedAt;
    let bestMs = new Date(best).getTime();
    for (let i = 1; i < rows.length; i++) {
      const t = new Date(rows[i].analyzedAt).getTime();
      if (t > bestMs) {
        bestMs = t;
        best = rows[i].analyzedAt;
      }
    }
    return best;
  }

  lastAnalyzedSummary(rows: InventoryPage[]): string {
    const best = this.newestAnalyzedAtIso(rows);
    return best ? this.timeAgo(best) : '';
  }

  lastAnalyzedTitle(rows: InventoryPage[]): string {
    const best = this.newestAnalyzedAtIso(rows);
    if (!best) return '';
    try {
      return new Date(best).toLocaleString();
    } catch {
      return best;
    }
  }

  /** Relative time label */
  timeAgo(dateStr: string): string {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diffMs = now - then;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 30) return `${diffDay}d ago`;
    return new Date(dateStr).toLocaleDateString();
  }

  /** Score color class */
  scoreClass(score: number): string {
    if (score >= 80) return 'badge--success';
    if (score >= 50) return 'badge--warning';
    return 'badge--danger';
  }

  /** Status code class */
  statusCodeClass(code: number): string {
    if (code >= 200 && code < 300) return 'status-ok';
    if (code >= 300 && code < 400) return 'status-redirect';
    return 'status-error';
  }

  /** Issues badge class */
  issuesClass(critical: number, warnings: number): string {
    if (critical > 0) return 'issues-critical';
    if (warnings > 0) return 'issues-warning';
    return 'issues-clean';
  }

  get appliedClusterFilterHasSelection(): boolean {
    return this.appliedClusterFilterRules.some((r) => r.topicIds.length > 0);
  }

  /** Whether the inventory is empty (no pages at all, not just filtered) */
  get isEmpty(): boolean {
    return (
      !this.loading &&
      !this.error &&
      this.pages.length === 0 &&
      !this.searchQuery.trim() &&
      !this.selectedTopicSlug.trim() &&
      !this.appliedClusterFilterHasSelection
    );
  }

  /** Whether a search returned no results */
  get isEmptySearch(): boolean {
    return (
      !this.loading &&
      !this.error &&
      this.pages.length === 0 &&
      (!!this.searchQuery.trim() || !!this.selectedTopicSlug.trim() || this.appliedClusterFilterHasSelection)
    );
  }

  /** Single-column empty filter banner; in compare mode each panel shows its own empty line. */
  get showGlobalEmptySearch(): boolean {
    return this.isEmptySearch && !this.isCompareTopicMode;
  }

  get panelHasNoRowsPrimary(): boolean {
    if (this.isCompareTopicMode) {
      return !this.loading && !this.error && this.pages.length === 0;
    }
    return false;
  }

  get panelHasNoRowsCompare(): boolean {
    return (
      this.isCompareTopicMode &&
      !this.compareLoading &&
      !this.compareError &&
      this.comparePages.length === 0
    );
  }

  ngOnDestroy() {
    clearTimeout(this.searchTimeout);
    if (this.planSuccessTimeout) {
      clearTimeout(this.planSuccessTimeout);
    }
    if (this.topicSuggestionHoverTimer) {
      clearTimeout(this.topicSuggestionHoverTimer);
    }
    this.subscriptions.unsubscribe();
  }
}
